import { portalIT } from '../portal/it';
import { privacyPolicyBodyIt } from '@/shared/i18n/locales/privacy-policy-bodies/it';
import { restaurantOrdersIt } from '@/shared/i18n/locales/restaurant-orders/it';
import type { TranslationSchema } from '@/shared/i18n/types';

const paymentPolicyTemplate = `POLICY DI PAGAMENTO, CARTA E CANCELLAZIONE

1. Identità della struttura
La prenotazione e il relativo pagamento sono gestiti da [RAGIONE SOCIALE], con sede in [INDIRIZZO], contattabile a [EMAIL/TELEFONO]. Stripe agisce esclusivamente come prestatore del servizio di pagamento.

2. Verifica della carta
Quando è richiesta la sola verifica, nessun importo viene addebitato o bloccato. La carta viene verificata tramite Stripe per confermarne la validità.

3. Pagamento anticipato
Quando è previsto il pagamento immediato, l'importo totale mostrato prima della conferma viene addebitato al momento della prenotazione e imputato come [ACCONTO/PAGAMENTO COMPLETO].

4. Preautorizzazione
Quando è prevista una preautorizzazione, l'importo mostrato viene temporaneamente bloccato. La struttura può incassarlo nei casi descritti nella presente policy. In assenza di incasso, i tempi di sblocco dipendono dalla banca del cliente.

5. Cancellazione e modifica
Cancellazione gratuita fino a [TERMINE]. Oltre tale termine la struttura può trattenere o addebitare [IMPORTO/PERCENTUALE]. Le modifiche sono soggette a disponibilità.

6. No-show e ritardo
In caso di mancata presentazione o ritardo superiore a [MINUTI], la struttura può trattenere o addebitare [IMPORTO/PERCENTUALE].

7. Rimborsi
Gli eventuali rimborsi vengono disposti sullo stesso metodo di pagamento entro [GIORNI] giorni; i tempi di accredito finali dipendono dal circuito e dalla banca.

8. Conservazione e uso futuro della carta
La carta può essere conservata in forma tokenizzata da Stripe e riutilizzata soltanto se il cliente presta uno specifico consenso per gli addebiti descritti. PRO20 e la struttura non memorizzano il numero completo della carta.

9. Contestazioni e assistenza
Per richieste, cancellazioni o contestazioni contattare [EMAIL/TELEFONO] indicando il codice prenotazione.

Versione e validità
La versione accettata, l'importo, la modalità di pagamento e la data di accettazione vengono registrati insieme alla prenotazione.`;

export const it: TranslationSchema = {
  collaboratorRoles: {
    inherited:
      'Questo ruolo non ha ancora una configurazione: mantiene tutti gli accessi attuali. Salva per definire i permessi espliciti.',
    title: 'Permessi dell’app',
    description:
      'Scegli quali operazioni può eseguire ogni ruolo collaboratore nell’app. L’accesso al portale resta invariato.',
    role: 'Ruolo collaboratore',
    enabled: 'abilitati',
    wholeCategory: 'Intera categoria',
    search: 'Cerca operazioni',
    saved: 'Permessi salvati',
    saveError:
      'Salvataggio non riuscito. Ricarica il ruolo per verificare eventuali modifiche di un altro gestore.',
    versionError:
      'Il catalogo dei permessi è cambiato. Ricarica prima di salvare.',
    unsaved: 'Modifiche non salvate',
    appOnly: 'Questi permessi si applicano solo all’app.',
    reset: 'Annulla modifiche',
    save: 'Salva permessi',
    discard:
      'Scartare le modifiche non salvate e caricare il ruolo selezionato?',
    selectBranch: 'Seleziona un’attività per configurare i permessi.',
    loading: 'Caricamento permessi…',
    unavailable:
      'Impossibile caricare i permessi. Verifica l’accesso e che il servizio supporti questa configurazione. Nessun permesso è stato modificato.',
    retry: 'Riprova',
  },

  portal: portalIT,
  restaurantOrders: restaurantOrdersIt,
  giftCards: {
    openRecap: 'Apri riepilogo',
    recapError:
      'Impossibile recuperare il link personale della Gift card. Il servizio Wallet potrebbe non essere ancora disponibile.',

    select: 'Seleziona',
    card: 'Carta',
    invalidDates: 'Seleziona un intervallo di date valido.',

    title: 'Gift card',
    description: 'Carte emesse, QR e saldi',
    create: 'Crea gift card',
    search: 'Cerca cliente o codice carta…',
    empty: 'Nessuna gift card trovata',
    loadError: 'Impossibile caricare le gift card.',
    noBranch: 'Seleziona una struttura per gestire le gift card.',
    customer: 'Cliente',
    amount: 'Importo (€)',
    chooseCustomer: 'Scegli un cliente dall’elenco',
    selected: 'Cliente selezionato',
    createSuccess: 'Gift card creata.',
    createError: 'Impossibile creare la gift card.',
    amountError:
      'Inserisci un importo maggiore di zero con massimo due decimali.',
    details: 'Dettagli gift card',
    initial: 'Importo iniziale',
    balance: 'Saldo',
    status: 'Stato',
    created: 'Creazione',
    expires: 'Scadenza',
    operations: 'Movimenti',
    operation: 'Movimento',
    note: 'Nota',
    noOperations: 'Nessun movimento disponibile.',
    email: 'Invia email',
    emailSuccess: 'Invio email richiesto.',
    emailError: 'Impossibile richiedere l’invio email.',
    active: 'Attiva',
    used: 'Utilizzata',
    expired: 'Scaduta',
    cancelled: 'Annullata',
    allStates: 'Tutti gli stati',
    from: 'Dal',
    to: 'Al',
    dateRange: 'Intervallo di date',
    clearDateRange: 'Cancella intervallo di date',
    requestPending:
      'Mantieni cliente e importo per riprovare la stessa richiesta senza emettere una seconda carta.',
  },
  common: {
    retry: 'Riprova',
    loading: 'Caricamento',
    save: 'Salva',
    delete: 'Sì, elimina',
    deleting: 'Eliminazione...',
    createLoading: 'Creazione...',
    cancel: 'Annulla',
    yes: 'Sì',
    no: 'No',
    back: 'Indietro',
    search: 'Cerca',
    noData: 'Nessun dato disponibile',
    noDataDesc: 'Non ci sono dati che corrispondono ai filtri selezionati.',
    previous: 'Pagina precedente',
    next: 'Pagina successiva',
    actions: 'Azioni',
    serialNumber: 'Sr No.',
    itemsPerPage: 'Elementi per pagina',
    range: '{{start}} - {{end}} di {{total}}',
    footer: 'Pro20 © {{year}}',
    toast: {
      copyUrlSuccess: 'URL copiato negli appunti.',
    },
  },
  nav: {
    collaboratorRoles: 'Ruoli Collaboratori',
    deliverySection: 'Delivery',
    drivers: 'Fattorini',
    orders: 'Ordini',
    delivery: 'Delivery',
    deliveryTakeaway: 'Asporto/Delivery',
    orderingConfiguration: 'Configurazioni',
    takeaway: 'Asporto',
    operations: 'Ordini',
    suppliers: 'Fornitori e cataloghi',
    supplierOrderHistory: 'Storico ordini',
    supplierOrders: 'Ordini ai fornitori',
    ingredients: 'Ingredienti',
    menuList: 'I tuoi menu',
    home: 'HOME',
    menu: 'Menu',
    settingsGroup: 'IMPOSTAZIONI',
    dashboard: 'Dashboard',
    calendar: 'Calendario',
    calendarDesc: 'Prenotazioni e moduli',
    marketing: 'Marketing',
    marketingDesc: 'Messaggi e promozioni',
    menuItem: 'Menu',
    staff: 'Personale',
    settings: 'Configurazioni',
    payments: 'Pagamenti',
    settingsDesc: 'Impostazioni generali',
    users: 'Utenti',
    reports: 'Report',
    notifications: 'Notifiche',
    bookingHistory: 'Prenotazioni',
    forms: 'Form di prenotazione',
    formHistory: 'Booking',
    formMenu: 'Menu',
    others: 'Altri',
    extraServiceAndProduct: 'Servizi extra',
    events: 'Event',
    bulkMessage: 'Messaggio di massa',
    surveys: 'Questionari',
    automationConfiguration: 'Configurazione',
    automationDashboard: 'Dashboard andamento',
    automation: 'Automation',
    dataCollectionForm: 'Raccolta dati form',
    promotion: 'Promozioni',
    fidelity: 'Fedeltà',
    discountCards: 'Card sconto',
    cards: 'Card',
    experienceCards: 'Experience',
    prizes: 'Premio',
    loyalty: 'Fidelizzazione',
    landingPage: 'Landing Page',
    tag: 'Tag',
    publicRelation: 'Relazioni pubbliche',
    collaborator: 'Collaboratori',
    collaboratorReport: 'Report collaboratori',
    myCustomers: 'I miei clienti',
    localConfiguration: 'Locale',
    stripeConfiguration: 'Configurazione Stripe',
    stripeTransactions: 'Transazioni Stripe',
    configureWhatsApp: 'WhatsApp non ufficiale',
    messages: 'Messaggi',
    officialWhatsAppChannel: 'Canale WhatsApp ufficiale',
    templates: 'Template',
    whatsAppOfficial: 'WhatsApp Official',
    whatsAppOfficialConfigure: 'Configura',
    whatsAppOfficialTemplates: 'Template',
    whatsAppOfficialInbox: 'Posta in arrivo',
  },
  header: {
    fullscreen: 'Schermo intero',
    language: 'Lingua',
    theme: 'Tema',
    lightMode: 'Chiaro',
    darkMode: 'Scuro',
    profile: 'Profilo',
    signOut: 'Esci',
    userCode: 'Codice utente',
    branchList: 'Elenco filiali',
    searchBranches: 'Cerca filiali...',
    noBranchesFound: 'Nessuna filiale trovata',
    switchingBranch: 'Cambio filiale…',
  },
  dashboard: {
    aiCredits: 'Crediti AI',
    smsAvailable: 'SMS disponibili',
    smsLoading: 'Caricamento saldo SMS…',
    smsError: 'Errore saldo SMS',
    smsErrorHint: 'Saldo SMS non disponibile. Riprova.',
    smsRefresh: 'Aggiorna saldo SMS',
    smsEmpty: 'SMS esauriti',
    title: 'Dashboard',
    subtitle: 'Ecco cosa sta succedendo nel tuo business.',
    headerMeta: '{{dateRange}} · finestra di performance di {{days}} giorni',
    customRange: 'Intervallo di date',
    customRangePlaceholder: 'Seleziona intervallo di date',
    refresh: 'Aggiorna',
    bookingStatistics: 'Booking statistics',
    lastWeek: 'Last week',
    thisWeek: 'This week',
    processedBookings: 'Processed bookings vs. Arrived customers',
    overview: 'Overview',
    bookings: 'Bookings',
    customersManaged: 'Customers managed',
    arrived: 'Arrivati',
    refused: 'REFUSED',
    noShow: 'NO SHOW',
    cancelled: 'CANCELLED',
    summary: 'Summary of workspace',
    customerContacts: 'Customer Contacts',
    acceptedBookings: 'Accepted Bookings',
    managedGuests: 'Managed Guests',
    callCenterInteractions: 'Call Center Interactions',
    remainingSms: 'Remaining SMS',
    remainingEmails: 'Remaining Emails',
    totalUsers: 'Total Users',
    activeUsers: 'Active Users',
    revenue: 'Revenue',
    pendingTasks: 'Pending Tasks',
    recentActivity: 'Recent Activity',
    quickActions: 'Quick Actions',
    fromLastMonth: 'from last month',
    noData: '—',
    noBranchTitle: 'No branch selected',
    noBranchMessage: 'Select a branch to view dashboard analytics.',
    loadError: 'Could not load dashboard data. Please try again.',
    sectionLoadError: 'Impossibile caricare questa sezione.',
    bookingsFlow: 'Bookings flow',
    forms: 'Moduli',
    statusDistribution: 'Status distribution',
    totalBookingsShort: 'Bookings',
    waitingAction: 'In attesa di azione',
    waitingActionSubtitle: 'Prenotazioni in IN_ATTESA pronte da elaborare.',
    waitingBadge: 'In attesa',
    people: 'persone',
    openBooking: 'Apri prenotazione',
    copyContact: 'Copia contatto',
    copySuccess: 'Copiato negli appunti.',
    emptyWaiting: 'Nessuna prenotazione in attesa di azione.',
    emptyChart: 'Nessun dato per questo periodo.',
    emptyCustomers: 'Nessun cliente da mostrare.',
    emptyForms: 'Nessun modulo da mostrare.',
    guests: 'ospiti',
    children: 'bambini',
    accept: 'Accetta',
    reject: 'Rifiuta',
    acceptSuccess: 'Prenotazione accettata.',
    rejectSuccess: 'Prenotazione rifiutata.',
    actionError: 'Impossibile aggiornare lo stato della prenotazione.',
    busiestSlots: 'Fasce più affollate',
    busiestSlotsSubtitle: 'Dove il servizio si concentra.',
    customerMix: 'Base clienti',
    customerRisk: 'Rischio clienti',
    customerRiskSubtitle: 'Concentrazione di no-show e rifiuti.',
    bestCustomers: 'Migliori clienti',
    bestCustomersSubtitle: 'Presenza ricorrente di alto valore.',
    lastBooking: 'Ultima prenotazione',
    messageDelivery: 'Consegna messaggi',
    messageDeliverySubtitle: 'Salute della comunicazione automatica.',
    dailyOperations: 'Operazioni giornaliere',
    dailyOperationsSubtitle: 'Prenotazioni e carico ospiti per giorno.',
    bookingsFlowSubtitle: 'Prenotazioni giornaliere nel periodo selezionato.',
    formsSubtitle: 'Da dove arrivano le prenotazioni.',
    statusDistributionSubtitle: 'Come si sono concluse le prenotazioni.',
    customerMixSubtitle: 'La tua base clienti in questo periodo.',
    pulse: {
      today: 'Oggi',
      upcoming: 'In arrivo',
      pending: 'In attesa',
      waitList: "Lista d'attesa",
      refusalRate: 'Tasso di rifiuto',
      google: 'Google Reservations',
      googleOn: 'Attivo',
      googleOff: 'Non attivo',
      googleMissing: '{{count}} requisiti mancanti',
    },
    waitingTabs: {
      all: 'Tutti',
      today: 'Oggi',
      future: 'Futuri',
      large: 'Grandi',
    },
    flow: {
      volume: 'Volume',
      people: 'Persone',
      outcome: 'Esito',
    },
    kpis: {
      reservations: 'Prenotazioni',
      reservationsHint: '{{count}} persone',
      arrivalRate: 'Tasso di arrivo',
      customers: 'Clienti',
      arrivedOfTotal: '{{arrived}} su {{total}} arrivati',
      customersHint: '{{active}} attivi · {{new}} nuovi',
      messageErrors: '{{count}} errori di invio',
      noShowRate: 'Tasso di no-show',
      arrivedVsNoShow: '{{arrived}} arrivati · {{noShow}} no-show',
      peakDay: 'Giorno di picco',
      peakSlot: 'Fascia di picco',
      dailyAverage: 'Media giornaliera',
      dailyAverageHint: 'Prenotazioni al giorno',
      messagesWaiting: 'Messaggi in attesa',
      scheduledTotal: '{{count}} programmati totali',
      bookingsGuests: '{{bookings}} prenotazioni, {{guests}} ospiti',
      slotStats: '{{bookings}} prenotazioni · {{arrived}} arrivati ({{rate}}%)',
      totalBookings: 'Prenotazioni totali',
      activeUsers: 'Utenti attivi',
      newUsers: 'Nuovi utenti',
      peakDate: 'Data di picco',
      managedBookings: 'Prenotazioni gestite',
    },
    series: {
      bookings: 'Prenotazioni',
      confirmed: 'Confermate',
      pending: 'In attesa',
      refused: 'Rifiutate',
      noShow: 'No show',
      risk: 'Rischio',
    },
    mix: {
      active: 'Attivi',
      new: 'Nuovi',
      marketingOptIn: 'Consenso marketing',
      blacklisted: 'In blacklist',
      ofBase: '{{percent}}% della base',
      total: 'totali',
    },
    comm: {
      bookingNotifications: 'Notifiche prenotazione',
      scheduledMessages: 'Messaggi programmati',
      successRate: 'Tasso di successo',
      total: 'Totale',
      processed: 'elaborati',
      waiting: 'in attesa',
      errors: 'errori',
      centralino: 'Stato centralino',
      centralinoActive: 'Centralino attivo',
      wao: 'WAO',
      whatsapp: 'WhatsApp',
      sms: 'SMS',
      email: 'Email',
      enabled: 'On',
      disabled: 'Off',
    },
    ops: {
      byDay: 'Per giorno',
      byWeekday: 'Per giorno della settimana',
      avgPeople: 'Persone medie',
      avgBookings: 'Prenotazioni medie',
      avgPartySize: 'Coperti medi',
      busiestWeekday: 'Giorno più affollato',
      totalPeople: 'Persone totali',
      daysCount: '{{count}} giorni',
    },
    days: {
      mon: 'Lun',
      tue: 'Mar',
      wed: 'Mer',
      thu: 'Gio',
      fri: 'Ven',
      sat: 'Sab',
      sun: 'Dom',
    },
  },
  auth: {
    welcomeBack: 'Bentornato!',
    signIn: 'Accedi',
    email: 'Email',
    password: 'Password',
    enterPassword: 'Inserisci la tua password',
    userCode: 'Codice utente',
    enterUserCode: 'Inserisci il tuo codice utente',
    branchCode: 'Codice filiale',
    enterBranchCode: 'Inserisci il codice filiale',
    employeeId: 'ID dipendente',
    enterEmployeeId: "Inserisci l'ID dipendente",
    rememberMe: 'Ricordami',
    rememberCredentials: 'Ricorda credenziali',
    forgotPassword: 'Password dimenticata?',
    signInToContinue:
      'Accedi per continuare a gestire il tuo ristorante con PRO20.',
    signInToAccount: 'Accedi al tuo account',
    chooseSignInMethod: 'Scegli il metodo di accesso preferito',
    or: 'oppure',
    loginWithEmployeeId: 'Accedi con ID dipendente',
    loginWithUserCode: 'Accedi con codice utente',
    loginWithAdminCode: 'Accedi con codice amministratore',
    invalidCredentials: 'Credenziali non valide. Riprova.',
    enterpriseTagline: 'Admin enterprise, semplificato.',
    enterpriseDescription:
      "Gestisci utenti, monitora le analisi e configura il workspace da un'unica dashboard.",
    trustedBy: 'Scelto da ristoranti in tutto il mondo',
    trustSecurityTitle: 'Sicurezza enterprise',
    trustSecurityDesc: 'Protezione dei dati e conformità di livello bancario.',
    trustUptimeTitle: 'Uptime 99,9%',
    trustUptimeDesc: 'Prestazioni affidabili quando ti servono.',
    trustSupportTitle: 'Supporto 24/7',
    trustSupportDesc: 'Siamo qui per aiutarti in qualsiasi momento.',
    secureSignIn: 'Accesso sicuro | La tua connessione è crittografata.',
    copyright: '© {{year}} PRO20. Tutti i diritti riservati.',
    dashboardPreviewAlt: 'Anteprima della dashboard PRO20',
    demoCredentials: 'Credenziali demo: admin@proventi.com / password123',
    version: 'v. {{version}}(#1)',
  },
  subscription: {
    expired: {
      badge: 'Abbonamento scaduto',
      title: 'Il tuo abbonamento è scaduto',
      description: 'Se hai già effettuato il rinnovo contatta il supporto',
      infoTitle: 'Hai bisogno di rinnovare o ripristinare l’accesso?',
      infoDescription:
        'Contatta il nostro team di supporto e ti aiuteremo a tornare operativo.',
      footerHelp: 'Hai ancora bisogno di aiuto?',
      footerHelpDesc:
        'Il nostro team di supporto è disponibile 24/7 per assisterti.',
    },
  },
  automation: {
    disabled: {
      badge: 'Sezione non abilitata',
      title: 'Questa sezione non è abilitata',
      description:
        'Questa funzione è attualmente disabilitata per il tuo account. Contatta il supporto per saperne di più.',
      infoTitle: 'Hai bisogno di accedere a questa sezione?',
      infoDescription:
        'Contatta il nostro team di supporto e ti aiuteremo a iniziare.',
      footerHelp: 'Hai ancora bisogno di aiuto?',
      footerHelpDesc:
        'Il nostro team di supporto è disponibile 24/7 per assisterti.',
    },
  },
  orders: {
    disabled: {
      badge: 'Sezione non abilitata',
      title: 'Questa sezione non è abilitata',
      description:
        'Questa funzione è attualmente disabilitata per il tuo account. Contatta il supporto per saperne di più.',
      infoTitle: 'Hai bisogno di accedere a questa sezione?',
      infoDescription:
        'Contatta il nostro team di supporto e ti aiuteremo a iniziare.',
      footerHelp: 'Hai ancora bisogno di aiuto?',
      footerHelpDesc:
        'Il nostro team di supporto è disponibile 24/7 per assisterti.',
    },
  },
  delivery: {
    disabled: {
      badge: 'Sezione non abilitata',
      title: 'Questa sezione non è abilitata',
      description:
        'Questa funzione è attualmente disabilitata per il tuo account. Contatta il supporto per saperne di più.',
      infoTitle: 'Hai bisogno di accedere a questa sezione?',
      infoDescription:
        'Contatta il nostro team di supporto e ti aiuteremo a iniziare.',
      footerHelp: 'Hai ancora bisogno di aiuto?',
      footerHelpDesc:
        'Il nostro team di supporto è disponibile 24/7 per assisterti.',
    },
  },
  users: {
    title: 'Utenti',
    subtitle: 'Gestisci membri del team e permessi',
    searchPlaceholder: 'Cerca utenti...',
    role: 'Ruolo',
    status: 'Stato',
    joined: 'Iscritto',
    accountDetails: 'Dettagli account',
    userId: 'ID utente',
    created: 'Creato',
    lastUpdated: 'Ultimo aggiornamento',
    notFound: 'Utente non trovato',
  },
  settings: {
    title: 'Impostazioni',
    subtitle: 'Gestisci le preferenze del workspace',
    comingSoon: 'Questa sezione sarà disponibile a breve.',
    general: 'Generale',
    theme: 'Tema',
    language: 'Lingua',
    timezone: 'Fuso orario',
    notifications: 'Notifiche',
    emailNotifications: 'Notifiche email',
    pushNotifications: 'Notifiche push',
    enabled: 'Abilitato',
    disabled: 'Disabilitato',
  },
  userSettings: {
    title: 'Impostazioni utente',
    subtitle: 'Gestisci la password e le preferenze del tuo account',
    password: {
      sectionTitle: 'Gestione password',
      sectionSubtitle: 'Cambia password',
      passwordLabel: 'Password',
      confirmLabel: 'Conferma password',
      submit: 'Aggiorna password',
      errors: {
        required: 'La password è obbligatoria',
        minLength: 'La password deve avere almeno 4 caratteri',
        confirmRequired: 'Conferma la password',
        mismatch: 'Le password non coincidono',
      },
      toast: {
        success: 'Password aggiornata con successo',
        failed: 'Impossibile aggiornare la password. Riprova.',
        missingUser: 'Impossibile trovare il tuo account. Accedi di nuovo.',
      },
    },
  },
  localConfiguration: {
    subtitle: 'Configura logo, orari, location e tracking del ristorante.',
    saveConfiguration: 'Salva Configurazione',
    saveTooltip: 'Salva la configurazione del ristorante',
    noBranch: 'Seleziona una sede per configurare le impostazioni locali.',
    loadError: 'Impossibile caricare la configurazione locale. Riprova.',
    logoTitle: 'Logo del Ristorante',
    logoHint: "Carica un'immagine",
    logoDescription:
      "Carica un logo PNG con sfondo trasparente. Doppio clic sull'immagine per l'anteprima.",
    uploadImage: "Carica un'immagine",
    removeLogo: 'Rimuovi logo',
    logoUploading: 'Caricamento…',
    logoRemoving: 'Rimozione…',
    timezoneTitle: 'Fuso orario della sala',
    timezoneHint:
      'Determina il giorno corrente mostrato nel form ed evita slittamenti di data per i clienti all’estero.',
    timezoneLabel: 'Area geografica',
    timezoneHelper:
      'Il passaggio tra ora solare e ora legale è gestito automaticamente.',
    generalTitle: 'Dettagli generali',
    generalHint:
      'Configura capacità del locale, fasce orarie e giorni di chiusura.',
    scheduleTitle: 'Orari',
    scheduleHint: 'Scegli quando gli ospiti possono prenotare.',
    dayOverviewTitle: 'Quando gli ospiti possono prenotare',
    dayOverviewHint: 'Aprila per vedere l’intera giornata.',
    dayOverviewShow: 'Mostra l’anteprima del giorno',
    dayOverviewHide: 'Nascondi l’anteprima del giorno',
    guestsCanBook: 'Gli ospiti possono prenotare in questo orario.',
    guestsCannotBook: 'Gli ospiti non possono prenotare in questo orario.',
    emptyServiceTimes:
      'Nessun orario. Aggiungi colazione, pranzo o cena così gli ospiti sanno quando prenotare.',
    timeStepHint:
      'Scegli orari a passi di 15 minuti. Le prenotazioni si fermano all’ora di fine.',
    switchOn: 'Attivo',
    switchOff: 'Spento',
    editAction: 'Modifica',
    deleteAction: 'Elimina',
    howItWorks: 'Come funziona',
    previewOnly: 'Solo anteprima — modifica le fasce sotto',
    stepBadge: 'Passo: 15 min — 04:00 / 03:59',
    localDay: 'Giorno locale: {{time}}',
    tips: {
      turnOnTitle: 'Attiva',
      turnOn: 'Lascia attive solo le fasce che usi.',
      moveTitle: 'Sposta',
      move: 'Sposta inizio e fine con gli slider.',
      saveTitle: 'Salva',
      save: 'Il sistema evita le sovrapposizioni.',
    },
    shiftsTitle: 'Orari di servizio',
    shiftsHint:
      'Aggiungi gli orari in cui accetti prenotazioni, come pranzo o cena. Puoi spegnerne uno senza eliminarlo.',
    createMealBand: 'Aggiungi un orario',
    createMealBandAction: 'Aggiungi',
    createMealBandHint:
      'Dai un nome, poi scegli quando inizia e quando finisce.',
    editMealBand: 'Modifica orario',
    editMealBandNamed: 'Modifica {{name}}',
    updateMealBandAction: 'Salva',
    mealBandName: 'Nome',
    mealBandNamePlaceholder: 'Pranzo',
    selectStartTime: 'Seleziona ora di inizio',
    selectEndTime: 'Seleziona ora di fine',
    mealBandLimitReached: 'Puoi aggiungere al massimo 20 orari.',
    customMealBand: 'Orario di servizio',
    deleteMealBand: 'Elimina {{name}}',
    deleteMealBandTitle: 'Eliminare questo orario?',
    deleteMealBandDescription:
      'Vuoi eliminare {{name}}? Questa azione non può essere annullata.',
    active: 'Attiva',
    inactive: 'Inattiva',
    shiftLegendOff: '{{name}}: non accetta prenotazioni',
    shiftLegendRange: '{{name}}: {{from}} - {{to}}',
    booking: 'Prenotazione',
    activeBand: 'Accetta prenotazioni',
    activeBandHint:
      'Spegnilo se gli ospiti non possono prenotare in questo orario.',
    deactivate: 'Disattiva',
    deactivated: 'Disattivata',
    activate: 'Attiva',
    valueSaved: 'Valore salvato',
    valueSavedHint: 'I clienti potranno prenotare in questa fascia oraria.',
    selectBandTitle: 'Seleziona la fascia con una sola barra',
    selectBandHint: 'La parte selezionata rispetta le altre fasce attive.',
    fine: 'Fine',
    spaceAvailable: 'Spazio disponibile per {{name}}: {{from}} - {{to}}',
    minStartValue: 'Min inizio',
    maxEndValue: 'Max fine',
    bandTurnedOff:
      'Questa fascia è disattivata. Attivala con l’interruttore sopra per modificare gli orari.',
    activityHours: 'Orari di attività',
    start: 'Inizio',
    end: 'Fine',
    closuresTitle: 'Giorni in cui il ristorante è chiuso',
    closuresHint: 'Gli ospiti non possono prenotare in queste date.',
    addClosure: 'Aggiungi chiusura',
    closuresModalHint:
      'Seleziona una data di inizio e di fine per il periodo di chiusura.',
    closingPeriod: 'Periodo di chiusura',
    chooseDateRange: 'Scegli un intervallo di date',
    fromDate: 'Dalla data',
    toDate: 'Alla data',
    actions: 'Azioni',
    noClosures: 'Nessun giorno di chiusura.',
    deleteClosure: 'Elimina chiusura',
    locationTitle: 'Posizione',
    trackingTitle: 'Monitoraggio e analitica',
    trackingHint:
      'Configura Google Tag Manager, il Pixel di Facebook e gli strumenti di monitoraggio',
    googleTagManager: 'Google Tag Manager',
    facebookPixel: 'Pixel di Facebook',
    tabs: {
      tabListLabel: 'Sezioni configurazione locale',
      general: 'Generale',
      bookingTimeSlots: 'Orari',
      locationTracking: 'Posizione e monitoraggio',
    },
    shifts: {
      breakfast: 'Colazione',
      breakfastSubtitle: 'Fascia colazione',
      lunch: 'Pranzo',
      lunchSubtitle: 'Fascia pranzo',
      aperitivo: 'Aperitivo',
      aperitivoSubtitle: 'Fascia happy hour',
      dinner: 'Cena',
      dinnerSubtitle: 'Fascia cena',
    },
    fields: {
      localName: 'Nome locale',
      localCapacity: 'Capacità locale',
      address: 'Indirizzo',
      city: 'Città',
      zip: 'CAP',
      province: 'Provincia',
      nation: 'Nazione',
      state: 'Stato',
      latitude: 'Latitudine',
      longitude: 'Longitudine',
      gtmId: 'ID contenitore',
      pixelId: 'ID pixel',
    },
    validation: {
      nameRequired: 'Il nome locale è obbligatorio',
      mealBandNameRequired: 'Inserisci un nome',
      mealBandNameDuplicate: 'Esiste già un orario con questo nome',
      openingTimeRequired: "L'ora di inizio è obbligatoria",
      closingTimeRequired: "L'ora di fine è obbligatoria",
    },
    toast: {
      saved: 'Configurazione salvata',
      saveError: 'Impossibile salvare la configurazione',
      logoUploadSuccess: 'Logo caricato con successo',
      logoUploadError: 'Impossibile caricare il logo',
      logoRemoveSuccess: 'Logo rimosso con successo',
      logoRemoveError: 'Impossibile rimuovere il logo',
      selectDateRange: 'Seleziona una data di inizio e una di fine',
      closureAdded: 'Periodo di chiusura aggiunto',
      closureAddError: 'Impossibile aggiungere il periodo di chiusura',
      closureOverlap:
        'Questo periodo si sovrappone a un periodo di chiusura esistente',
      closureRemoved: 'Periodo di chiusura rimosso',
      closureDeleteError: 'Impossibile eliminare il periodo di chiusura',
      mealBandCreated: 'Orario aggiunto',
      mealBandCreateError: 'Impossibile aggiungere questo orario',
      mealBandUpdated: 'Orario aggiornato',
      mealBandUpdateError: 'Impossibile aggiornare questo orario',
      mealBandStatusUpdated: 'Stato prenotazioni aggiornato',
      mealBandStatusError: 'Impossibile aggiornare lo stato delle prenotazioni',
      mealBandDeleted: 'Orario eliminato',
      mealBandDeleteError: 'Impossibile eliminare questo orario',
      mealBandInvalidCode: 'Questo orario non è stato salvato. Riprova.',
    },
  },
  stripe: {
    common: {
      refresh: 'Aggiorna da Stripe',
      noBranch: 'Seleziona una struttura per continuare.',
      processing: 'Elaborazione…',
    },
    configuration: {
      title: 'Configurazione Stripe',
      subtitle: "Dati live dell'account Stripe collegato.",
      subtitleWithBranch: "Dati live dell'account collegato a {{branch}}.",
      loadError: 'Impossibile recuperare lo stato direttamente da Stripe.',
      notConnectedTitle: 'Account Stripe non configurato',
      notConnectedDescription:
        'La struttura selezionata non ha ancora un account Stripe collegato. Completa l’onboarding dal backoffice.',
      accountSection: 'Informazioni account',
      accountHeading: 'Account Stripe',
      email: 'Email',
      country: 'Paese',
      currency: 'Valuta',
      accountType: 'Tipo account',
      disabledReason: 'Motivo blocco',
      disabledReasonNone: 'Nessuno',
      detailsSubmitted: 'Dati inviati',
      chargesEnabled: 'Pagamenti',
      payoutsEnabled: 'Accrediti',
      requirements: {
        title: 'Requisiti Stripe',
        currentlyDue: 'Da completare',
        pastDue: 'Scaduti',
        pendingVerification: 'In verifica',
        none: 'Nessuno',
      },
      policy: {
        title: 'Policy di pagamento',
        description:
          'Testo mostrato al cliente quando la prenotazione richiede verifica carta, preautorizzazione o pagamento.',
        versionMeta: 'Versione {{version}} · aggiornata {{date}}',
        useTemplate: 'Suggeriscimi una struttura per policy',
        templateWarning:
          'Il modello è un punto di partenza: sostituisci tutti i campi tra parentesi quadre e fai verificare il testo da un consulente legale.',
        placeholder: 'Inserisci la policy di pagamento della struttura…',
        charCount: '{{count}}/{{max}} caratteri',
        save: 'Salva e crea nuova versione',
        template: paymentPolicyTemplate,
      },
      toast: {
        policySaved: 'Policy di pagamento salvata.',
        policyError: 'Impossibile salvare la policy di pagamento.',
      },
      validation: {
        policyRequired: 'Inserisci il testo della policy di pagamento.',
        policyMax: 'La policy non può superare 20000 caratteri.',
      },
    },
    transactions: {
      title: 'Transazioni Stripe',
      empty: 'Nessuna operazione',
      emptyDescription:
        'Non risultano ancora pagamenti o depositi per questa struttura.',
      notConnectedTitle: 'Account Stripe non collegato.',
      notConnectedDescription:
        'Qui restano disponibili tutte le transazioni storiche registrate per la struttura.',
      filters: {
        all: 'Tutte',
        succeeded: 'Riuscite',
        uncaptured: 'Non incassate',
        failed: 'Fallite',
      },
      columns: {
        date: 'Data',
        booking: 'Prenotazione',
        operation: 'Operazione',
        requested: 'Richiesto',
        captured: 'Incassato',
        status: 'Stato',
        details: 'Dettagli',
      },
      mode: {
        payNow: 'Pagamento',
        authorizeOnly: 'Deposito / preautorizzazione',
      },
      status: {
        linkCreated: 'In attesa del pagamento',
        processing: 'Registrato, in attesa della conferma Stripe',
        pending: 'In attesa',
        authorized: 'Non incassato',
        partiallyPaid: 'Parzialmente incassato',
        paid: 'Riuscito',
        failed: 'Fallito',
        canceled: 'Annullato',
        refundPending: 'Rimborso in corso',
        partiallyRefunded: 'Rimborso parziale',
        refunded: 'Rimborsato',
        refundFailed: 'Rimborso fallito',
      },
      actions: {
        capture: 'Incassa deposito',
        release: 'Rifiuta deposito',
      },
      confirm: {
        captureTitle: 'Incassare il deposito?',
        captureDescription:
          'Verranno incassati {{amount}} per la prenotazione {{booking}}.',
        releaseTitle: 'Rifiutare il deposito?',
        releaseDescription:
          'L’autorizzazione di {{amount}} verrà annullata e l’importo sarà rilasciato.',
      },
      toast: {
        captureSuccess: 'Stripe ha confermato l’incasso.',
        captureError:
          'Incasso non riuscito. Controlla lo stato su Stripe e riprova.',
        releaseSuccess: 'Stripe ha annullato l’autorizzazione.',
        releaseError:
          'Rilascio non riuscito. Controlla lo stato su Stripe e riprova.',
      },
      technical: {
        title: 'Dettagli tecnici',
        description:
          'Identificativi usati da PRO20 e Stripe per questa operazione.',
        open: 'Mostra dettagli tecnici',
        close: 'Chiudi',
        bookingCode: 'Codice prenotazione',
        depositCode: 'Codice transazione PRO20',
        paymentIntentId: 'PaymentIntent Stripe',
        paymentLinkId: 'Payment Link Stripe',
        connectedAccountId: 'Account Stripe',
      },
    },
  },
  messages: {
    orderingEvents: {
      CREATED: 'Ordine ricevuto',
      ACCEPTED: 'Ordine accettato',
      PREPARING: 'Ordine in preparazione',
      READY: 'Ordine pronto',
      DELIVERED: 'Ordine consegnato',
      COLLECTED: 'Ordine ritirato',
      CANCELLED: 'Ordine annullato',
      FAILED: 'Consegna non riuscita',
      PAYMENT_REQUIRED: 'Link di pagamento',
      PAYMENT_CONFIRMED: 'Pagamento ricevuto',
      PAYMENT_FAILED: 'Pagamento non riuscito',
      REFUNDED: 'Rimborso confermato',
      SCHEDULE_REMINDER: 'Promemoria ordine',
    },
    orderingFlows: {
      CREATED:
        'Dopo la creazione dell’ordine. Il cliente sa che il ristorante deve ancora accettarlo.',
      ACCEPTED: 'Quando accetti l’ordine e scegli di avvisare il cliente.',
      PREPARING:
        'Quando inizi la preparazione e scegli di avvisare il cliente.',
      READY:
        'Quando l’ordine è pronto e scegli di avvisare il cliente. Lo stesso testo vale per asporto e delivery; non indica che il fattorino è partito.',
      DELIVERED:
        'Quando processi singolarmente l’ordine come consegnato e scegli di avvisare il cliente. Nessun invio dalle azioni massive o del fattorino.',
      COLLECTED:
        'Quando processi singolarmente l’ordine come ritirato e scegli di avvisare il cliente. Nessun invio dalle azioni massive.',
      CANCELLED:
        'Quando annulli l’ordine e scegli di avvisare il cliente. Non conferma un rimborso.',
      FAILED:
        'Quando segni la consegna come non riuscita e scegli di avvisare il cliente.',
      PAYMENT_REQUIRED:
        'Solo dopo la creazione effettiva di un link di pagamento valido.',
      PAYMENT_CONFIRMED:
        'Dopo la conferma effettiva del pagamento, automatica o manuale. Le azioni massive restano silenziose.',
      PAYMENT_FAILED:
        'Se il tentativo di pagamento non riesce e l’ordine è ancora da pagare.',
      REFUNDED: 'Solo dopo il completamento effettivo del rimborso.',
      SCHEDULE_REMINDER:
        'Prima dell’orario previsto, solo per ordini accettati o in preparazione. Esclude i pagamenti con carta non completati.',
    },
    ordering: {
      configurationHint:
        'Attiva questo messaggio per gli ordini di questa sede. Nessun messaggio viene inviato quando salvi.',
      initialize: 'Crea messaggi Delivery e Asporto',
      initialized: 'Messaggi creati. Nessun invio effettuato.',
      loadError: 'Impossibile caricare i messaggi Delivery e Asporto. Riprova.',
      missing:
        'Crea i messaggi mancanti. I testi già personalizzati verranno mantenuti. Tutti i nuovi messaggi saranno disattivati.',
      minutes: 'Minuti prima dell’orario dell’ordine',
      scheduleError: 'Inserisci un numero intero da 1 a 10080 minuti.',
      exclusive:
        'Scegli WhatsApp non ufficiale oppure un template ufficiale, non entrambi.',
      emailPreset: 'Testo email predefinito',
      paymentHint:
        'Lascia disattivato se invii già il link di pagamento manualmente su WhatsApp.',
    },

    searchPlaceholder: 'Cerca per nome, codice, categoria o canale…',
    allCategories: 'Tutte le categorie',
    browseCategories: 'Categorie messaggi',
    searchEmpty:
      'Nessun risultato. Prova un altro termine o seleziona tutte le categorie.',

    giftCards: {
      scheduleTitle: 'Promemoria prima della scadenza',
      daysBeforeExpiry: 'Giorni prima della scadenza',
      scheduleHint:
        'Invia il promemoria questo numero di giorni prima della scadenza, all’ora scelta nel fuso del locale. Sono escluse le carte utilizzate, annullate o senza saldo residuo.',
      scheduleError:
        'Inserisci un numero intero di giorni maggiore di zero e un’ora tra 00:00 e 23:00.',

      creation: 'Creazione gift card',
      expired: 'Gift card scaduta',
      walletHint:
        'Il backend valorizza wallet_link durante l’invio. Un pulsante URL Wallet deve usare il suffisso della singola carta, mai un link cliente fisso.',

      initialize: 'Crea messaggi mancanti',
      initializeSuccess: 'Configurazioni gift card create.',
      initializeError: 'Il backend non ha creato le configurazioni gift card.',
      missing:
        'Mancano le seguenti configurazioni. Creale per associare i template WhatsApp ufficiali ed email.',
      notCreated: 'Da creare nel backend',
      loadError:
        'Impossibile caricare le configurazioni Marketing. Riprova prima di modificarle.',
    },
    title: 'Centro messaggi',
    subtitle:
      'Gestisci tutti i messaggi automatici WhatsApp, SMS ed Email della sede.',
    configuredLabel: 'Messaggi configurati',
    refresh: 'Aggiorna',
    emptyCategory: 'Nessun messaggio in questa categoria.',
    emptyTitle: 'Nessun messaggio trovato',
    emptyDescription:
      'Non ci sono ancora messaggi automatici configurati per questa sede.',
    noBranchTitle: 'Nessuna sede selezionata',
    noBranchDescription:
      'Seleziona una sede per visualizzare e gestire i messaggi automatici.',
    loadErrorTitle: 'Impossibile caricare i messaggi',
    loadErrorDescription:
      'Caricamento dell’elenco messaggi non riuscito. Riprova.',
    categories: {
      restaurantOrdering: 'Delivery e Asporto',
      supplierOrders: 'Ordini ai fornitori',
      giftCards: 'Messaggi gift card',
      standard: 'Messaggi standard',
      cta: 'CTA',
      marketing: 'Messaggi di marketing',
      preBooking: 'Comunicazioni pre-prenotazione',
      postBooking: 'Comunicazioni post-prenotazione',
      switchboard: 'Messaggi centralino',
    },
    categoryDescriptions: {
      marketing:
        'Messaggi promozionali, di fidelizzazione e azioni automatiche sulle carte.',
    },
    items: {
      ORDER_SENT: 'Ordine inviato',
      ORDER_UPDATED: 'Ordine aggiornato',
      ORDER_DELIVERY: 'Consegna ordine',
      ORDER_GENERIC: 'Ordine fornitore',

      ATT: 'Messaggio in attesa di conferma',
      ARR: 'Conferma di arrivo',
      CONF: 'Conferma prenotazione',
      REF: 'Prenotazione rifiutata',
      DEL: 'Prenotazione eliminata',
      FQ1: 'Gestione coda 1',
      FQ2: 'Gestione coda 2',
      EDTU: 'Aggiornamento prenotazione (utente)',
      EDTC: 'Aggiornamento prenotazione (cliente)',
      EDTR: 'Aggiornamento prenotazione (ristorante)',
      ANS1: 'Avviso no-show 1',
      ANS2: 'Avviso no-show 2',
      ANS3: 'Avviso no-show 3',
      RPR: 'Promemoria promo',
      DOB: 'Promo compleanno',
      AAD: 'Richiesta recensione',
      RRP: 'Promo ci manchi',
      CBP: 'Invito a tornare',
      CENTRALINO1: 'Funzione centralino 1',
      CENTRALINO2: 'Funzione centralino 2',
      CENTRALINO3: 'Funzione centralino 3',
    },
    variables: {
      nomelocale: '@nomelocale@',
      ospiti: '@ospiti@',
      bambini: '@bambini@',
      nome: '@nome@',
      cognome: '@cognome@',
      oraprenotazione: '@oraprenotazione@',
      dataprenotazione: '@dataprenotazione@',
      servizioextra: '@servizioextra@',
      linkmodificaprenotazione: '@linkmodificaprenotazione@',
      linkrichiestacartadicredito: '@linkrichiestacartadicredito@',
      avviso_uscita: '@avviso_uscita@',
    },
    detail: {
      close: 'Chiudi',
      pageSubtitle:
        'Configura i canali di messaggistica automatica per questo modello.',
      marketingTitle: 'Messaggio di marketing',
      enableConfiguration: 'Abilita questa configurazione',
      enableConfigurationHint:
        'Le modifiche si applicano a tutti i programmi che usano {{type}}.',
      enableWhatsApp: 'Abilita WhatsApp',
      enableTemplates: 'Abilita i template',
      enableEmail: 'Abilita Email',
      emailObject: 'Oggetto',
      configuration: 'Configurazione',
      configurationHint: 'Scegli come vuoi inviare i messaggi',
      configurationHintPreBooking: 'Configura metodi di invio e tempistiche',
      configurationHintPostBooking:
        'Configura invio e programmazione dei messaggi post-prenotazione',
      sendingMethods: 'Metodi di invio',
      sendingMode: 'Modalità di invio',
      sendingTiming: 'Tempistica di invio',
      whenToSend: 'Quando inviare',
      sendingNow: 'Orario di invio',
      timingImmediate: 'Invia il messaggio subito dopo la prenotazione 🕒',
      timingOneHourBefore: 'Invia il messaggio 1 ora prima 🕒',
      timingHoursAdvance: 'Invia il messaggio {{count}} ore in anticipo 🕒',
      timingOneDayAdvance: 'Invia il messaggio 1 giorno in anticipo 🕒',
      timingDaysAdvance: 'Invia il messaggio {{count}} giorni in anticipo 🕒',
      whenToSendOneDayLater: 'Invia il messaggio 1 giorno dopo',
      whenToSendDaysLater: 'Invia il messaggio {{count}} giorni dopo',
      sendingWhatsAppOnly: 'Invia solo WhatsApp',
      sendingWhatsAppAndEmail: 'Invia WhatsApp ed Email',
      whatsappMessage: 'Messaggio WhatsApp',
      whatsappMessageDesc: 'Configura il messaggio WhatsApp inviato al cliente',
      whatsappForeign: 'WhatsApp clienti esteri',
      whatsappForeignDesc:
        'Versione in inglese. Messaggio automatico in inglese per clienti con prefisso internazionale.',
      whatsappBusinessTemplate: 'Template WhatsApp Business',
      whatsappBusinessTemplateDesc:
        'Seleziona tra i template approvati da Meta per l’invio tramite WhatsApp Business API.',
      smsMessage: 'Messaggio SMS',
      smsMessageDesc: 'Configura il messaggio SMS inviato al cliente',
      email: 'Email',
      emailDesc: 'Configura il messaggio email inviato al cliente',
      emailSubject: 'Oggetto email',
      emailSubjectPlaceholder: 'Es: Comunicazione da @hotel-name',
      bodyPlaceholder: 'Scrivi qui il tuo messaggio...',
      inspireMe: 'Ispirami',
      testSending: 'Invio di prova',
      testSendPrompt: 'Inserisci il numero a cui vuoi inviare il messaggio',
      testSendAction: 'Invia',
      selectTemplate: 'Seleziona template',
      selectTemplateTooltip: 'Seleziona un template approvato da Meta',
      selectTemplateHint:
        'Scegli un template approvato da Meta per questo messaggio.',
      selectedTemplate: 'Template selezionato',
      noTemplateSelected: 'Nessun template selezionato',
      noTemplateHint:
        'Nessun template selezionato. Seleziona un template approvato da Meta per configurare l’invio via WhatsApp Business API.',
      templateCategory: 'Categoria',
      templateType: 'Tipo',
      templateLanguages: 'Lingue',
      templateLanguage: 'Lingua',
      templateVariableMap: 'Variable map',
      templateBody: 'Body',
      templateBodyEmpty:
        'Nessuna anteprima del body disponibile per questa lingua.',
      ctaButton: 'Call to action',
      ctaDynamicPath: 'Path dinamico',
      ctaDynamicPathPlaceholder:
        'Path dinamico, es: /menu/xxxx0111 o /booking/0000101111',
      ctaDynamicPathHint:
        'Ciò che inserirai qui sarà concatenato all’url del template al momento dell’invio, al posto di {{1}}, permettendoti di creare link dinamici in base al contesto del messaggio.',
      ctaDynamicPathRequired: 'Il path dinamico non può essere vuoto.',
      ctaUrlValid: '{{url}} è valido',
      ctaUrlInvalid: 'URL non valido. Controlla il formato.',
      ctaLinksTooltip: 'Quali opzioni ho?',
      ctaLinksTitle: 'Link disponibili per i servizi creati su Pro20',
      ctaLinksDesc: 'Seleziona un link per usarlo come path dinamico.',
      ctaLinksEmpty:
        'Non hai ancora creato nessun servizio su Pro20, non riesco a suggerirti nulla.',
      ctaLinkUnnamed: 'Link senza nome',
    },
    toast: {
      refreshed: 'Elenco messaggi aggiornato.',
      saveSuccess: 'Configurazione messaggio salvata.',
      saveError: 'Impossibile salvare la configurazione del messaggio.',
      testSent: 'Messaggio di prova inviato correttamente.',
      sendError: 'Impossibile inviare il messaggio.',
      templateSelected: 'Template selezionato.',
      resetSuccess: 'Configurazione ripristinata ai valori predefiniti.',
      resetError: 'Impossibile ripristinare la configurazione. Riprova.',
      applyDefaultsSuccess: 'Testi predefiniti applicati e canale abilitato.',
      applyDefaultsError:
        'Impossibile applicare i default. Riprova oppure annulla.',
      detailUnavailable:
        'Il dettaglio del messaggio non è ancora disponibile per questa categoria.',
    },
    validation: {
      whatsappBodyRequired:
        'Inserisci un messaggio WhatsApp quando WhatsApp è abilitato.',
      templateRequired:
        'Seleziona un template WhatsApp Business quando i template sono abilitati.',
      emailSubjectRequired:
        'Inserisci l’oggetto dell’email quando Email è abilitata.',
      emailBodyRequired:
        'Inserisci il corpo dell’email quando Email è abilitata.',
    },
    reset: {
      tooltip: 'Ripristina configurazione predefinita',
      title: 'Ripristinare la configurazione?',
      description:
        'Le configurazioni esistenti verranno sostituite con quella predefinita.',
      confirm: 'Sì, procedi!',
      cancel: 'Annulla',
      resetting: 'Ripristino…',
    },
    applyDefaults: {
      whatsappTooltip:
        'Applica i testi predefiniti e abilita WhatsApp per questo gruppo',
      emailTooltip:
        'Applica oggetto e testo predefiniti e abilita email per questo gruppo',
      whatsappAria: 'Applica configurazione WhatsApp predefinita',
      emailAria: 'Applica configurazione email predefinita',
      whatsappTitle: 'Applicare i default WhatsApp?',
      emailTitle: 'Applicare i default email?',
      description:
        'I testi del canale scelto saranno sostituiti e il canale verrà abilitato per tutto il gruppo. Attivazione dei messaggi, altri canali e regole di invio restano invariati.',
      marketingWhatsappNote:
        'WhatsApp userà il messaggio libero; i dati del template ufficiale saranno conservati.',
      confirm: 'Applica e abilita',
      cancel: 'Annulla',
      applying: 'Applicazione…',
    },
  },
  configureWhatsApp: {
    title: 'Configura WhatsApp',
    subtitle: 'Collega un numero WhatsApp Business per questa sede',
    disabled: {
      badge: 'Configurazione disabilitata',
      title: 'Configurazione WhatsApp non disponibile',
      description:
        'Procedi tramite l’app mobile per abilitare questa funzione.',
    },
    onboarding: {
      eyebrow: 'WhatsApp Business',
      title: 'Collega il numero WhatsApp locale',
      description:
        'Crea una nuova istanza, scansiona il QR code e abilita l’invio di messaggi automatici dalla piattaforma PRO20.',
      create: 'Crea configurazione',
      guided: 'Operazione guidata e reversibile',
      featureSecureTitle: 'Sicuro e affidabile',
      featureSecureDesc: 'Sicurezza di livello enterprise',
      featureQuickTitle: 'Setup rapido',
      featureQuickDesc: 'Richiede meno di 2 minuti',
      featureReversibleTitle: 'Reversibile',
      featureReversibleDesc: 'Modifica o disconnetti in qualsiasi momento',
      step1Title: 'Crea istanza',
      step1Desc:
        'Genera la configurazione tecnica collegata alla sede selezionata.',
      step1Time: '~ 30 sec',
      step2Title: 'Scansiona QR',
      step2Desc:
        'Apri WhatsApp sul telefono e collega il dispositivo tramite QR code.',
      step2Time: '~ 60 sec',
      step3Title: 'Prova l’invio',
      step3Desc:
        'Invia un messaggio di prova e verifica che tutto sia operativo.',
      step3Time: '~ 30 sec',
    },
    creating: {
      title: 'Configurazione in corso…',
      description: 'Attendi mentre creiamo l’istanza WhatsApp.',
      starting:
        'WhatsApp si sta avviando. La prima connessione può richiedere un paio di minuti.',
    },
    success: {
      title: 'Completato',
      description: 'Istanza creata con successo!',
      banner:
        'Ora puoi collegare il dispositivo con un QR code o un codice di abbinamento.',
      continue: 'Continua',
    },
    configured: {
      heading: 'Istanza WhatsApp',
      subtitle:
        'Gestisci collegamento, stato e messaggi di prova per questa sede.',
      statusInstanceCreated: 'Istanza creata',
      statusQr: 'In attesa di scansione QR',
      statusReady: 'Connesso',
      statusCreating: 'Creazione…',
      statusFailed: 'Connessione non riuscita',
      restart: 'Riavvia connessione',
      stillStarting:
        'WhatsApp è ancora in avvio. Questa pagina continuerà a controllare. Usa Scansiona QR quando il codice è pronto, oppure riavvia la connessione.',
      failureHint:
        'Il motore WhatsApp non si è avviato. Riavvia la connessione per riprovare.',
      scanQr: 'Scansiona QR',
      delete: 'Elimina',
      deleting: 'Eliminazione...',
      statusDeleting: 'Eliminazione...',
      sessionName: 'Nome sessione',
      instanceId: 'ID istanza',
      connectedNumber: 'Numero collegato',
      operatingStatus: 'Stato operativo',
      nextStepTitle: 'Prossimo passo',
      nextStepQr:
        'Scansiona il QR code con WhatsApp sul telefono per collegare il dispositivo.',
      notLinked: 'Non ancora collegato',
    },
    link: {
      title: 'Collega dispositivo',
      description:
        'Usa un QR code o un codice di abbinamento per collegare WhatsApp.',
      tabQr: 'QR code',
      tabPhone: 'Codice mobile',
      qrInstructions:
        'Apri WhatsApp sul telefono → Dispositivi collegati → Collega un dispositivo, poi scansiona questo codice.',
      waiting: 'In attesa della scansione…',
      phoneInstructions:
        'Inserisci il numero di cellulare da collegare, poi richiedi un codice di abbinamento.',
      phoneLabel: 'Numero di cellulare',
      phonePlaceholder: '333 123 4567',
      getCode: 'Ottieni codice',
      pairingCode: 'Il tuo codice di abbinamento',
      pairingHint:
        'Inserisci questo codice in WhatsApp → Dispositivi collegati → Collega con numero di telefono.',
    },
    delete: {
      title: 'Eliminare la configurazione WhatsApp?',
      description:
        'Verrà rimossa l’istanza per questa sede. Potrai crearne una nuova in seguito.',
      confirm: 'Sì, elimina',
    },
    test: {
      title: 'Invia messaggio di prova',
      phone: 'Numero destinatario',
      phonePlaceholder: '333 123 4567',
      message: 'Messaggio',
      defaultMessage: 'Ciao! Questo è un messaggio di prova da Proventi.',
      send: 'Invia prova',
      success: 'Messaggio di prova inviato con successo.',
      disabledHint:
        'Scansiona il QR code e collega WhatsApp prima di inviare un messaggio di prova.',
      notConnected:
        'La sessione WhatsApp non è connessa. Scansiona il QR code per collegare prima il dispositivo.',
    },
    toast: {
      linked: 'WhatsApp collegato correttamente.',
      stillStarting:
        'WhatsApp è ancora in avvio. Questa pagina continuerà a controllare.',
      createFailed: 'Impossibile creare la sessione WhatsApp.',
      qrFailed: 'Impossibile preparare il QR WhatsApp.',
    },
  },
  configureWhatsAppOfficial: {
    title: 'Configura WhatsApp Ufficiale',
    subtitle:
      'Collega il tuo numero WhatsApp Business ufficiale (Meta) per questa filiale.',
    loading: 'Caricamento stato onboarding…',
    loadError: 'Impossibile caricare lo stato di onboarding.',
    retry: 'Riprova',
    toast: {
      onboardSuccess: 'Onboarding WhatsApp completato con successo.',
      onboardError: 'Impossibile completare l’onboarding WhatsApp.',
      registerSuccess: 'Numero di telefono registrato con successo.',
      registerError: 'Impossibile registrare il numero di telefono.',
      businessDetailsSuccess: 'Dettagli business caricati con successo.',
      businessDetailsError: 'Impossibile caricare i dettagli business.',
      missingPhoneNumberId: 'ID numero di telefono mancante.',
      metaError: 'Errore signup WhatsApp: {{message}}',
      missingBranch: 'Codice filiale mancante.',
      sdkNotReady: 'Facebook SDK non è ancora pronto.',
    },
    onboarding: {
      eyebrow: 'API WhatsApp Business',
      title: 'Connetti con WhatsApp Business API',
      description:
        'In questa sezione puoi creare e collegare un numero al canale WhatsApp Business ufficiale. La configurazione ti consente di associare il tuo numero aziendale alla piattaforma per iniziare a inviare e ricevere messaggi in modo professionale, automatizzato e conforme alle policy.',
      connect: 'Crea configurazione',
      connecting: 'Connessione…',
      howToConnect: 'Come connettersi',
      featuresTitle: 'Con Meta puoi',
      featureExisting: 'Collegare un numero WhatsApp Business esistente',
      featureNew: 'Creare un nuovo account con Meta',
      step1Title: 'Registra il tuo numero',
      step1Desc:
        'Aggiungi e verifica il numero di telefono aziendale con Meta per attivare il canale.',
      step1Time: '~ 2 min',
      step2Title: 'Crea un template e fallo approvare',
      step2Desc:
        'Crea un template di messaggio e invialo a Meta per la revisione e l’approvazione.',
      step2Time: '~ 1 giorno',
      step3Title: 'Invia un template di testo',
      step3Desc:
        'Invia un template approvato per avviare una nuova conversazione.',
      step3Time: '~ 30 sec',
      step4Title: 'Invia a un numero di telefono',
      step4Desc:
        'Recapita i messaggi a qualsiasi numero di telefono del cliente e inizia a chattare.',
      step4Time: '~ 30 sec',
    },
    configured: {
      heading: 'Informazioni onboarding WhatsApp',
      subtitle: 'Il tuo account WhatsApp Business ufficiale è connesso.',
      connectedBadge: 'Connesso',
      phoneNumberId: 'ID numero di telefono',
      wabaId: 'ID account WhatsApp Business',
      businessId: 'ID azienda',
      onboardingStatus: 'Stato onboarding',
      phoneRegistrationStatus: 'Stato registrazione numero',
      statusCompleted: 'Completato',
      statusSuccess: 'Successo',
      statusPending: 'In sospeso',
      statusRegistered: 'Registrato',
      statusNotRegistered: 'Non registrato',
      registerPhone: 'Registra il tuo numero',
      registeringPhone: 'Registrazione…',
      businessDetailsHeading: 'Dettagli business',
      businessDetailsEmpty:
        'I dettagli business non sono ancora disponibili. Recuperali da Meta con l’ID del numero di telefono.',
      fetchBusinessDetails: 'Ottieni dettagli business',
      fetchingBusinessDetails: 'Recupero…',
      verifiedName: 'Nome verificato',
      displayPhoneNumber: 'Numero visualizzato',
      qualityRating: 'Qualità',
      codeVerificationStatus: 'Verifica codice',
      platformType: 'Tipo piattaforma',
      throughputLevel: 'Throughput',
      phoneStatus: 'Stato telefono',
      accountMode: 'Modalità account',
      nameStatus: 'Stato nome',
      messagingLimitTier: 'Limite messaggi',
      officialBusinessAccount: 'Account aziendale ufficiale',
      businessProfileHeading: 'Profilo aziendale',
      email: 'Email aziendale',
      vertical: 'Categoria attività',
      description: 'Descrizione attività',
      about: 'Informazioni',
      address: 'Indirizzo',
      websites: 'Siti web',
      metaPhoneNumberId: 'ID numero Meta',
      webhookApplication: 'URL webhook',
      reset: 'Reimposta configurazione',
      resetting: 'Reimpostazione…',
    },
    reset: {
      title: 'Reimpostare la configurazione?',
      description:
        'Questo reimposta solo la vista locale per poter rieseguire la configurazione. Non elimina l’onboarding sul backend.',
      confirm: 'Sì, reimposta',
    },
    guide: {
      title: 'Guida all’onboarding delle API WhatsApp Business',
      intro:
        'Benvenuto! Segui i passaggi seguenti per collegare il tuo account WhatsApp Business alla nostra piattaforma.',
      prerequisitesTitle: 'Prerequisiti',
      prerequisitesIntro:
        'Prima di iniziare, assicurati di avere quanto segue:',
      prereq1: 'Un account Facebook con accesso Amministratore.',
      prereq2:
        'Un account Meta Business Manager (o essere pronto a crearne uno).',
      prereq3:
        'Un numero di cellulare valido in grado di ricevere la verifica via SMS o chiamata vocale.',
      prereq4:
        'Un numero di telefono non attualmente registrato con WhatsApp Messenger, l’app WhatsApp Business o un altro provider di API WhatsApp Business.',
      prereq5:
        'Le informazioni della tua azienda pronte: nome, sito web (facoltativo), email e categoria.',
      prereq6:
        'Una carta di credito/debito valida da aggiungere come metodo di pagamento in Meta.',
      prereq7: 'Una connessione internet stabile.',
      prereqNote:
        'Importante: se il tuo numero di telefono è attualmente utilizzato nell’app WhatsApp Business, rimuovilo dall’app prima di collegarlo alla WhatsApp Business Platform.',
      stepsTitle: 'Passaggi di onboarding',
      step1Title: 'Passaggio 1 – Accedi alla nostra piattaforma',
      step1Body:
        'Accedi al tuo account, vai su Integrazione WhatsApp e clicca su Connetti WhatsApp.',
      step2Title: 'Passaggio 2 – Accedi con Facebook',
      step2Body:
        'Si apre una finestra di accesso sicura di Meta (Facebook). Accedi con il tuo account Facebook e clicca su Continua.',
      step3Title: 'Passaggio 3 – Seleziona o crea un Business Manager',
      step3Body:
        'Seleziona il tuo Meta Business Manager esistente oppure clicca su Crea account aziendale e inserisci nome, email, sito web e paese dell’azienda, quindi clicca su Avanti.',
      step4Title: 'Passaggio 4 – Crea o seleziona un account WhatsApp Business',
      step4Body:
        'Usa un account WhatsApp Business esistente oppure creane uno nuovo fornendo nome account, nome visualizzato e categoria aziendale, quindi clicca su Continua.',
      step5Title: 'Passaggio 5 – Aggiungi il tuo numero di telefono',
      step5Body:
        'Seleziona il paese, inserisci il numero di telefono, scegli la verifica via SMS o chiamata vocale, quindi inserisci il codice di verifica a 6 cifre.',
      step6Title: 'Passaggio 6 – Concedi le autorizzazioni',
      step6Body:
        'Meta richiederà le autorizzazioni necessarie. Clicca su Continua e Fine; la nostra piattaforma completa automaticamente la configurazione.',
      step7Title: 'Passaggio 7 – Connessione completata',
      step7Body:
        'Una volta connesso, vedrai il numero di telefono collegato, l’account WhatsApp Business, lo stato della connessione e sarai pronto a inviare e ricevere messaggi.',
      nextStepsTitle: 'Prossimi passi consigliati',
      nextStep1: 'Crea i tuoi template di messaggio WhatsApp.',
      nextStep2:
        'Aggiungi un metodo di pagamento valido in Meta Business Manager.',
      nextStep3: 'Completa la verifica aziendale Meta (consigliata).',
      nextStep4: 'Invia un messaggio template di prova.',
      nextStep5: 'Configura il tuo chatbot o l’automazione (se applicabile).',
      notesTitle: 'Note importanti',
      note1:
        'Un numero di telefono può essere collegato a un solo account WhatsApp alla volta.',
      note2:
        'Non eliminare il tuo account WhatsApp Business da Meta dopo l’onboarding.',
      note3:
        'Mantieni attivo il metodo di pagamento per evitare interruzioni nella consegna dei messaggi.',
      note4:
        'La verifica aziendale è consigliata per limiti di messaggistica più elevati.',
      note5:
        'L’approvazione del nome visualizzato è gestita da Meta e potrebbe richiedere del tempo.',
      issuesTitle: 'Problemi comuni',
      issue1Title: 'Codice di verifica non ricevuto',
      issue1Body:
        'Verifica che il numero di telefono sia corretto, prova l’opzione chiamata vocale e attendi qualche minuto prima di richiedere un altro codice.',
      issue2Title: 'Numero di telefono già registrato',
      issue2Body:
        'Il numero è collegato a WhatsApp Messenger, all’app WhatsApp Business o a un altro provider di API WhatsApp Business. Rimuovilo dal servizio esistente prima dell’onboarding.',
      issue3Title: 'Errore di autorizzazione',
      issue3Body:
        'Assicurati di essere Amministratore sia dell’account Facebook sia del Meta Business Manager.',
      issue4Title: 'Pagamento richiesto',
      issue4Body:
        'Meta richiede un metodo di pagamento valido prima di poter avviare determinate conversazioni. Aggiungi il tuo metodo di pagamento in Meta Business Manager.',
      issue5Title: 'Verifica aziendale in sospeso',
      issue5Body:
        'Il tuo account può comunque essere connesso e utilizzato, ma completare la verifica aziendale è fortemente consigliato per sbloccare limiti di messaggistica più elevati e migliorare l’affidabilità dell’account.',
      needHelpTitle: 'Hai bisogno di aiuto?',
      needHelpIntro:
        'Se riscontri problemi durante l’onboarding, contatta il nostro team di supporto con:',
      help1: 'Screenshot dell’errore.',
      help2: 'Numero di telefono (incluso il prefisso internazionale).',
      help3: 'ID Meta Business Manager (se disponibile).',
      help4: 'ID account WhatsApp Business (se disponibile).',
      needHelpOutro:
        'Fornire queste informazioni ci aiuterà a risolvere il problema più rapidamente.',
    },
  },
  whatsAppOfficial: {
    title: 'WhatsApp Official',
    subtitle: 'Manage inbox conversations for the official WhatsApp channel.',
    systemBadge: 'System',
    disabled: {
      badge: 'Sezione non abilitata',
      title: 'Questa sezione non è abilitata',
      description:
        'Questa funzione è attualmente disabilitata per il tuo account. Contatta il supporto per saperne di più.',
      infoTitle: 'Hai bisogno di accedere a questa sezione?',
      infoDescription:
        'Contatta il nostro team di supporto e ti aiuteremo a iniziare.',
      footerHelp: 'Hai ancora bisogno di aiuto?',
      footerHelpDesc:
        'Il nostro team di supporto è disponibile 24/7 per assisterti.',
    },
    inbox: {
      searchPlaceholder: 'Search conversations...',
      noConversations: 'No conversations found',
      noConversationsDesc:
        'New chats will appear here as soon as customers contact this branch.',
      emptySelectionTitle: 'Select a chat',
      emptySelectionDescription:
        'Choose a conversation from the list to view the thread and reply.',
    },
    thread: {
      sessionLabel: 'State 24 hours',
      sessionTooltip:
        'When the 24-hour service window is active, you can send free text replies. Otherwise, you must send an approved template first.',
      active: 'Active',
      inactive: 'Inactive',
      today: 'Today',
      yesterday: 'Yesterday',
      emptyTitle: 'No messages yet',
      emptyDescription:
        'This conversation is ready to receive the first inbound or outbound message.',
      pending: 'Invio…',
      retry: 'Riprova',
      retryConfirmTitle: 'Riprovare il messaggio?',
      retryConfirmMessage:
        'Sei sicuro di voler inviare di nuovo questo messaggio?',
      billingFree: 'Gratis',
      billingPaid: 'A pagamento',
    },
    delivery: {
      warningTooltip: 'Non consegnato — tocca per i dettagli',
      detailsTitle: 'Problema di consegna',
      fallbackTitle: 'Il messaggio non è stato consegnato',
      technicalDetails: 'Dettagli tecnici',
      noTechnicalDetails: 'Nessun dettaglio tecnico disponibile.',
    },
    composer: {
      placeholder: 'Scrivi un messaggio...',
      send: 'Invia',
      submitTemplate: 'Invia template',
      templateOnlyTitle: 'Template richiesto',
      templateOnlyDescription:
        'La finestra di servizio di 24 ore non è attiva. Invia un template approvato per riaprire la conversazione.',
      attach: 'Allega file',
      attachImage: 'Immagine',
      attachAudio: 'Audio',
      attachDocument: 'Documento',
      removeAttachment: 'Rimuovi allegato',
    },
    templateSend: {
      title: 'Invia template',
      fillVariablesTitle: 'Compila le variabili',
      customerLabel: 'Cliente',
      phoneLabel: 'Telefono destinatario',
      prefix: 'Prefisso',
      number: 'Numero',
      sendingTo: 'Invio a',
      phoneInvalid: 'Inserisci prefisso e numero di telefono prima di inviare.',
      enterValuePlaceholder: 'Inserisci valore',
      fieldRequired: 'Campo obbligatorio',
      ctaUrlSuffix: 'Suffisso URL CTA',
      ctaUrlSuffixPlaceholder: 'Suffisso URL',
      ctaUrlSuffixHint:
        'Aggiunto all’URL del pulsante quando il template ha un link dinamico.',
      noVariables: 'Questo template non ha variabili da compilare.',
      sendMessage: 'Invia messaggio',
      cancel: 'Annulla',
      previewTitle: 'Anteprima',
      infoCategory: 'Categoria',
      infoLanguage: 'Lingua',
      infoType: 'Tipo',
      infoSendingTo: 'Invio a',
    },
    toast: {
      sendSuccess: 'Messaggio inviato.',
      sendError: 'Impossibile inviare il messaggio.',
      templateSendSuccess: 'Template inviato.',
      templateSendError: 'Impossibile inviare il template.',
      loadConversationsError: 'Impossibile caricare le conversazioni.',
      loadMessagesError: 'Impossibile caricare i messaggi.',
    },
  },
  templateslist: {
    import_no_branch: 'Branch code is missing. Please try again.',
    processing: 'Processing…',
    import_global_template: 'Import global template',
    bulk_import_started: 'Bulk import started',
    import_failed: 'Could not import templates.',
    bulk_import_job_not_found: 'Bulk import job not found.',
    bulk_import_poll_timeout: 'Bulk import timed out. Please try again.',
    bulk_import_bad_accept_response:
      'Invalid response from bulk import request.',
    bulk_import_failed_with_reason: 'Bulk import failed: {{message}}',
    bulk_import_completed_summary:
      'Bulk import completed. Success: {{success}}, Failed: {{failed}}',
    import_success: 'Global templates imported.',
  },
  whatsAppTemplates: {
    browseGroups: 'Gruppi di template',
    allGroups: 'Tutti i template',
    allStatuses: 'Tutti gli stati',
    filterStatus: 'Filtra per stato',
    searchEmpty:
      'Nessun template corrisponde alla ricerca o ai filtri selezionati.',
    groups: {
      UTILITY: 'Servizio',
      MARKETING: 'Marketing',
      AUTHENTICATION: 'Autenticazione',
      OTHER: 'Altri',
    },
    title: 'Template',
    subtitle:
      'Organizza i template per un accesso facile e scala la comunicazione con messaggi predefiniti.',
    learnMore: 'Scopri di più',
    importGlobal: 'Importa Template Globale',
    addTemplate: 'Aggiungi Template',
    tableTitle: 'Template',
    sync: 'Sincronizza Template',
    searchPlaceholder: 'Cerca per nome, tipo o contenuto...',
    emptyTitle: 'Nessun template trovato',
    emptyDescription:
      'Crea o sincronizza i template per iniziare a messaggiare i clienti.',
    detailsTitle: 'Dettagli template',
    deleteTitle: 'Eliminare il template?',
    deleteDescription:
      'Sei sicuro di voler eliminare "{{name}}"? Questa azione non può essere annullata.',
    columns: {
      statusDate: 'Stato / Data',
      nameType: 'Nome / Tipo',
      category: 'Categoria',
      language: 'Lingua',
      actions: 'Azioni',
    },
    stats: {
      approved: 'Template approvati',
      pending: 'Template in attesa',
      draft: 'Template in bozza',
      rejected: 'Template rifiutati',
    },
    status: {
      APPROVED: 'Approvati',
      PENDING: 'In attesa',
      DRAFT: 'Bozze',
      REJECTED: 'Rifiutati',
    },
    languages: {
      en: 'English',
      it: 'Italiano',
      es: 'Español',
      fr: 'Français',
      nl: 'Nederlands',
      zh: '中文',
    },
    actions: {
      menu: 'Azioni template',
      edit: 'Modifica',
      delete: 'Elimina',
    },
    toast: {
      learnMoreSoon: 'La documentazione sarà disponibile a breve.',
      importSoon: 'L’importazione globale sarà disponibile a breve.',
      createSoon: 'La creazione template sarà disponibile a breve.',
      syncSuccess: 'Template sincronizzati con successo.',
      syncError: 'Impossibile sincronizzare i template.',
      importSuccess: 'Template globali importati.',
      importError: 'Impossibile importare i template globali.',
      loadError: 'Impossibile caricare i template.',
      deleteSuccess: 'Template eliminato.',
      deleteError: 'Impossibile eliminare il template.',
    },
    create: {
      title: 'Nuovo Template',
      subtitle: 'Crea facilmente nuovi template in base alle tue esigenze.',
      backToList: 'Torna ai template',
      formTitle: 'Aggiungi Nuovo Template',
      editTitle: 'Modifica template',
      editSubtitle:
        'Aggiorna un template rifiutato e invialo di nuovo in revisione.',
      editFormTitle: 'Modifica template',
      category: 'Categoria',
      name: 'Nome del template',
      namePlaceholder: 'es. booking_confirmation_v1',
      language: 'Lingua del template',
      typeLabel: 'Tipo di template',
      types: {
        text: 'Testo',
        image: 'Immagine',
        video: 'Video',
        audio: 'Audio',
        document: 'Documento',
      },
      categories: {
        UTILITY: 'Utility',
        MARKETING: 'Marketing',
        AUTHENTICATION: 'Autenticazione',
      },
      uploadFile: 'Carica file',
      browse: 'Sfoglia…',
      uploading: 'Caricamento…',
      clearMedia: 'Cancella',
      noFileChosen: 'Nessun file selezionato',
      orPasteUrl: 'Oppure incolla handle / URL',
      mediaUrlPlaceholder: 'https://…',
      previewHandlePlaceholder:
        'Media intestazione caricato (anteprima non disponibile)',
      mediaHintImage: 'Solo JPEG o PNG, massimo 5 MB.',
      mediaHintVideo: 'Solo MP4, massimo 16 MB, max 60 secondi.',
      mediaHintAudio: 'MP3, OGG o M4A, massimo 16 MB.',
      mediaHintDocument: 'PDF o documenti Office, massimo 100 MB.',
      heading: 'Intestazione (opzionale)',
      headingHint: 'Mostrata sopra il corpo del messaggio.',
      body: 'Corpo',
      footer: 'Piè di pagina',
      variablesTitle: 'Variabili',
      variables: {
        requested_amount: 'Acconto richiesto',
        balance_due: 'Saldo rimanente',
        paid_total: 'Totale già ricevuto',
        deposit_percentage: 'Percentuale acconto',
        payment_link: 'Link pagamento acconto',

        service_name: 'Servizio',
        order_number: 'Numero ordine',
        ordering_channel: 'Asporto / Delivery',
        scheduled_label: 'Data e ora ordine',
        total_amount: 'Totale ordine',
        order_link: 'Link riepilogo ordine',
        delivery_address: 'Indirizzo consegna',
        pickup_address: 'Indirizzo ritiro',
        paid_amount: 'Importo pagato',
        refund_amount: 'Importo rimborsato',
        cancellation_reason: 'Motivo annullamento',
        failure_reason: 'Motivo mancata consegna',
        amount_due: 'Importo da pagare',

        customerName: 'Nome cliente',
        branchName: 'Nome sede',
        bookingId: 'ID prenotazione',
        discountCode: 'Codice sconto',
        promoCode: 'Codice promo',
        bookingDate: 'Data prenotazione',
        bookingTime: 'Ora prenotazione',
        bookingUpdateLink: 'Link modifica prenotazione',
        branchCode: 'Codice sede',
        guestCount: 'Numero ospiti',
        childrenCount: 'Numero bambini',
        creditCardRequestUrl: 'URL richiesta carta di credito',
        depositRequestLink: 'Link richiesta deposito',
        depositAmount: 'Importo deposito',
        expectedLeaveTime: 'Ora di uscita prevista',
        qrCodeImageUrl: 'URL immagine codice QR',
      },
      buttonsTitle: 'Pulsanti',
      buttonType: 'Tipo di pulsante',
      buttonText: 'Testo del pulsante',
      buttonUrl: 'URL del pulsante',
      quickReply: 'Risposta rapida',
      ctaUrl: 'CTA URL',
      addQuickReply: 'Risposta rapida',
      addCta: 'CTA',
      removeButton: 'Rimuovi',
      buttonsHint: 'Puoi aggiungere fino a 3 pulsanti per template.',
      submit: 'Invia',
      previewTitle: 'Anteprima WhatsApp',
      previewEmpty: 'L’anteprima del template apparirà qui.',
      validation: {
        categoryRequired: 'Seleziona una categoria.',
        nameMin: 'Il nome deve avere almeno 3 caratteri.',
        nameMax: 'Il nome può avere al massimo 60 caratteri.',
        nameFormat: 'Usa solo lettere minuscole, numeri e underscore.',
        languageRequired: 'Seleziona una lingua.',
        bodyRequired: 'Il corpo è obbligatorio.',
        bodyMax: 'Il corpo può avere al massimo 1024 caratteri.',
        mediaRequired:
          'Il media è obbligatorio per i template immagine e video.',
        invalidImageType: 'L’immagine deve essere JPEG o PNG.',
        invalidVideoType: 'Il video deve essere MP4.',
        imageTooLarge: 'L’immagine deve essere di 5 MB o meno.',
        videoTooLarge: 'Il video deve essere di 16 MB o meno.',
        videoDurationMax: 'Il video deve durare 60 secondi o meno.',
        videoDurationUnreadable: 'Impossibile leggere la durata del video.',
        buttonLabelRequired: 'Il testo del pulsante è obbligatorio.',
        buttonUrlRequired: 'I pulsanti CTA richiedono un URL.',
        qrPlaceholder:
          'Il testo della risposta rapida non può contenere variabili.',
        qrMax:
          'Il testo della risposta rapida deve avere al massimo 25 caratteri.',
        urlInvalid: 'L’URL CTA deve essere un indirizzo http(s) valido.',
      },
      toast: {
        success: 'Template creato con successo.',
        error: 'Impossibile creare il template.',
        updateSuccess: 'Template aggiornato con successo.',
        updateError: 'Impossibile aggiornare il template.',
        uploadSuccess: 'Media intestazione caricato.',
        uploadFailed: 'Impossibile caricare il media intestazione.',
      },
    },
  },
  notifications: {
    title: 'Notifiche',
    allCaughtUp: 'Tutto aggiornato',
    unread: 'notifica non letta',
    unreadPlural: 'notifiche non lette',
    markAllRead: 'Segna tutte come lette',
    noNotifications: 'Nessuna notifica',
    noNotificationsDesc: 'Sei aggiornato. Le nuove notifiche appariranno qui.',
    viewAll: 'Vedi tutte le notifiche',
  },
  bookings: {
    history: {
      title: 'Storico prenotazioni',
      subtitle: 'Prenotazioni per {{branch}}',
      subtitleDefault: 'Visualizza e gestisci lo storico prenotazioni',
      totalBookings: '{{count}} prenotazioni',
      bookingsForDateBefore: '{{count}} prenotazioni per il ',
      bookingsForDateAfter: '',
      previousDay: 'Giorno precedente',
      nextDay: 'Giorno successivo',
      calendar: {
        title: 'Seleziona data prenotazione',
        openCalendar: 'Apri calendario prenotazioni',
        previousMonth: 'Mese precedente',
        nextMonth: 'Mese successivo',
        bookings: '{{count}} prenotazioni',
        arrived: '{{count}} arrivati',
        refused: '{{count}} rifiutati',
        legendBookings: 'Prenotazioni',
        legendArrived: 'Arrivati',
        legendRefused: 'Rifiutati',
      },
      calendarConfig: {
        eyebrow: 'Calendario prenotazioni',
        title: 'Configura questo giorno',
        subtitle: 'Modifica solo la data selezionata nel calendario.',
        branchLabel: 'Filiale',
        singleDay: 'Solo questo giorno',
        updateDate: 'Aggiorna {{date}}',
        dateRange: 'Intervallo di date',
        dateRangeHint: 'Crea o sovrascrivi più giorni',
        eventLabel: 'Ricorrenza o evento',
        eventPlaceholder: 'Aggiungi una ricorrenza o un evento',
        daysSelected: '{{count}} giorno selezionato',
        daysSelectedPlural: '{{count}} giorni selezionati',
        rangePlaceholder: 'Seleziona intervallo di date',
        dateRangeRequired: 'Seleziona una data di inizio e di fine.',
        rulesTitle: 'Regole prenotazioni',
        manual: 'Manuale',
        acceptTo: 'Accetta fino a',
        acceptToValue: 'Numero',
        acceptToRequired: 'Questo campo è obbligatorio',
        acceptAll: 'Accetta tutto',
        refuseAll: 'Rifiuta tutto',
        manualHint: 'Manuale lascia decidere al normale flusso app/portale.',
        close: 'Chiudi',
        saveDay: 'Salva giorno',
        saveRange: 'Salva intervallo',
        loadError: 'Impossibile caricare la configurazione del giorno.',
        saveSuccess: 'Configurazione del giorno salvata.',
        saveError: 'Impossibile salvare la configurazione del giorno.',
      },
      exportPdf: 'Scarica PDF delle prenotazioni di oggi',
      exportPdfGeneratingTitle: 'Generazione PDF',
      exportPdfGeneratingDescription:
        'Sto preparando il file delle prenotazioni...',
      exportPdfReadyTitle: 'PDF pronto',
      exportPdfReadyDescription: 'Il file è stato generato correttamente.',
      exportPdfOpen: 'Apri PDF',
      exportPdfClose: 'Chiudi',
      exportPdfError: 'Generazione del PDF non riuscita. Riprova.',
      addBooking: 'Aggiungi prenotazione',
      shifts: {
        all: 'Tutti',
        filterLabel: 'Servizio',
        breakfast: 'Colazione',
        daily: 'Pranzo',
        happyhour: 'Happy hour',
        night: 'Cena',
      },
      searchPlaceholder:
        'Cerca cliente, email, cellulare o codice prenotazione',
      filterStatus: 'Filtra stati',
      callCustomer: 'Chiama {{name}}',
      moreActions: 'Altre azioni',
      snapshot: {
        bookings: 'Prenotazioni',
        confirmed: 'Confermato',
        arrived: 'Arrivati',
        waiting: 'In attesa',
        issues: 'No-show / rifiutati',
        configureShifts: 'Configura le regole del giorno',
        filterShift: 'Filtra {{shift}}',
        shiftSelected: '{{shift}} selezionato — clicca per rimuovere',
        shiftUnavailable: '{{shift}} non è configurato per questo locale',
      },
      itemsPerPage: 'Elementi per pagina',
      range: '{{start}} - {{end}} di {{total}}',
      date: 'Data',
      time: 'Orario',
      timeAndGuests: 'Orario e ospiti',
      customer: 'Cliente',
      guests: 'Ospiti',
      guestCount: '{{count}} ospiti',
      childrenCount: '+ {{count}} bambini',
      event: 'Evento',
      noEvent: 'no event',
      contacts: 'Contatti',
      details: 'Dettagli',
      openImage: 'Apri immagine',
      openPdf: 'Apri PDF della prenotazione',
      googleReservation: 'Prenotazione da Google Reservation',
      walkIn: 'Walk in',
      associatedServicesProducts: 'Servizi/prodotti associati',
      generatedBooking: 'Prenotazione generata {{score}}/100',
      extraServices: 'Servizi extra {{count}}',
      extraServicesOnly: 'Servizi extra',
      tables: 'Tavoli assegnati',
      noTables: 'Nessun tavolo assegnato',
      notes: 'Note private',
      noNotes: 'Nessuna nota privata',
      manageBooking: 'Gestisci prenotazione',
      eventDetails: {
        title: 'Dettagli evento',
        eventName: 'Nome evento',
        eventCode: 'Codice evento',
        startDate: 'Inizio',
        maxCapacity: 'Capienza',
        eventDescription: 'Descrizione',
        eventImage: 'Immagine evento',
        formCodes: 'Moduli',
        noImage: 'Nessuna immagine',
      },
      menuDetails: {
        title: 'Dettagli menu',
        menuName: 'Nome menu',
        peopleCount: 'Numero persone',
      },
      viewBookingImage: 'Visualizza immagine prenotazione',
      bookingImage: 'Immagine prenotazione',
      users: 'users',
      status: 'Stato',
      processedBy: 'Gestito da',
      source: 'Origine',
      noBranch: 'Seleziona una filiale per vedere lo storico',
      empty: 'Nessuna prenotazione trovata',
      emptyDesc: 'Non ci sono prenotazioni per il giorno {{date}}.',
      updateStatus: 'Aggiorna lo stato della prenotazione',
      confirmOperationTitle: "Confermare l'operazione?",
      confirmStatusChange:
        'Aggiornare lo stato della prenotazione a "{{status}}"?',
      notifyCustomerTitle: 'Notificare il cliente?',
      notifyCustomerMessage:
        'Vuoi notificare il cliente delle modifiche effettuate?',
      statusUpdateSuccess: 'Stato della prenotazione aggiornato con successo.',
      statusUpdateError:
        'Impossibile aggiornare lo stato della prenotazione. Riprova.',
      updatingStatus: 'Aggiornamento stato...',
      associatedServices: {
        title: 'Servizi/prodotti associati',
        reference: 'Prenotazione {{code}}',
        quantity: '×{{count}}',
        empty: 'Nessun servizio associato a questa prenotazione.',
        paid: 'Pagato',
        unpaid: 'Non pagato',
      },
      messageHistory: {
        open: 'Storico messaggi',
        communicationError: 'Invio messaggio non riuscito',
        title: 'Storico messaggi',
        reference: 'Riferimento {{code}}',
        refresh: 'Aggiorna',
        empty: 'Nessun messaggio inviato per questa prenotazione.',
        loadError: 'Impossibile caricare lo storico messaggi.',
        endpoint: 'Endpoint',
        eventCode: 'Codice evento',
        payload: 'Payload',
        response: 'Risposta',
        statusProcessed: 'Inviato',
        statusPending: 'In attesa',
        messageText: 'Messaggio',
        messageTextUnavailable: 'Testo del messaggio non disponibile.',
        httpStatus: 'HTTP {{status}}',
        attempts: '{{count}} tentativi',
        resubmit: 'Reinvia messaggio',
        resubmitSuccess: 'Messaggio reinviato con successo.',
        resubmitError: 'Impossibile reinviare il messaggio. Riprova.',
        resubmitConfirmTitle: 'Reinviare il messaggio?',
        resubmitConfirmMessage:
          'Sei sicuro di voler reinviare questo messaggio?',
      },
      statusActions: {
        confirm: 'Conferma',
        refuse: 'Rifiuta',
        eliminate: 'Elimina',
        arrived: 'Arrivato',
        notArrived: 'Non arrivato',
        notifyUser: 'Notifica utente per recarsi in locale',
        bookingAlreadyProcessed: 'Prenotazione gia processata',
        moveToConfirm: 'Sposta in confermato',
      },
      statuses: {
        IN_ATTESA: 'In attesa',
        MODIFICATO_DA_UTENTE: 'Modificato da utente',
        ELIMINATO_DA_UTENTE: 'Eliminato da utente',
        MODIFICA_CONFERMATA: 'Modifica confermata',
        MODIFICA_RIFIUTATA: 'Modifica rifiutata',
        PRE_CONFERMA_PAGAMENTO: 'Pre-conferma pagamento',
        CONFERMATO: 'Confermato',
        AVVISATO_LISTA_ATTESA: 'Avvisato lista attesa',
        ARRIVATO: 'Arrivato',
        NON_ARRIVATO: 'Non arrivato',
        RIFIUTATO: 'Rifiutato',
        LISTA_ATTESA: 'Lista attesa',
        ELIMINATO: 'Eliminato',
      },
    },
    payment: {
      column: 'Pagamenti',
      close: 'Chiudi',
      connectRequired:
        'Stripe Connect non è pronto per questa struttura. Completa l’onboarding per gestire i pagamenti.',
      loadError:
        'Impossibile recuperare le operazioni di pagamento della prenotazione.',
      linkCopied: 'Link di pagamento copiato.',
      linkCopyError: 'Impossibile copiare il link di pagamento.',
      linkReadyTitle: 'Link deposito pronto',
      linkReadyDescription:
        'Il link è pronto per essere inviato attraverso i tuoi canali.',
      badge: {
        title: 'Pagamenti',
        cardPresent: 'Carta presente',
        cardMissing: 'Carta mancante',
        paidPendingOps: 'Pagato · operazioni in attesa',
        manage: 'Gestisci',
        noCard: 'Nessuna carta associata',
        verifiedSuccess: 'Verificata con successo',
        depositAuthorizedCapture: 'Deposito autorizzato · Incassa',
        depositCaptured: 'Deposito incassato',
        depositRefunded: 'Deposito rimborsato',
        depositPartiallyRefunded: 'Deposito rimborsato parzialmente',
        depositPrefix: 'Deposito',
      },
      tooltip: {
        cardPresent: 'Apri i dati carta salvati direttamente dalla struttura',
        cardMissing: 'Invia al cliente la richiesta dei dati carta',
        openOperations:
          'Apri il deposito o le operazioni di pagamento della prenotazione',
        openVerified: 'Apri i dettagli di pagamento e della carta verificata',
        noCard: 'Nessuna carta associata. Apri per gestire i pagamenti',
      },
      status: {
        notRequired: 'Non richiesto',
        pending: 'In attesa del pagamento',
        awaitingConfirmation: 'Pagamento inviato, conferma Stripe in attesa',
        cardVerified: 'Carta verificata',
        authorized: 'Non incassato',
        paid: 'Pagato',
        partiallyPaid: 'Parziale',
        failed: 'Fallito',
        canceled: 'Annullato',
        refundPending: 'Rimborso in corso',
        partiallyRefunded: 'Rimborso parziale',
        refunded: 'Rimborsato',
        refundFailed: 'Rimborso fallito',
      },
      actions: {
        copyLink: 'Copia link',
        refund: 'Rimborsa',
      },
      modal: {
        title: 'Depositi',
        description:
          'Controlla le preautorizzazioni, genera un link e decidi se incassare o rilasciare il deposito.',
        bookingLabel: 'Prenotazione {{code}}',
        pendingWarning:
          'Uno o più pagamenti sono completati, ma ci sono ancora operazioni da eseguire o link in attesa.',
        requested: 'Richiesto',
        captured: 'Incassato',
        authorized: 'Autorizzato',
        netCaptured: 'Incassato netto',
        refunded: 'Rimborsato',
        descriptionLabel: 'Descrizione',
        operationsTab: 'Operazioni ({{count}})',
        newRequestTab: 'Nuova richiesta',
        historyTitle: 'Storico operazioni',
        historyDescription:
          'Qui trovi i depositi e le autorizzazioni collegati alla prenotazione.',
        emptyTitle: 'Nessuna operazione presente',
        emptyDescription:
          'Apri la scheda “Nuova richiesta” per generare il primo link.',
        createRequest: 'Crea richiesta',
      },
      create: {
        title: 'Crea un link deposito',
        description:
          'Il cliente autorizza l’importo; in seguito potrai incassarlo oppure rilasciarlo.',
        amountLabel: 'Importo deposito (€)',
        descriptionLabel: 'Descrizione facoltativa',
        descriptionPlaceholder: 'Esempio: deposito per la prenotazione',
        authorizeOnlyHint:
          'L’importo viene soltanto autorizzato. Potrai decidere in seguito se incassarlo o rilasciarlo.',
        submit: 'Genera link deposito',
        invalidAmount: 'Inserisci un importo maggiore di zero.',
        error: 'Impossibile creare il link Stripe.',
        success: 'Link deposito creato.',
        sent: 'Link deposito creato e inviato.',
        linkCreatedMessageFailed:
          'Link creato, ma il messaggio DR non è stato inviato.',
      },
      refund: {
        title: 'Rimborsa pagamento',
        maxAmount: 'Importo massimo rimborsabile: {{amount}}',
        amountLabel: 'Importo (€)',
        reasonLabel: 'Motivo',
        invalidAmount:
          'Inserisci un importo valido entro il massimo rimborsabile.',
        started: 'Rimborso avviato.',
        completed: 'Rimborso completato.',
        error: 'Rimborso non riuscito. Controlla lo stato Stripe e riprova.',
        reasons: {
          requested_by_customer: 'Richiesto dal cliente',
          duplicate: 'Pagamento duplicato',
          fraudulent: 'Operazione fraudolenta',
        },
      },
    },
    create: {
      title: 'Crea prenotazione',
      cancel: 'Annulla',
      submit: 'Crea prenotazione',
      customerDetails: 'Dettagli cliente',
      bookingDetails: 'Dettagli prenotazione',
      firstName: 'Nome *',
      firstNamePlaceholder: 'Inserisci il nome',
      lastName: 'Cognome *',
      lastNamePlaceholder: 'Inserisci il cognome',
      email: 'Email',
      emailPlaceholder: 'Inserisci l’email',
      phone: 'Numero di telefono *',
      phonePlaceholder: 'Inserisci il numero di telefono',
      nickName: 'Nickname',
      nickNamePlaceholder: 'Inserisci il nickname',
      bookingImage: 'Immagine prenotazione',
      pdfDocument: 'Documento PDF',
      bookingTime: 'Orario prenotazione *',
      bookingTimePlaceholder: 'Seleziona orario',
      guests: 'Ospiti *',
      guestsPlaceholder: 'Inserisci ospiti',
      children: 'Bambini *',
      childrenPlaceholder: 'Inserisci bambini',
      customerRequests: 'Richieste del cliente',
      customerRequestsPlaceholder: 'Inserisci le richieste del cliente',
      restaurantNotes: 'Note del ristorante',
      restaurantNotesPlaceholder: 'Inserisci le note del ristorante',
    },
    calendar: {
      selected: 'Selezionato: {{date}}',
      dotsHint: 'I punti indicano le prenotazioni disponibili',
    },
  },
  forms: {
    title: 'Moduli di prenotazione',
    subtitle: 'Gestisci tutti i moduli attivi, sospesi o archiviati',
    addDataCollectionForm: 'Aggiungi modulo',
    searchPlaceholder: 'Cerca moduli per nome',
    noDataTitle: 'Nessun modulo',
    noDataDesc:
      'Non hai ancora creato alcun modulo. Inizia creando il tuo primo modulo per raccogliere prenotazioni e interazioni.',
    status: 'Stato',
    active: 'Attivo',
    suspended: 'Sospeso',
    url: 'URL',
    qrCode: 'Codice QR',
    preview: 'Anteprima',
    googleBooking: 'Google Booking',
    googleReservation: 'Google Reservation',
    copyUrl: 'Copia URL',
    downloadQr: 'Scarica QR',
    editForm: 'Modifica modulo',
    dublicateUrl: 'Duplica modulo',
    statusActive: 'Attivo',
    statusInactive: 'Inattivo',
    statusDraft: 'Bozza',
    statsCount: '{{count}} moduli',
    updatedAgo: 'Aggiornato 2 minuti fa',
    deleteForm: 'Elimina modulo',
    share: {
      label: 'Condividi modulo',
      whatsapp: 'WhatsApp',
      instagram: 'Instagram',
      twitter: 'Twitter / X',
      facebook: 'Facebook',
      defaultText: 'Prenota con noi',
      instagramCopied: 'Link copiato. Incollalo su Instagram per condividere.',
      instagramOpened:
        'Instagram aperto. Link del modulo copiato — incollalo per condividere.',
      copyFailed: 'Impossibile copiare il link. Riprova.',
    },
    createTitle: 'Crea modulo',
    formName: 'Nome modulo',
    formNamePlaceholder: 'Inserisci il nome del modulo',
    createForm: 'Crea modulo',
    toast: {
      createSuccess: 'Modulo creato con successo.',
      createError: 'Impossibile creare il modulo. Riprova.',
      deleteSuccess: 'Modulo eliminato con successo.',
      deleteError: 'Impossibile eliminare il modulo. Riprova.',
      duplicateSuccess: 'Modulo duplicato con successo.',
      duplicateError: 'Impossibile duplicare il modulo. Riprova.',
      statusActivateSuccess: 'Modulo attivato con successo.',
      statusSuspendSuccess: 'Modulo sospeso con successo.',
      statusActivateError: 'Impossibile attivare il modulo. Riprova.',
      statusSuspendError: 'Impossibile sospendere il modulo. Riprova.',
      googleReservationEnabled: '{{name}} Google Reservation abilitato',
      googleReservationDisabled: '{{name}} Google Reservation disabilitato',
      googleReservationError:
        'Impossibile aggiornare Google Reservation. Riprova.',
      logoUploadSuccess: 'Logo caricato con successo.',
      logoUploadError: 'Impossibile caricare il logo.',
      logoRemoveSuccess: 'Logo rimosso con successo.',
      logoRemoveError: 'Impossibile rimuovere il logo.',
      bannerUploadSuccess: 'Banner caricato con successo.',
      bannerUploadError: 'Impossibile caricare il banner.',
      bannerRemoveSuccess: 'Banner rimosso con successo.',
      bannerRemoveError: 'Impossibile rimuovere il banner.',
    },
    deleteModel: {
      deleteTitle: 'Eliminare il modulo?',
      deleteDescription:
        'Sei sicuro di voler eliminare questo modulo? Questa azione non può essere annullata.',
    },
    validation: {
      formNameMin: 'Il nome deve contenere almeno 2 caratteri',
      formNameMax: 'Il nome non può superare i 50 caratteri',
    },
    scanQrAccess: 'Scansiona il codice QR per accedere:',
    qrClose: 'Chiudi',
    qrDownload: 'Scarica',
    edit: {
      pageTitle: 'Consenso dati personali',
      editTitle: 'Modifica {{name}}',
      subtitle: 'Configura il modulo di prenotazione',
      saveChanges: 'Salva modifiche',
      close: 'Chiudi',
      toast: {
        saveSuccess: 'Modulo salvato con successo.',
        saveError: 'Impossibile salvare il modulo.',
      },
      tabListLabel: 'Sezioni modifica modulo',
      uploading: 'Caricamento...',
      removing: 'Rimozione...',
      tabs: {
        general: 'Generale',
        capacity: 'Capacità',
        schedule: 'Orari',
        formFields: 'Campi modulo',
        preferences: 'Preferenze',
        services: 'Servizi',
      },
      general: {
        media: 'Media',
        logo: 'Logo',
        banner: 'Immagine banner',
        bannerHint: 'Immagine banner — trascina o sfoglia',
        basicDetails: 'Dettagli base',
        formName: 'Nome modulo',
        customerName: 'Emoticon Form',
        address: 'Indirizzo locale da mostrare nel modulo',
        customThankYouPage: 'Pagina di ringraziamento personalizzata',
        buttonTextOnBookingForm:
          'Testo del pulsante nel modulo di prenotazione',
        policy: 'Policy',
        webLink: 'Link web',
      },
      capacity: {
        hint: 'Limiti di prenotazione e regole tavolo per questo modulo.',
        sections: {
          coverLimits: 'Limiti coperti',
          timeSettings: 'Impostazioni fascia oraria',
          policies: 'Policy',
        },
        maxGuests: 'Coperti massimi prima della chiamata',
        maxGuestsHint:
          'Qual è il numero massimo di coperti che il tuo locale accetta prima di richiedere una chiamata?',
        maxTableGuests: 'Ospiti max per tavolo',
        maxTableGuestsHint:
          'Imposta il numero massimo di ospiti che puoi accettare a un singolo tavolo. Se lasci il valore a 0, il massimo coinciderà con la capienza totale del locale.',
        minGuests: 'Coperti min per tavolo',
        minGuestsHint: 'Dimensione minima del gruppo per un singolo tavolo.',
        phoneOverbooking: 'Cellulare Overbooking',
        phoneOverbookingHint:
          'Telefono mostrato quando gli ospiti superano il max per tavolo. Formato: PREFISSO + NUMERO (es. +39 3444393343).',
        cut: 'Taglio slot',
        cutHint:
          'Suddivide la disponibilità in slot di prenotazione di questa durata.',
        cutSlotOption: 'Dividi le fasce orarie in slot da {{minutes}} minuti',
        stayOnTable: 'Permanenza al tavolo',
        stayOnTableHint: 'Durata prevista della permanenza per prenotazione.',
        minutes: '{{count}} min',
        dogsAccepted: 'Cani ammessi',
        dogsAcceptedHint:
          'Consenti ai clienti di indicare che portano un cane.',
        menuSelectionMandatory: 'Selezione menu obbligatoria',
        menuSelectionMandatoryHint:
          'Se attivo, l’ospite deve scegliere un menu (o “Nessun menu”) per ogni persona in modo che il totale corrisponda a ospiti + bambini per continuare.',
        showRestaurantNoMenu: 'Mostra card ristorante senza menu',
        showRestaurantNoMenuHint:
          'Se attivo, il form pubblico mostra una card che spiega che il ristorante non ha un menu fisso.',
      },
      schedule: {
        shiftOverview: 'Panoramica turni',
        weeklyHours: 'Orari settimanali',
        addTimeSlot: 'Aggiungi fascia oraria',
        day: 'Giorno',
        open: 'Aperto',
        closed: 'Chiuso',
        hours: 'Orari',
        editSlot: 'Modifica fascia',
        deleteSlot: 'Elimina fascia',
        deleteSlotConfirmTitle: 'Eliminare questa fascia oraria?',
        deleteSlotConfirmDesc:
          'La fascia selezionata verrà rimossa da questo giorno.',
        deleteSlotConfirmMultiDay:
          'Questo servizio usa lo stesso codice su {{count}} giorni. Eliminandolo verrà rimosso da tutti quei giorni.',
        extraordinarySection: 'Aperture / chiusure straordinarie',
        addException: 'Aggiungi eccezione',
        dateRange: 'Intervallo date',
        type: 'Tipo',
        closedOpenDay: 'Chiuso/Aperto',
        timeSlots: 'Fasce orarie',
        cuttingMin: 'Taglio (min)',
        maxGuests: 'Coperti max',
        noShifts: 'Nessun turno configurato.',
        emptyWeeklyHours:
          'Nessun orario settimanale. Usa + per creare la configurazione per tutti i giorni.',
        emptyExtraordinary:
          'Nessuna apertura o chiusura straordinaria configurata.',
        createForAllDays: 'Crea configurazione generale per tutti i giorni',
        resetConfiguration: 'Reimposta configurazione',
        saveConfiguration: 'Salva configurazione',
        resetConfirmTitle: 'Eliminare tutte le configurazioni esistenti?',
        resetConfirmDesc:
          'Questa azione rimuoverà tutte le impostazioni orarie di questo modulo.',
        resetProceed: 'Procedi',
        tags: 'Tag',
        tagsPlaceholder: 'Inserisci i tag separati da virgola',
        tagsPickerDesc: 'Seleziona i tag per questa fascia oraria.',
        tagsPickerEmpty: 'Nessun tag disponibile per questa sede.',
        allowOverbook: 'Consenti overbooking',
        alertMessage: 'Messaggio di avviso',
        alertMessagePlaceholder:
          'Inserisci un messaggio di avviso per questa fascia',
        enableOverbookBlocking: 'Abilita blocco overbooking',
        disableOverbookBlocking: 'Disabilita blocco overbooking',
        deposit: {
          tooltipInactive: 'Configura deposito / verifica carta',
          tooltipValidation: 'Stripe: solo verifica della carta',
          tooltipDeposit: 'Deposito Stripe: {{amount}} per ospite',
          tooltipMixed: 'Configurazione Stripe mista tra le fasce',
          allStandardBands: 'Tutte le fasce standard',
          allExtraordinaryBands: 'Tutte le fasce straordinarie',
          singleBand: 'Fascia oraria selezionata',
          depositTitle: 'Deposito',
          checkCardTitle: 'Verifica carta',
          bulkHint:
            'La configurazione verrà applicata in un’unica operazione a tutte le fasce di questa sezione.',
          requestTitle: 'Richiedi una carta di credito',
          requestDesc:
            'Puoi richiedere solo la verifica della carta oppure anche un deposito preautorizzato.',
          validationTitle: 'Validazione carta',
          validationDesc:
            'Verifica e collega la carta senza bloccare un importo.',
          depositOptionTitle: 'Deposito',
          depositOptionDesc:
            'Valida la carta e preautorizza l’importo configurato.',
          amountLabel: 'Importo EUR',
          amountHint:
            'L’importo è a persona: sarà richiesta una preautorizzazione di questo importo per ciascun ospite.',
          saveConfiguration: 'Salva configurazione',
          close: 'Chiudi',
          toastSaveSuccess: 'Configurazione deposito salvata.',
          toastSaveError: 'Salvataggio configurazione deposito non riuscito.',
        },
        legacyCreditCard: {
          enable: 'Abilita la richiesta diretta dei dati carta',
          disable: 'Disabilita la richiesta diretta dei dati carta',
        },
        maxGuestsPerSlot: 'Copertura massima per fascia oraria',
        customizeCutter:
          'Personalizza il taglio della fascia rispetto alla configurazione standard',
        noSlotsForDay:
          'Nessuna fascia oraria per questo giorno. Usa + per aggiungerne una.',
        slotClosedOnDate: 'Fascia non disponibile il {{date}}',
        alertModalTitle:
          'Avviso per il cliente in caso di selezione della fascia oraria',
        maxGuestsModalTitle:
          'Imposta il numero massimo di posti per questa fascia {{range}}',
        maxGuestsModalDesc:
          'Scegli il numero massimo di ospiti consentiti per questa fascia oraria.',
        cutterModalTitle: 'Aggiorna taglio fascia oraria',
        days: {
          monday: 'Lunedì',
          tuesday: 'Martedì',
          wednesday: 'Mercoledì',
          thursday: 'Giovedì',
          friday: 'Venerdì',
          saturday: 'Sabato',
          sunday: 'Domenica',
        },
        addTimeAllDays: {
          title: 'Aggiungi orario a tutti i giorni',
          subtitle:
            'Gli orari disponibili rispettano le fasce del locale e quelle già configurate.',
          chosenTime: 'Orario scelto',
          duration: 'Durata: {{duration}}',
          startAt: '1. Inizia alle',
          endsAt: '2. Termina alle',
          allOkDesc: 'Puoi salvare questo orario per tutti i giorni.',
          saveOnAllDays: 'Salva su tutti i giorni',
        },
        slotTime: {
          title: 'Fascia oraria',
          subtitle: 'Prima scegli quando inizia. Poi scegli quando termina.',
          allOk: 'Tutto ok',
          allOkDesc: 'Puoi salvare questo orario.',
          saveTime: 'Salva orario',
          timeRangeInvalid:
            "L'orario di fine deve essere successivo a quello di inizio.",
          timeRangeConflict:
            'Questo intervallo orario è in conflitto con un altro. Modifica o elimina uno degli intervalli.',
          occupiedTitle: 'Orari già occupati',
          occupiedHint: 'La seconda lista evita questi orari in automatico.',
          cannotSave: 'Cambia orario',
          cannotSaveDesc: 'Non c’è una chiusura valida per questo inizio.',
          cannotSaveButton: 'Non posso salvare',
          durationMinutes: '{{count}} minuti',
          durationHours: '{{count}} ore',
          durationHoursMinutes: '{{hours}} ore e {{minutes}} minuti',
          selectService: 'Seleziona un servizio',
          selectServiceDesc:
            'Scegli una fascia oraria per aggiungere i suoi orari di apertura.',
          noLocalSlots:
            'Gli orari sono stati eliminati nella configurazione locale. Creali di nuovo lì, poi aggiungili a questo calendario.',
          selectedCount: '{{count}} servizi selezionati',
          alreadyAdded: 'Già aggiunto',
        },
        extraordinary: {
          badge: 'Configurazione straordinaria',
          createRange: 'Crea intervallo',
          editRange: 'Modifica intervallo',
          description: 'Descrizione',
          descriptionPlaceholder: 'Es. Ferragosto, evento privato...',
          rangeDate: 'Intervallo date',
          rangeDatePlaceholder: 'Seleziona intervallo date',
          clearDates: 'Cancella',
          applyDates: 'Applica',
          open: 'Aperto',
          create: 'Crea',
          update: 'Aggiorna',
          validation: 'Descrizione e intervallo date sono obbligatori.',
          dateRequired: 'Seleziona un intervallo di date.',
          dateOrder: 'La data di fine non può precedere quella di inizio.',
          timeRangeInvalid:
            "L'orario di fine deve essere successivo a quello di inizio.",
          addConfiguration: 'Aggiungi configurazione straordinaria',
          saveConfiguration: 'Salva configurazione',
          addTimeSlot: 'Aggiungi fascia oraria',
          deleteRange: 'Elimina intervallo',
          deleteConfirmTitle: "Eliminare l'intervallo straordinario?",
          deleteConfirmDesc:
            'Questa azione rimuoverà definitivamente questa configurazione di date.',
          toast: {
            createSuccess: 'Intervallo straordinario creato.',
            updateSuccess: 'Intervallo straordinario aggiornato.',
            saveError: "Impossibile salvare l'intervallo straordinario.",
            deleteSuccess: 'Intervallo straordinario eliminato.',
            deleteError: "Impossibile eliminare l'intervallo straordinario.",
          },
        },
        toast: {
          saveSuccess: 'Configurazione orari salvata.',
          saveError: 'Impossibile salvare la configurazione orari.',
          resetSuccess: 'Configurazione orari reimpostata.',
          resetError: 'Impossibile reimpostare la configurazione.',
          addSlotSuccess: 'Fascia oraria aggiunta.',
          addSlotError: 'Impossibile aggiungere la fascia oraria.',
          deleteSlotSuccess: 'Fascia oraria eliminata.',
          deleteSlotError: 'Impossibile eliminare la fascia oraria.',
          updateTimeSuccess: 'Fascia oraria aggiornata.',
          updateTimeError: 'Impossibile aggiornare la fascia oraria.',
          dayToggleSuccess: 'Stato del giorno aggiornato.',
          dayToggleError: 'Impossibile aggiornare lo stato del giorno.',
          configurationMissing:
            'Configurazione del giorno non ancora disponibile.',
          slotNotReady: 'Fascia oraria non pronta. Aggiorna e riprova.',
          overbookToggleSuccess: 'Impostazione overbooking aggiornata.',
          overbookToggleError:
            "Impossibile aggiornare l'impostazione overbooking.",
          legacyCreditCardToggleSuccess: 'Richiesta dati carta aggiornata.',
          legacyCreditCardToggleError: 'Errore durante la modifica.',
          tagsSaveSuccess: 'Tag salvati.',
          tagsLoadError: 'Impossibile caricare i tag.',
          tagsSaveError: 'Impossibile salvare i tag.',
          tagsDeleteSuccess: 'Tag rimossi.',
          alertSaveSuccess: 'Messaggio di avviso salvato.',
          alertSaveError: 'Impossibile salvare il messaggio di avviso.',
          alertDeleteSuccess: 'Messaggio di avviso rimosso.',
          maxGuestsInvalid: 'Inserisci un numero valido di ospiti.',
          maxGuestsSaveSuccess: 'Numero massimo ospiti aggiornato.',
          maxGuestsSaveError:
            'Impossibile aggiornare il numero massimo ospiti.',
          cutterSaveSuccess: 'Taglio fascia oraria aggiornato.',
          cutterSaveError: 'Impossibile aggiornare il taglio fascia oraria.',
        },
      },
      formFields: {
        hint: 'Scegli quali campi mostrare nel modulo di prenotazione pubblico.',
        configure: 'Configura campi modulo',
        configureDesc:
          'Attiva i campi visibili al cliente durante la prenotazione.',
        name: 'Nome',
        surname: 'Cognome',
        phone: 'Telefono',
        email: 'Email',
        dateOfBirth: 'Data di nascita',
        zipCode: 'CAP (Codice postale)',
        customerNotes: 'Note cliente',
        field: 'Campo',
        mandatory: 'Obbligatorio',
        nonMandatory: 'Non obbligatorio',
      },
      preferences: {
        sectionTitle: 'Opzioni aggiuntive',
        sectionDescription:
          'Configurazioni extra, consenti ai tuoi clienti di comunicare preferenze, allergie o intolleranze più facilmente.',
        singleChoicePredefined:
          "Compila l'elenco come suggerito per creare opzioni di preferenza a scelta singola.",
        singleChoiceAdditional:
          "Crea un'opzione di preferenza aggiuntiva a scelta singola",
        multiChoicePredefined:
          "Compila l'elenco come suggerito per creare opzioni di preferenza a scelta multipla.",
        multiChoiceAdditional:
          "Crea un'opzione di preferenza aggiuntiva a scelta multipla",
        singleChoiceOptionsPlaceholder:
          'Esempi: Sala esterna | Sala interna | Bar',
        multiChoiceOptionsPlaceholder: 'Es: Lattosio | Celiaci | Arachidi',
        singleChoiceFieldPlaceholder: 'Es: Nome campo scelta singola',
        multiChoiceFieldPlaceholder: 'Es: Nome campo scelta multipla',
        pipeSeparatedNote: 'Nota: separa i valori con il carattere |.',
        importRooms: 'Importa le mie sale',
        importRoomsSuccess: 'Sale importate con successo.',
        importRoomsError: 'Impossibile importare le sale.',
        importRoomsEmpty: 'Nessuna sala trovata per questa sede.',
        importRoomsNotFound:
          'Nessuna sala trovata per questa sede. Verifica il branch code o crea le sale.',
        suggestAllergens: 'Suggeriscimi un elenco completo di allergeni',
        validation: {
          fillRequired:
            "Se Mandatory o Non-mandatory è selezionato, compila sia l'etichetta sia le opzioni.",
          labelRequired:
            "L'etichetta è obbligatoria quando l'opzione è attiva.",
          optionsRequired:
            "Aggiungi almeno un'opzione separata da | quando l'opzione è attiva.",
        },
      },
      services: {
        sectionTitle: 'Sezione prodotti e servizi aggiuntivi',
        sectionDescription:
          'Crea un elenco di prodotti e servizi da associare al modulo di prenotazione.',
        mandatoryLabel:
          'Voglio che la scelta di prodotti o servizi sia obbligatoria',
        title: 'Prodotti e servizi aggiuntivi',
        description: 'Proponi extra durante il flusso di prenotazione.',
        add: 'Aggiungi prodotto o servizio',
        name: 'Nome',
        descriptionCol: 'Descrizione',
        price: 'Prezzo',
        pax: 'Pax',
        empty: 'Nessun prodotto o servizio collegato a questo modulo.',
        pickerTitle: 'Elenco servizi/prodotti',
        pickerDesc:
          'Seleziona uno o più elementi da associare a questo modulo.',
        pickerEmpty: 'Nessun servizio o prodotto disponibile per questa sede.',
        deleteTitle: 'Rimuovere servizio/prodotto?',
        deleteDesc:
          'Questo rimuoverà l elemento selezionato da questo modulo di prenotazione.',
        toast: {
          saveSuccess: 'Servizi e prodotti aggiornati.',
          saveError: 'Impossibile aggiornare servizi e prodotti.',
          deleteSuccess: 'Servizio/prodotto rimosso.',
          deleteError: 'Impossibile rimuovere servizio/prodotto.',
          loadError: 'Impossibile caricare servizi e prodotti.',
        },
      },
      formConfiguration: 'Configurazione modulo',
      formConfigurationDesc: 'Configurazioni generali del modulo',
      formImage: 'Immagine modulo',
      formImageHint:
        "Consigliamo un logo PNG con sfondo trasparente. Doppio clic sull'immagine per il reindirizzamento.",
      formBackgroundImage: 'Immagine di sfondo del modulo',
      imageUpload: 'Carica immagine',
      removeImage: 'Rimuovi immagine',
      redirectAfterCompilation: 'Reindirizzamento dopo la compilazione',
      linkForm: 'Link modulo',
      buttonText: 'Testo pulsante',
      dataFields: 'Campi dati',
      requestEmail: 'Richiedi email',
      requestAddress: 'Richiedi indirizzo',
      requestNationality: 'Richiedi nazionalità',
      requestZipCode: 'Richiedi CAP',
      requestDateOfBirth: 'Richiedi data di nascita',
      tagsApplied: 'Tag applicati',
      tagsPlaceholder: 'Seleziona tag',
      tagsHint: 'Nomi tag separati da virgola',
      validation: {
        buttonTextRequired: 'Il testo del pulsante è obbligatorio',
      },
    },
  },
  customers: {
    title: 'I miei clienti',
    subtitle: 'Clienti di {{branch}}',
    subtitleDefault: "Visualizza e gestisci l'elenco clienti",
    statsCount: '{{count}} clienti',
    searchPlaceholder: 'Cerca nome, email o telefono',
    customer: 'Cliente',
    surname: 'Cognome',
    name: 'Nome',
    email: 'Email',
    phone: 'Telefono',
    contacts: 'Contatti',
    dateOfBirth: 'Data di nascita',
    reservations: 'Prenotazioni',
    noShow: 'No Show',
    tags: 'Tag',
    visits: 'Presenze',
    presenceCount: '{{count}} presenze',
    noShowCount: '{{count}} No show',
    registrationDate: 'Registrato',
    itemsPerPage: 'Elementi per pagina',
    range: '{{start}} - {{end}} di {{total}}',
    noBranch: 'Seleziona una filiale per visualizzare i clienti',
    empty: 'Nessun cliente trovato',
    emptyDesc: 'Non ci sono ancora clienti per questa filiale.',
    downloadPdf: 'Scarica pdf',
    downloadPdfSuccess: 'PDF storico prenotazioni scaricato.',
    downloadPdfError:
      'Impossibile scaricare il PDF dello storico prenotazioni.',
    downloadPdfEmpty: 'Nessuna prenotazione trovata per questo cliente.',
    downloadPdfMissingPhone: 'Questo cliente non ha un numero di telefono.',
    importExport: {
      menuLabel: 'Importa/esporta',
      import: 'Importa elenco clienti',
      export: 'Esporta elenco clienti',
      downloadSample: 'Scarica elenco di esempio',
      downloadSampleSuccess: 'File di esempio scaricato.',
      downloadSampleError: 'Impossibile scaricare il file di esempio.',
      exportSuccess: 'Elenco clienti esportato.',
      exportError: "Impossibile esportare l'elenco clienti.",
      dialogTitle: 'Importa elenco clienti',
      dialogDescription:
        'Seleziona un file CSV o XLSX per importare i clienti.',
      chooseFile: 'Scegli file',
      selectedFile: 'File selezionato',
      upload: 'Carica',
      uploading: "Caricamento dell'elenco clienti...",
      cancel: 'Annulla',
      close: 'Chiudi',
      invalidType: 'Seleziona un file CSV o XLSX.',
      importError: "Impossibile importare l'elenco clienti. Riprova.",
      importAnother: 'Importa un altro file',
      totalRecords: 'Record totali',
      successfulImports: 'Importazioni riuscite',
      failedImports: 'Importazioni non riuscite',
    },
    create: {
      title: 'Crea cliente',
      action: 'Cliente',
      tooltip: 'Aggiungi cliente',
      submit: 'Crea cliente',
      cancel: 'Annulla',
      firstName: 'Nome',
      firstNamePlaceholder: 'Inserisci il nome',
      lastName: 'Cognome',
      lastNamePlaceholder: 'Inserisci il cognome',
      mobile: 'Numero di cellulare',
      mobilePlaceholder: 'Inserisci il numero di cellulare',
      emailPlaceholder: "Inserisci l'indirizzo email",
      nickname: 'Nickname cliente',
      nicknamePlaceholder: 'Inserisci il nickname',
      tagsTitle: 'Tag cliente',
      tagsSubtitle: 'Seleziona i tag associati al cliente.',
      tagsPlaceholder: 'Seleziona tag',
      success: 'Cliente creato con successo.',
      error: 'Impossibile creare il cliente. Riprova.',
      loading: 'Creazione cliente in corso...',
    },
    bookingHistoryPdf: {
      title: 'Storico prenotazioni',
      generatedMeta: 'Generato il {{date}} · {{count}} prenotazioni',
      customerCode: 'Codice cliente: {{code}}',
      phone: 'Telefono',
      email: 'Email',
      birthDate: 'Data di nascita',
      address: 'Indirizzo',
      registration: 'Registrazione',
      tags: 'Tag',
      notAvailable: 'Non disponibile',
      guestsWithChildren: '{{guests}} + {{children}} bambini',
      columns: {
        date: 'Data',
        time: 'Ora',
        code: 'Codice',
        status: 'Stato',
        guests: 'Ospiti',
        source: 'Fonte',
      },
      sources: {
        WEB: 'Web',
        APP_CUSTOMER: 'Portale',
        APP: 'App',
      },
    },
    filters: {
      title: 'Filtri',
      tags: 'Tag',
      birthYears: 'Anni di nascita',
      birthMonth: 'Mese di nascita',
      clearAll: 'Cancella tutti i filtri',
      noTags: 'Nessun tag disponibile',
      onlyWithEmail: 'email configurata',
      marketingConsent: 'consenso marketing',
      blacklist: 'clienti in blacklist',
      phonePrefix: 'Prefisso paese',
      allPhonePrefixes: 'Tutti i prefissi',
      searchOptions: 'Cerca...',
      noSearchResults: 'Nessuna opzione corrispondente',
      minimumBookings: 'Numero minimo prenotazioni',
      selectMinimumBookings: 'Seleziona minimo prenotazioni',
      form: 'Modulo',
      selectForm: 'Tutti i moduli',
      noForms: 'Nessun modulo disponibile',
      bookingPeriodTitle: 'Prenotazioni nel periodo',
      bookingRangeLabel: 'Intervallo prenotazioni',
      bookingRangePlaceholder: 'Seleziona intervallo prenotazioni',
      removePeriod: 'Rimuovi periodo',
      minimumBookingsOptions: {
        '1': 'Almeno 1',
        '2': 'Almeno 2',
        '3': 'Almeno 3',
        '4': 'Almeno 4',
        '5': 'Almeno 5',
        '10': 'Almeno 10',
        '15': 'Almeno 15',
        '20': 'Almeno 20',
        '30': 'Almeno 30',
        '50': 'Almeno 50',
      },
      months: {
        '1': 'Gennaio',
        '2': 'Febbraio',
        '3': 'Marzo',
        '4': 'Aprile',
        '5': 'Maggio',
        '6': 'Giugno',
        '7': 'Luglio',
        '8': 'Agosto',
        '9': 'Settembre',
        '10': 'Ottobre',
        '11': 'Novembre',
        '12': 'Dicembre',
      },
    },
  },
  customerDetails: {
    title: 'Dettagli Cliente',
    subtitle: 'Dettagli Cliente per {{branch}}',
    subtitleDefault: 'Visualizza e gestisci i dettagli del cliente',
    deleteModelTitle: 'Eliminare il cliente?',
    deleteModelDescription:
      'Eliminando il cliente verranno rimosse anche le prenotazioni associate.',
    deleteModelConfirmText: 'Sì, elimina',
    deleteModelCancelText: 'Annulla',
    card: {
      title: 'Scheda cliente',
      close: 'Chiudi',
      delete: 'Elimina',
      update: 'Aggiorna cliente',
      name: 'Nome',
      surname: 'Cognome',
      nickname: 'Nickname',
      mobile: 'Cellulare',
      address: 'Indirizzo',
      city: 'Città',
      province: 'Provincia',
      postalCode: 'CAP',
      country: 'Paese',
      personalDataTitle: 'Dati personali',
      personalDataSubtitle: 'Nome, cognome e contatti principali.',
      phoneTitle: 'Telefono',
      phoneSubtitle: 'Prefisso internazionale e numero di cellulare.',
      addressTitle: 'Indirizzo',
      addressSubtitle: 'Dati geografici e recapiti del cliente.',
      birthDateTitle: 'Data di nascita',
      birthDateSubtitle: 'Formato di salvataggio: yyyy-MM-dd.',
      tagsTitle: 'Tag cliente',
      tagsSubtitle: 'Seleziona i tag associati al cliente.',
      tagsPlaceholder: 'Seleziona tag',
      updateSuccess: 'Cliente aggiornato con successo.',
      updateError: 'Impossibile aggiornare il cliente. Riprova.',
      deleteSuccess: 'Cliente eliminato con successo.',
      deleteError: 'Impossibile eliminare il cliente. Riprova.',
    },
  },
  dcfQuestions: {
    title: 'Domande e tag delle risposte',
    description:
      'Raccogli dati del cliente e risposte nello stesso modulo. Assegna tag alle domande compilate e alle opzioni selezionate.',
    backendRequired:
      'Le domande richiedono un aggiornamento delle API. Puoi configurarle qui, ma non salvarle finché il servizio non le supporta.',
    saveMismatch:
      'Le API non hanno restituito le domande salvate. Le modifiche sono ancora qui: chiedi al team API di abilitare il salvataggio.',
    unavailable:
      'Questo modulo non può ancora accettare risposte. Riprova più tardi.',
  },
  dataCollectionForms: {
    title: 'Modulo di Raccolta Dati',
    subtitle: 'Moduli presenti',
    addDataCollectionForm: 'Aggiungi Modulo',
    searchPlaceholder: 'Cerca moduli di raccolta dati per nome',
    noDataTitle: 'Nessun modulo presente',
    noDataDesc:
      'Crea il tuo primo modulo di raccolta dati per iniziare a raccogliere leads e interazioni.',
    form: 'Modulo',
    tags: 'Tag',
    tagsMore: '+{{count}} altri',
    tagsLess: 'Meno',
    status: 'Stato',
    interactions: 'Interazioni',
    interactionsCount: '{{count}} interazioni',
    url: 'URL',
    qrCode: 'Codice QR',
    preview: 'Anteprima',
    copyUrl: 'Copia URL',
    downloadQr: 'Scarica QR',
    editDataCollectionForm: 'Modifica modulo di raccolta dati',
    statusActive: 'Attivo',
    statusInactive: 'Inattivo',
    statusSuspended: 'Sospeso',
    statusDraft: 'Bozza',
    statsCount: '{{count}} moduli di raccolta dati',
    updatedAgo: 'Aggiornato 2 minuti fa',
    deleteDataCollectionForm: 'Elimina modulo di raccolta dati',
    duplicate: 'Duplica',
    createTitle: 'Crea modulo di raccolta dati',
    formName: 'Nome del modulo',
    formNamePlaceholder: 'Inserisci il nome del modulo',
    createForm: 'Crea form',
    deleteModel: {
      deleteTitle: 'Eliminare il modulo di raccolta dati?',
      deleteDescription:
        'Sei sicuro di voler eliminare questo modulo? Questa azione non può essere annullata.',
    },
    validation: {
      formNameMin: 'Il nome deve contenere almeno 2 caratteri',
      formNameMax: 'Il nome non può superare i 255 caratteri',
    },
    scanQrAccess: 'Scansiona il codice QR per accedere a:',
    qrClose: 'Chiudi',
    qrDownload: 'Scarica',
    toast: {
      createSuccess: 'Modulo creato con successo.',
      createError: 'Impossibile creare il modulo. Riprova.',
      updateSuccess: 'Modulo aggiornato con successo.',
      updateError: 'Impossibile aggiornare il modulo. Riprova.',
      logoUploadSuccess: 'Logo caricato con successo.',
      logoUploadError: 'Impossibile caricare il logo.',
      logoRemoveSuccess: 'Logo rimosso con successo.',
      logoRemoveError: 'Impossibile rimuovere il logo.',
      bgUploadSuccess: 'Sfondo caricato con successo.',
      bgUploadError: 'Impossibile caricare lo sfondo.',
      bgRemoveSuccess: 'Sfondo rimosso con successo.',
      bgRemoveError: 'Impossibile rimuovere lo sfondo.',
      statusUpdateSuccess: 'Stato aggiornato con successo.',
      statusUpdateError: 'Impossibile aggiornare lo stato. Riprova.',
      deleteSuccess: 'Modulo eliminato con successo.',
      deleteError: 'Impossibile eliminare il modulo. Riprova.',
      duplicateSuccess: 'Modulo duplicato con successo.',
      duplicateError: 'Duplicazione del modulo non riuscita. Riprova.',
    },
    edit: {
      pageTitle: 'Consenso dati personali',
      subtitle: 'Configura il modulo di raccolta dati',
      close: 'Chiudi',
      formConfiguration: 'Configurazione modulo',
      formConfigurationDesc:
        'Configurazioni generali del modulo di raccolta dati',
      formImage: 'Immagine modulo',
      formImageHint:
        "Consigliamo un logo PNG con sfondo trasparente. Doppio clic sull'immagine per il reindirizzamento.",
      formBackgroundImage: 'Immagine di sfondo del modulo',
      imageUpload: 'Carica immagine',
      removeImage: 'Rimuovi immagine',
      redirectAfterCompilation: 'Reindirizzamento dopo la compilazione',
      linkForm: 'Link modulo',
      buttonText: 'Testo pulsante',
      description: 'Descrizione',
      descriptionPlaceholder: 'Testo mostrato a sinistra nel modulo pubblico',
      dataFields: 'Campi dati',
      requestEmail: 'Richiedi email',
      requestAddress: 'Richiedi indirizzo leave',
      requestNationality: 'Richiedi nazionalità',
      requestZipCode: 'Richiedi CAP',
      requestDateOfBirth: 'Richiedi data di nascita',
      tagsApplied: 'Tag applicati al modulo',
      tagsOnDownload: 'Tag applicati al download',
      tagsOnUse: 'Tag assegnati dopo il salvataggio dei dati',
      tagsPlaceholder: 'Seleziona tag',
      tagsHint: 'Nomi tag separati da virgola',
      uploading: 'Caricamento...',
      removing: 'Rimozione...',
      validation: {
        redirectUrlRequired: 'L’URL di reindirizzamento è obbligatorio.',
        redirectUrlInvalid:
          'Inserisci un URL valido (es. https://example.com).',
      },
    },
  },
  publicDataCollectionForm: {
    documentTitle: 'Modulo raccolta dati',
    loading: 'Caricamento modulo',
    missingFormCode: 'Il codice del modulo manca dall’URL.',
    loadError: 'Impossibile caricare questo modulo di raccolta dati.',
    unavailableTitle: 'Modulo non disponibile',
    unavailableMessage:
      'Questo modulo di raccolta dati è attualmente sospeso o non disponibile.',
    personalInfo: 'Info personali',
    prefixLabel: 'Prefisso',
    phoneLabel: 'Cellulare',
    phonePlaceholder: 'Il tuo numero',
    firstName: 'Nome',
    firstNamePlaceholder: 'Il tuo nome',
    lastName: 'Cognome',
    lastNamePlaceholder: 'Il tuo cognome',
    email: 'Email',
    emailPlaceholder: 'nome@esempio.it',
    optional: 'Facoltativo',
    cap: 'CAP',
    capPlaceholder: 'Es. 09124',
    birthDate: 'Data di nascita',
    address: 'Indirizzo',
    addressPlaceholder: 'Via e numero',
    nationality: 'Nazionalità',
    nationalityPlaceholder: 'La tua nazionalità',
    requiredNote: '* Campi obbligatori',
    consentsTitle: 'Le tue preferenze',
    privacyConsent: 'Privacy e trattamento dei dati',
    marketingConsent: 'Consenso marketing e comunicazione',
    profilingConsent: 'Consenso al profiling',
    detailsLink: 'Informativa',
    selectAll: 'Seleziona tutto',
    submit: 'Accedi alla pagina',
    welcomeBackTitle: 'Bentornato, {{name}}!',
    welcomeBackTitleGeneric: 'Bentornato!',
    welcomeBackMessage:
      'Abbiamo già i tuoi dati. Puoi aggiungere un altro contatto se stai compilando per qualcun altro.',
    addAnotherContact: 'Aggiungi un altro contatto',
    toast: {
      success: 'Dati inviati con successo.',
      error: 'Invio dei dati non riuscito. Riprova.',
    },
    thankYou: {
      title: 'Grazie!',
      message: 'I tuoi dati sono stati inviati con successo.',
      continue: 'Continua',
    },
    validation: {
      prefixRequired: 'Seleziona un prefisso internazionale.',
      phoneMin: 'Inserisci un numero di cellulare valido.',
      firstNameRequired: 'Il nome è obbligatorio.',
      lastNameRequired: 'Il cognome è obbligatorio.',
      emailRequired: 'L’email è obbligatoria.',
      emailInvalid: 'Inserisci un’email valida.',
      birthDateInvalid: 'Inserisci una data valida, non futura.',
      privacyRequired: 'Il consenso privacy è obbligatorio.',
    },
  },
  bulkMessage: {
    title: 'Invio in massa',
    subtitle: 'Gestisci campagne WhatsApp, Email e SMS per {{branch}}',
    subtitleDefault:
      'Visualizza e gestisci le tue campagne di messaggi massivi',
    statsCount: '{{count}} campagne',
    searchPlaceholder: 'Cerca invio massivo...',
    campaignName: 'Nome campagna',
    campaignNamePlaceholder: 'Inserisci il nome della campagna',
    addCampaign: 'Nuova campagna',
    creating: 'Creazione...',
    createTitle: 'Crea nuova campagna di invio messaggi',
    createDesc:
      'Aggiungi una nuova campagna di messaggi massivi per la filiale.',
    close: 'Chiudi',
    duplicate: 'Duplica',
    editCampaign: 'Modifica campagna',
    viewDetails: 'Vedi dettagli',
    pause: 'Metti in pausa',
    resume: 'Riprendi invio',
    delete: 'Elimina campagna',
    noBranch: 'Seleziona una filiale per visualizzare i messaggi massivi',
    empty: 'Nessuna campagna trovata',
    emptyDesc:
      'Non ci sono ancora campagne di messaggi massivi per questa filiale.',
    channels: {
      whatsappWithImage: 'WHATSAPP CON IMMAGINE',
    },
    columns: {
      name: 'Nome',
      type: 'Tipo',
      state: 'Stato',
      progress: 'Progresso',
      selectedCustomers: 'Clienti selezionati',
      status: 'Stato',
      channel: 'Canale',
      messageType: 'Tipo di messaggio',
      customer: 'Cliente',
      reference: 'Riferimento',
      attempts: 'Tentativi',
      createdAt: 'Creato il',
    },
    statusFilter: {
      all: 'Tutti gli stati',
      pending: 'In attesa',
      sent: 'Inviato',
      delivered: 'Consegnato',
      failed: 'Non riuscito',
    },
    noDataTitle: 'Nessun messaggio trovato',
    noDataDesc:
      'Non ci sono ancora eventi di consegna dei messaggi per questa filiale.',
    deleteModal: {
      title: 'Eliminare la campagna?',
      description:
        'Sei sicuro di voler eliminare questa campagna? Questa azione non può essere annullata.',
    },
    details: {
      pageTitle: 'Campagna {{name}}',
      subtitle: 'Stato andamento campagna invio massivo',
      empty: 'Nessun risultato trovato',
      emptyDesc: 'Non ci sono ancora eventi di consegna per questa campagna.',
      loadError: 'Impossibile caricare gli eventi di consegna. Riprova.',
      emailLabel: 'Email:',
      phoneLabel: 'Tel:',
      chatId: 'Chat {{id}}',
      httpStatus: 'HTTP {{status}}',
      resubmit: 'Riprova invio',
      resubmitSuccess: 'Evento rimesso in coda correttamente.',
      resubmitError: 'Impossibile rimettere in coda il messaggio. Riprova.',
      resubmitConfirmTitle: 'Il precedente tentativo è risultato riuscito',
      resubmitConfirmDescription:
        'Sei sicuro di voler inviare nuovamente questo messaggio?',
      resubmitConfirmAction: 'Sì, invia di nuovo',
      columns: {
        recipient: 'Destinatario',
        message: 'Messaggio',
        channel: 'Canale',
        status: 'Esito',
        details: 'Dettagli',
      },
    },
    validation: {
      nameMin: 'Il nome della campagna deve avere almeno 2 caratteri',
      nameMax: 'Il nome della campagna non può superare i 120 caratteri',
    },
    toast: {
      createSuccess: 'Campagna creata correttamente.',
      createError: 'Creazione campagna non riuscita. Riprova.',
      updateSuccess: 'Campagna salvata correttamente.',
      updateError: 'Salvataggio campagna non riuscito. Riprova.',
      deleteSuccess: 'Campagna eliminata correttamente.',
      deleteError: 'Eliminazione campagna non riuscita. Riprova.',
      duplicateSuccess: 'Campagna duplicata correttamente.',
      duplicateError: 'Duplicazione campagna non riuscita. Riprova.',
      imageRemoveSuccess: 'Immagine allegata rimossa correttamente.',
      imageRemoveError: 'Rimozione dell’immagine allegata non riuscita.',
      imageUploadSuccess: 'Immagine caricata correttamente.',
      imageUploadError: 'Caricamento immagine non riuscito. Riprova.',
      imageTypeError: 'Sono consentite solo immagini JPG e PNG.',
      imageSizeError: 'L’immagine deve essere di massimo 5 MB.',
      startSuccess:
        'L’invio dei messaggi massivi è stato avviato. Monitora l’elenco per controllarne l’andamento.',
      startError: 'Avvio della campagna non riuscito. Riprova.',
      pauseSuccess: 'Campagna messa in pausa correttamente.',
      pauseError:
        'Errore durante l’interruzione dei messaggi massivi. Riprova fra 2 minuti.',
      resumeSuccess: 'Campagna ripresa correttamente.',
      resumeError:
        'Errore durante il riavvio dei messaggi massivi. Riprova fra 2 minuti.',
    },
    edit: {
      pageTitle: 'Modifica {{name}}',
      subtitle:
        'Configura i dettagli della campagna e il contenuto del messaggio',
      startCampaign: 'Avvia campagna',
      start: {
        confirmTitle: 'Procediamo?',
        confirmDescription:
          'Stai per mandare un messaggio massivo di tipo {{channel}} a {{count}} clienti.',
        confirmAction: 'Sì, procedi',
        noRecipients:
          'Non ci sono destinatari, aggiungi dei destinatari per avviare la campagna.',
        emptyMessage: 'Inserisci un messaggio per avviare la campagna.',
        emptySubject:
          'Inserisci un titolo/oggetto per avviare la campagna email.',
        missingImage:
          'Inserisci un’immagine per avviare la campagna WhatsApp con immagine.',
        missingTemplate:
          'Seleziona un template per avviare la campagna tramite WhatsApp API Ufficiali.',
        corruptTemplateVariables:
          'Le variabili per questo template sono vuote o non disponibili. Il template è corrotto. Scegli un altro template.',
        corruptTemplateLanguages:
          'Questo template non ha lingue associate e quindi risulta corrotto. Scegli un altro template.',
      },
      saveConfiguration: 'Salva configurazione',
      configTitle: 'Configurazione messaggio massivo',
      configDesc:
        'Configura campagne WhatsApp ed email da inviare ai clienti selezionati.',
      configuration: 'Configurazione',
      configurationDesc:
        'Imposta il nome della campagna e il canale di invio massivo.',
      audience: 'Audience',
      selectedCustomers: 'Clienti selezionati',
      customersReady: '{{count}} clienti pronti per l’invio massivo',
      selectCustomers: 'Seleziona clienti',
      selectCustomersSoon: 'La selezione clienti sarà disponibile a breve.',
      channel: 'Canale',
      whatsappTitle: 'Messaggio WhatsApp',
      whatsappDesc: 'Configura il contenuto del messaggio WhatsApp.',
      whatsappPlaceholder: 'Scrivi qui il tuo messaggio...',
      whatsappBusinessTitle: 'Template WhatsApp Business',
      whatsappBusinessDesc:
        'Seleziona tra i template approvati da Meta per l’invio via WhatsApp Business API.',
      whatsappBusinessEmptyDesc:
        'Seleziona un template approvato da Meta per configurare l’invio via WhatsApp Business API.',
      selectedTemplate: 'Template selezionato',
      noTemplateSelected: 'Nessun template selezionato',
      selectTemplate: 'Seleziona template',
      selectTemplateTooltip:
        'Seleziona un template tra quelli approvati da Meta',
      selectTemplateSoon: 'La selezione del template sarà disponibile a breve.',
      templateModal: {
        title: 'Seleziona un template',
        description:
          'Scegli uno dei template approvati su WhatsApp Business API.',
        messageTypeBadge: 'Per {{messageType}}',
        countBadge: '{{count}} template',
        countBadgeFiltered: '{{count}} di {{total}}',
        searchPlaceholder: 'Search template by name...',
        empty: 'Nessun template disponibile',
        selectAction: 'Seleziona template',
        recommended:
          'Template consigliato in base al tipo messaggio selezionato ({{messageType}})',
        bodyPreview: 'Body Preview',
        noPreview: 'Nessuna anteprima del body disponibile.',
        noCta: 'No CTA',
        ctaEnabled: 'CTA Enabled',
        corruptHint:
          'Questo template risulta corrotto e non è utilizzabile. Probabilmente è stato modificato manualmente su Facebook Business Manager e non rispetta più la struttura richiesta da WhatsApp Business API.',
        loadError: 'Impossibile caricare i template',
        loadErrorDesc:
          'Recupero dei template WhatsApp Business non riuscito. Riprova.',
        selectSuccess: 'Template selezionato correttamente.',
        selectError: 'Selezione del template non riuscita. Riprova.',
      },
      whatsappWithImageTitle: 'WhatsApp con immagine',
      whatsappWithImageDesc:
        'Configura messaggio, allegato immagine e segnaposto dinamici.',
      whatsappMessageText: 'Testo messaggio WhatsApp',
      emailTitle: 'Email massiva',
      emailDesc: 'Configura email, allegati e contenuto HTML.',
      emailSubject: 'Oggetto email',
      emailSubjectPlaceholder: 'Es: Promo speciale per te 🎉',
      emailBody: 'Contenuto email',
      emailBodyHint: 'Se inserisci un link, lascia uno spazio',
      applyTemplate: 'Applica modello',
      sendTest: 'Invia messaggio di prova',
      attachedImage: 'Immagine allegata',
      uploadImage: 'Carica immagine',
      removeImage: 'Rimuovi immagine',
      uploading: 'Caricamento...',
      removing: 'Rimozione...',
      test: {
        title: 'Invio di prova',
        emailTitle: 'Invio di prova',
        oopsTitle: 'Oops...',
        ok: 'OK',
        send: 'Invia',
        testSending: 'Invio di prova',
        backwards: 'Indietro',
        emptyMessage: 'Inserisci un messaggio per provare l’invio.',
        emptySubject: 'Inserisci un oggetto email per provare l’invio.',
        missingImage:
          'Nessuna immagine associata alla campagna, inseriscine una per provare l’invio con immagine.',
        mobilePlaceholder: 'Telefono cellulare',
        emptyPhone: 'Inserisci un numero di cellulare per provare l’invio.',
        emptyEmail: 'Inserisci un indirizzo email per provare l’invio.',
        emailLabel: 'Email:',
        emailPlaceholder:
          'Inserisci l’indirizzo email dove vuoi ricevere il test',
        apiSoon: 'L’API di invio di prova sarà disponibile a breve.',
        sendSuccess: 'Messaggio di prova inviato correttamente.',
        sendError: 'Impossibile inviare il messaggio di prova. Riprova.',
      },
    },
    selectCustomers: {
      pageTitle: 'Assegna clienti a {{name}}',
      subtitle: 'Filtra e seleziona i clienti per questa campagna',
      subtitleWithBranch: 'Filtra e seleziona i clienti per {{branch}}',
      searchPlaceholder: 'Cerca cliente...',
      selected: 'Selezionati',
      clearSelection: 'Cancella selezione',
      removeAll: 'Rimuovi tutti',
      saveList: 'Salva elenco clienti',
      bookingPeriodTitle: 'Prenotazioni nel periodo',
      bookingRangeLabel: 'Intervallo prenotazioni',
      bookingRangePlaceholder: 'Seleziona intervallo prenotazioni',
      removePeriod: 'Rimuovi periodo',
      marketingConsentShort: 'Consenso marketing',
      listTitle: 'Elenco clienti',
      listDesc: 'Gestisci la selezione dei clienti',
      selectPage: 'Seleziona pagina',
      deselectPage: 'Deseleziona pagina',
      selectAll: 'Seleziona tutto',
      deselectAll: 'Deseleziona tutto',
      progressAdding: 'Aggiunta {{current}} di {{total}}',
      selectRow: 'Seleziona cliente',
      results: 'Risultati',
      page: 'Pagina',
      noEmail: 'Nessuna email',
      columns: {
        customer: 'Cliente',
        phone: 'Telefono',
        birth: 'Nascita',
        bookings: 'Prenotazioni',
        tags: 'Tag',
      },
      toast: {
        saveSuccess: 'Elenco clienti salvato correttamente.',
        saveError: 'Salvataggio dell’elenco clienti non riuscito. Riprova.',
        updateError: 'Aggiornamento selezione clienti non riuscito. Riprova.',
        selectAllSuccess: 'Selezionati {{count}} clienti.',
        selectAllPartial:
          'Aggiunti {{added}} clienti, poi la richiesta è fallita. Riprova con Seleziona tutto.',
        clearSuccess: 'Tutti i clienti rimossi dalla selezione.',
        clearError: 'Impossibile cancellare la selezione clienti. Riprova.',
      },
    },
  },
  fidelity: {
    title: 'Fedeltà',
    subtitleDefault: 'Visualizza e gestisci le tue fedeltà',
    statsCount: '{{count}} fedeltà',
    searchPlaceholder: 'Cerca una fedeltà per nome...',
    addProgram: 'Nuova fedeltà',
    creating: 'Creazione...',
    saving: 'Salvataggio...',
    createTitle: 'Crea fedeltà',
    createDesc: 'Aggiungi una nuova fedeltà per la tua filiale.',
    createActiveConflict:
      'Esiste già una fedeltà {{type}} attiva "{{name}}" per questa filiale.',
    createActiveConflictAction:
      'Disattiva prima la fedeltà esistente, oppure lascia questa inattiva.',
    createActiveBlocked:
      'Disattiva Fedeltà attiva per continuare, oppure disattiva "{{name}}" dall’elenco.',
    edit: {
      pageTitle: 'Modifica fedeltà',
    },
    programName: 'Nome della fedeltà',
    programNamePlaceholder: 'Inserisci il nome della fedeltà',
    description: 'Descrizione',
    descriptionPlaceholder: 'Inserisci la descrizione della fedeltà',
    policyMessageCard: 'Policy / Carta messaggio',
    tagsOnDownload: 'Tag applicati al download',
    tagsOnUse: 'Tag applicati all’utilizzo',
    tagsPlaceholder: 'Seleziona tag',
    type: 'Tipo',
    typePoints: 'Punti',
    typeDiscount: 'Sconto',
    typeDiscountPrepaid: 'Sconto / prepagata',
    customerCards: 'Carte clienti',
    noThresholds: 'Nessuna soglia punti configurata.',
    codeLinePrepaid:
      'Programma con credito prepagato e sconto applicato alla card.',
    color: 'Colore',
    value: 'Valore',
    active: 'Attivo',
    inactive: 'Inattivo',
    activeProgram: 'Fedeltà attiva',
    discountApplied: 'Sconto applicato (%)',
    codeLineRange: 'Da {{from}} a {{to}} euro',
    codeLineDiscount: '{{value}}% di sconto',
    thresholdCount: '{{count}} soglie',
    customersCount: '{{count}} clienti',
    thresholdsTitle: 'Fasce di spesa e punti assegnati',
    thresholdsHint:
      'Per ogni intervallo di spesa scegli quanti punti riceve il cliente. Le fasce vengono riordinate automaticamente.',
    addThreshold: 'Soglia',
    updateThreshold: 'Aggiorna soglia',
    thresholdFrom: 'Da euro*',
    thresholdTo: 'A euro*',
    thresholdPointsLabel: 'Punti*',
    thresholdLabel: 'Etichetta',
    thresholdColor: 'Colore soglia',
    editProgram: 'Modifica fedeltà',
    deleteProgram: 'Elimina fedeltà',
    duplicate: 'Duplica',
    editThreshold: 'Modifica soglia',
    deleteThreshold: 'Elimina soglia',
    noBranch: 'Seleziona una filiale per vedere le fedeltà',
    empty: 'Nessuna fedeltà configurata',
    emptyDesc:
      'Crea una fedeltà a punti o sconto per iniziare a generare carte clienti.',
    cardsTitle: 'Carte cliente',
    cardsSubtitle: 'Carte emesse, QR e saldi',
    cardsEmpty: 'Nessuna carta fedeltà trovata per questa fedeltà.',
    searchCustomerPlaceholder: 'Sul cliente, la carta...',
    createCard: 'Crea carta',
    refreshCards: 'Aggiorna carte',
    update: 'Aggiorna',
    scanQrCode: 'Scansiona codice QR',
    useCard: 'Usa carta',
    toggleCardStatus: 'Cambia stato usata/disponibile',
    operationHistory: 'Cronologia operazioni',
    qrCodeTitle: 'Scansiona codice QR',
    downloadQr: 'Scarica',
    historyTitle: 'Storico carta fedeltà',
    historyEmpty: 'Nessuna operazione disponibile per questa carta fedeltà.',
    postedBy: 'Pubblicato da',
    branchCode: 'Filiale',
    fidelityCode: 'Fedeltà',
    customerId: 'ID cliente',
    pointsSuffix: 'punti',
    amountSuffix: 'euro',
    cardAvailable: 'disponibile',
    cardUsed: 'usata',
    columns: {
      qr: 'QR',
      customer: 'Cliente',
      contacts: 'Contatti',
      card: 'Carta',
      balance: 'Saldo',
      state: 'Stato',
      activation: 'Attivazione',
      threshold: 'Soglia',
      label: 'Etichetta',
      points: 'Punti',
      color: 'Colore',
    },
    createCardPage: {
      title: 'Scegli cliente',
      subtitle: 'Seleziona un cliente per emettere una nuova carta fedeltà.',
      subtitleWithProgram: 'Clienti per {{branch}} · {{program}}',
      newCard: 'Nuova carta',
      chooseCustomer: 'Scegli cliente',
      searchPlaceholder: 'Cerca per nome, telefono o email...',
      results: 'Risultati',
      page: 'Pagina',
      availableCustomers: 'Clienti disponibili',
      availableCustomersDesc:
        'Seleziona un cliente per aprire la conferma di creazione carta.',
      noEmail: 'Nessuna email',
      columns: {
        customer: 'Cliente',
        telephone: 'Telefono',
        wood: 'Presenze',
        tags: 'Tag',
      },
    },
    createCardConfirm: {
      title: 'Vuoi creare una carta per questo cliente?',
      confirm: 'Sì, crea carte',
      creating: 'Creazione...',
    },
    statCards: {
      programs: 'Fedeltà',
      active: 'Attivi',
      types: 'Tipi',
    },
    deleteModal: {
      title: 'Eliminare la fedeltà?',
      description:
        'Sei sicuro di voler eliminare questa fedeltà? L’azione non può essere annullata.',
    },
    deleteThresholdModal: {
      title: 'Eliminare la soglia?',
      description:
        'Sei sicuro di voler eliminare questa soglia? L’azione non può essere annullata.',
    },
    thresholdModal: {
      createTitle: 'Aggiungi soglia punti',
      editTitle: 'Modifica soglia punti',
      description:
        'Configura intervallo, punti, etichetta e colore della soglia.',
      lowerLimit: 'Limite inferiore*',
      upperLimit: 'Limite superiore*',
      pointsApplied: 'Punti applicati*',
    },
    toast: {
      createSuccess: 'Fedeltà creata con successo.',
      createError: 'Creazione della fedeltà non riuscita. Riprova.',
      updateSuccess: 'Fedeltà aggiornata con successo.',
      updateError: 'Aggiornamento della fedeltà non riuscito. Riprova.',
      deleteSuccess: 'Fedeltà eliminata con successo.',
      deleteError: 'Eliminazione della fedeltà non riuscita. Riprova.',
      duplicateSuccess: 'Fedeltà duplicata con successo.',
      duplicateError: 'Duplicazione della fedeltà non riuscita. Riprova.',
      thresholdCreateSuccess: 'Soglia creata con successo.',
      thresholdCreateError: 'Creazione della soglia non riuscita.',
      thresholdUpdateSuccess: 'Soglia aggiornata con successo.',
      thresholdUpdateError: 'Aggiornamento della soglia non riuscito.',
      thresholdDeleteSuccess: 'Soglia eliminata con successo.',
      thresholdDeleteError: 'Eliminazione della soglia non riuscita.',
      cardCreateSuccess: 'Carta fedeltà creata con successo.',
      cardCreateError: 'Creazione della carta fedeltà non riuscita.',
      cardUseSuccess: 'Carta segnata come usata.',
      cardUseError: 'Aggiornamento dell’uso della carta non riuscito.',
      cardToggleSuccess: 'Stato della carta aggiornato con successo.',
      cardToggleError: 'Aggiornamento dello stato della carta non riuscito.',
    },
    operations: {
      addPoints: 'Punti aggiunti',
      updatePoints: 'Punti aggiornati',
      removePoints: 'Punti rimossi',
      addBillAmount: 'Importo conto aggiunto',
      addPrepaidAmount: 'Importo prepagato aggiunto',
      updatePrepaidAmount: 'Importo prepagato aggiornato',
      disableAccount: 'Account disabilitato',
      createAccount: 'Account creato',
      used: 'Carta usata',
    },
    registration: {
      title: 'Link di iscrizione alla Fidelity',
      description:
        'Condividi il link: dopo l’iscrizione il cliente riceve la propria card con QR personale.',
      linkLabel: 'Link iscrizione fidelity',
      openForm: 'Apri il modulo',
      copy: 'Copia link',
      qr: 'Codice QR',
    },
    views: {
      label: 'Programma fidelity',
      configuration: 'Configurazione',
      configurationHint: 'Regole, vantaggi e messaggi',
      cards: 'Carte clienti',
      cardsHint: 'Carte emesse, QR e movimenti',
      allPrograms: 'Tutti i programmi fidelity',
    },
    wizard: {
      program: 'Programma e vantaggi',
      messages: 'Messaggi',
      review: 'Riepilogo',
      back: 'Indietro',
      continue: 'Continua',
      save: 'Salva configurazione',
      stepOf: '{{current}} / {{total}}',
      stepListLabel: 'Passaggi di configurazione fedeltà',
      pointsIntro:
        'Definisci il programma, le regole per accumulare punti e il messaggio di benvenuto.',
      discountIntro:
        'Definisci la percentuale, l’aspetto della carta e il messaggio di benvenuto.',
      discountNote:
        'Il cliente ottiene lo sconto appena si iscrive alla Fidelity. Non deve accumulare punti o raggiungere soglie.',
      benefitPoints: 'Accumulo punti',
      benefitDiscount: '{{value}}% di sconto',
      messageLabel: 'Messaggio di benvenuto',
      messagePlaceholder: 'Scegli un messaggio fidelity',
      messageStatus: '{{status}} · WhatsApp {{whatsapp}} · Email {{email}}',
      messageActive: 'Attivo',
      messageInactive: 'Disattivato',
      channelOn: 'attivo',
      channelOff: 'disattivo',
      messageWarning:
        'Attiva WhatsApp o email per questo messaggio prima di salvare.',
      messageLoadError: 'Impossibile caricare i messaggi.',
      configureMessages: 'Configura i messaggi',
      rulesLabel: 'Regole del programma',
      reviewTitle: 'Riepilogo del programma',
      name: 'Nome',
      status: 'Stato',
      benefit: 'Vantaggio',
      message: 'Messaggio',
      autoUpdates: 'Aggiornamenti automatici',
      autoUpdatesValue:
        'Messaggio dedicato per ogni operazione, configurabile nella sezione Messaggi.',
      createProgram: 'Il programma',
      createBenefits: 'Punti e premi',
      createPoints: 'Fasce punti',
      createRewards: 'Premi riscattabili',
      createHistorical: 'Soglie storiche',
      createProgramDesc: 'Scegli il nome e il tipo di programma.',
      createPointsDesc:
        'Definisci una sola regola di accumulo: gli stessi punti alimenteranno il saldo spendibile e quello storico.',
      createRewardsDesc:
        'Configura le soglie e i premi riscattabili. Al riscatto i punti richiesti vengono sottratti dal saldo disponibile.',
      createHistoricalDesc:
        'Configura punti, nome, colore e sconto dei livelli Fidelity. Anche da 0 punti: lo sconto è riconosciuto dal QR Fidelity e i punti storici non vengono scalati.',
      createBenefitsDesc:
        'Decidi come si guadagnano i punti e quali vantaggi si sbloccano, oppure imposta uno sconto.',
      createMessagesDesc:
        'Configura WhatsApp e l’email di benvenuto con il QR della carta.',
      createReviewDesc:
        'Verifica vantaggi, stato e messaggio prima di creare il programma.',
      createAction: 'Crea programma',
      afterSaveNote:
        'Potrai creare le carte cliente dopo aver salvato il programma.',
      thresholdStepHint:
        'Assegna punti fissi in base al totale speso. Esempio: una spesa da 0 a 49 € assegna 5 punti in totale, non 5 punti per euro.',
      thresholdReviewLine: 'Da {{from}} a {{to}} €: {{points}} punti',
    },
    validation: {
      nameMin: 'Il nome della fedeltà deve avere almeno 2 caratteri',
      nameMax: 'Il nome della fedeltà non può superare 100 caratteri',
      colorInvalid: 'Inserisci un colore esadecimale valido (es. #FFFFFF)',
      thresholdRequired: 'Aggiungi almeno una soglia.',
      thresholdRange:
        'Il limite superiore deve essere maggiore o uguale al limite inferiore.',
      thresholdLower: 'Inserisci un limite inferiore valido.',
      thresholdUpper: 'Inserisci un limite superiore valido.',
      thresholdPoints: 'Inserisci punti validi.',
      thresholdLabelRequired: 'L’etichetta della soglia è obbligatoria.',
      thresholdLabelMax: 'L’etichetta non può superare 100 caratteri.',
    },
  },
  marketingGuide: {
    infoButton: 'Guida marketing',
    title: 'Scegli lo strumento di marketing giusto',
    intro:
      'Promozioni e Fidelity possono offrire entrambe un vantaggio, ma rispondono a obiettivi diversi.',
    promotionTitle: 'Promozioni · una campagna con una scadenza',
    promotionBody:
      'Usa una promozione per attirare nuovi clienti, riattivare quelli occasionali o sostenere un periodo più tranquillo. Il cliente aderisce tramite un link pubblico o un QR e riceve un coupon: non deve accumulare punti.\nEsempio: “10% di sconto fino a domenica, con spesa minima di 30 €, una volta per cliente.”',
    fidelityTitle: 'Fidelity · un motivo per tornare',
    fidelityBody:
      'Usa la Fidelity per premiare le visite ripetute. La spesa genera punti secondo le regole configurate; raggiunta una soglia, il cliente può scegliere un premio. Saldo e storico delle operazioni restano associati alla sua carta.\nEsempio: “Accumula punti con ogni scontrino e scegli un premio al raggiungimento di un traguardo.”',
    rulesTitle: 'Regole pratiche di marketing',
    rulesBody:
      '1. Definisci un obiettivo preciso: acquisire clienti, riattivarli, aumentare le visite o lo scontrino medio.\n2. Proteggi il margine: scegli uno sconto e una spesa minima sostenibili, e imposta un limite di quantità o budget.\n3. Spiega bene le condizioni: date, prodotti o canali validi, spesa minima e cumulabilità.\n4. Rendi l’offerta semplice da capire e usare: troppe condizioni riducono le adesioni.\n5. Misura i riscatti e le spese successive, non solo i coupon distribuiti o i punti accumulati.',
    togetherTitle: 'Possono lavorare insieme',
    togetherBody:
      'Usa una promozione a tempo per portare un cliente nel locale, poi invitalo ad aderire alla Fidelity per dargli un motivo continuativo per tornare. Coupon promozionale e premio Fidelity devono restare ben riconoscibili, così cliente e personale sanno quale vantaggio stanno usando.',
  },
  promotions: {
    title: 'Promozioni',
    titleWithBranch: 'Promo {{branch}}',
    subtitle: 'Elenco Promozioni per {{branch}}',
    subtitleDefault: 'Visualizza e gestisci l’elenco delle promozioni',
    pageDescription:
      'Gestisci promozioni, coupon e configurazioni promo della sede',
    statsCount: '{{count}} promozioni',
    searchPlaceholder: 'Cerca promozioni per nome...',
    promoName: 'Nome promo',
    promoNamePlaceholder: 'Inserisci il nome della promo',
    description: 'Descrizione',
    descriptionPlaceholder: 'Inserisci la descrizione della promo',
    used: 'Utilizzati',
    usedCouponCount: '{{count}} coupon',
    downloaded: 'Scaricati',
    messages: 'Messaggi',
    messagesTooltip: 'Messaggi inviati tramite questa promo',
    messageHistory: {
      title: 'Cronologia messaggi',
      reference: 'Riferimento {{code}}',
      empty: 'Nessun messaggio disponibile.',
      refresh: 'Aggiorna',
      loadError: 'Impossibile caricare i messaggi.',
      attempts: '{{count}} tentativi',
    },
    customerCoupons: {
      title: 'Coupon clienti',
      subtitle: 'Promo: {{name}} · {{count}} coupon emessi',
      discount: '{{percent}}% sconto',
      redeemed: 'Riscattato',
      notRedeemed: 'Non riscattato',
      generatedOn: 'Generato il {{date}}',
      redeemedOn: 'Riscattato il {{date}}',
      operator: 'Operatore: {{name}}',
      empty: 'Nessun coupon emesso.',
      loadError: 'Impossibile caricare i coupon clienti.',
      loading: 'Caricamento coupon...',
    },
    status: 'Stato',
    active: 'Attiva',
    expired: 'Scaduta',
    url: 'URL',
    qrCode: 'Codice QR',
    preview: 'Anteprima',
    copyUrl: 'Copia URL',
    duplicate: 'Duplica',
    stop: 'Ferma',
    restart: 'Riavvia',
    editPromotion: 'Modifica promo',
    delete: 'Elimina promo',
    addPromotion: 'Aggiungi promo',
    creating: 'Creazione...',
    createTitle: 'Crea Promozione',
    createDesc: 'Aggiungi una nuova campagna per la tua filiale.',
    createWizard: {
      detailsDesc:
        'Definisci nome, disponibilità e sconto. Potrai personalizzare il modulo cliente dopo la creazione.',
      messageDesc:
        'Scegli uno dei tre messaggi configurati in Impostazioni → Messaggi.',
      reviewHint: 'Controlla le impostazioni prima di creare la promozione.',
      stepCount: '{{current}} / {{total}}',
      back: 'Indietro',
      continue: 'Continua',
      createAndCustomize: 'Crea e personalizza',
      couponHint: 'Usa 0 se la promozione non prevede uno sconto percentuale.',
      automaticUpdatesLabel: 'Aggiornamenti automatici',
      automaticUpdates:
        'Messaggio dedicato per ogni operazione, configurabile nella sezione Messaggi.',
    },
    close: 'Chiudi',
    save: 'Salva',
    saveChanges: 'Salva modifiche',
    buttonLabel: 'Testo pulsante',
    buttonLabelPlaceholder: 'Inserisci il testo del pulsante',
    redirectPage: 'Redirect dopo compilazione',
    redirectPagePlaceholder: 'URL pagina di ringraziamento personalizzata',
    percentageDiscount: 'Sconto (%)',
    maxCouponCount: 'Numero coupon',
    maxUsageCount: 'Utilizzi massimi',
    itemsPerPage: 'Elementi per pagina',
    range: '{{start}} - {{end}} di {{total}}',
    noBranch: 'Seleziona una filiale per visualizzare le promozioni',
    empty: 'Nessuna promo disponibile',
    emptyDesc:
      'Non hai ancora creato promozioni per questa sede. Inizia creando la tua prima promo.',
    validation: {
      promotionNameMin: 'Il nome deve avere almeno 2 caratteri',
      promotionNameMax: 'Il nome non può superare 120 caratteri',
      couponMin: 'Inserisci un numero intero maggiore di zero.',
      discountRange: 'Inserisci un numero intero da 0 a 100.',
      redirectPageInvalid: 'Inserisci un URL valido (es. https://example.com).',
    },
    deleteModal: {
      title: 'Eliminare la promo?',
      description:
        'Sei sicuro di voler eliminare questa promo? L’azione non può essere annullata.',
    },
    toast: {
      createSuccess: 'Promo creata con successo.',
      createError: 'Impossibile creare la promo. Riprova.',
      updateSuccess: 'Promo aggiornata con successo.',
      updateError: 'Impossibile aggiornare la promo. Riprova.',
      deleteSuccess: 'Promo eliminata con successo.',
      deleteError: 'Impossibile eliminare la promo. Riprova.',
      duplicateSuccess: 'Promo duplicata con successo.',
      duplicateError: 'Impossibile duplicare la promo. Riprova.',
      stopSuccess: 'Promo fermata con successo.',
      stopError: 'Impossibile fermare la promo. Riprova.',
      restartSuccess: 'Promo riavviata con successo.',
      restartError: 'Impossibile riavviare la promo. Riprova.',
      logoUploadSuccess: 'Logo caricato con successo.',
      logoUploadError: 'Impossibile caricare il logo.',
      logoRemoveSuccess: 'Logo rimosso con successo.',
      logoRemoveError: 'Impossibile rimuovere il logo.',
      posterUploadSuccess: 'Locandina caricata con successo.',
      posterUploadError: 'Impossibile caricare la locandina.',
      posterRemoveSuccess: 'Locandina rimossa con successo.',
      posterRemoveError: 'Impossibile rimuovere la locandina.',
    },
    edit: {
      pageTitle: 'Modifica promo',
      subtitle: 'Configura form e impostazioni promo.',
      tabListLabel: 'Sezioni modifica promo',
      wizard: {
        stepListLabel: 'Passaggi di configurazione promo',
        form: 'Modulo cliente',
        formDesc:
          'Personalizza il modulo che il cliente compila per richiedere il coupon.',
        rules: 'Regole',
        rulesDesc:
          'Definisci periodo, disponibilità, sconto e dati richiesti al cliente.',
        message: 'Messaggio',
        messageDesc:
          'Scegli uno dei tre messaggi configurati in Impostazioni → Messaggi.',
        review: 'Riepilogo',
        reviewDesc:
          'Controlla le impostazioni e salva. Le modifiche non salvate restano solo in questa pagina.',
        editStep: 'Modifica',
        period: 'Periodo',
        requestedFields: 'Dati richiesti',
        noDate: 'Senza data',
        none: 'Nessuno',
      },
      uploadImage: 'Carica immagine',
      removeImage: 'Rimuovi immagine',
      uploading: 'Caricamento...',
      removing: 'Rimozione...',
      tabs: {
        formConfig: 'Configurazione Form',
        promoConfig: 'Configurazione Promo',
      },
      formConfig: {
        media: 'Media',
        logo: 'Logo promo',
        details: 'Dettagli form',
        promoText: 'Testo promo',
        promoLink: 'Link promo',
      },
      promoConfig: {
        poster: 'Locandina',
        posterLabel: 'Locandina promo',
        options: 'Opzioni',
        useOnlyOnce: 'Coupon utilizzabile una sola volta',
        useOnlyOnceDesc:
          'Limita il download coupon ad una sola volta per cliente',
        showEmail: 'Richiedi Email',
        showEmailDesc: 'Mostra il campo email nel form promo',
        showAddress: 'Richiedi Indirizzo',
        showAddressDesc: 'Mostra il campo indirizzo nel form promo',
        showCap: 'Richiedi CAP',
        showCapDesc: 'Mostra il campo CAP nel form promo',
        showDob: 'Richiedi Data di Nascita',
        showDobDesc: 'Mostra il campo data di nascita nel form promo',
        showCountry: 'Richiedi Paese',
        limits: 'Date e limiti',
        startDate: 'Data Inizio Promo',
        endDate: 'Data Fine Promo',
        tags: 'Tag',
        tagsOnDownload: 'Tag applicati al download',
        tagsOnUse: 'Tag applicati all’utilizzo',
        tagsPlaceholder: 'Seleziona tag',
        messaging: 'Messaggistica',
        messageLabel: 'Messaggio per il cliente',
        messagePlaceholder: 'Scegli una delle tre configurazioni condivise.',
        messageStatus: '{{status}} · WhatsApp: {{whatsapp}} · Email: {{email}}',
        messageActive: 'Configurazione attiva',
        messageInactive: 'Configurazione disattivata',
        channelOn: 'attivo',
        channelOff: 'disattivato',
        messageWarning:
          'Abilita almeno un canale in Messaggi per inviare questa comunicazione.',
        configureMessages: 'Configura testi, canali e template in Messaggi',
        messageLoadError:
          'Impossibile caricare le configurazioni. Riprova dalla sezione Messaggi.',
        sharedMessageNote:
          'Ogni operazione usa il messaggio dedicato configurato nella sezione Messaggi. PROMO_INFO è riservato alle informazioni sullo stato.',
        whatsappMessage: 'Messaggio WhatsApp Coupon',
        whatsappSubtitle: 'Messaggio inviato con il QR code promo',
        whatsappPlaceholder: 'Scrivi qui il messaggio..',
        applyTemplate: 'Applica modello predefinito',
        sendMessage: 'Invia messaggio',
      },
      detailsTitle: 'Dettagli promo',
      detailsDesc: 'Aggiorna informazioni e limiti della campagna.',
      branchHint: 'Filiale: {{branch}}',
    },
  },
  supplierOrdersPage: {
    receivedModified: 'Ricevuto modificato',
    processedModified: 'Processato ma modificato',
    processed: 'Processato',
    title: 'Ordini ai fornitori',
    subtitle: 'Acquisti, consegne e verifica dei prodotti',
    history: 'Storico ordini',
    create: 'Crea ordine',
    search: 'Cerca ordine o prodotto…',
    supplier: 'Fornitore',
    allSuppliers: 'Tutti i fornitori',
    invalidDates: 'Seleziona un intervallo di date valido.',
    onlyModified: 'Solo ordini modificati',
    loadError: 'Impossibile caricare gli ordini.',
    ordersOn: 'Ordini del {{date}} · {{count}}',
    empty: 'Nessun ordine trovato',
    order: 'Ordine',
    delivery: 'Consegna',
    status: 'Stato',
    totalVat: 'Totale IVA inclusa',
    perPage: 'Per pagina',
    orderCount: '{{count}} ordini',
    modified: 'Modificato',
    recipient: 'Destinatario',
    chooseRecipient: 'Scegli un fornitore o una sede',
    branchSuffix: 'Sede',
    deliveryDate: 'Data consegna',
    futureDate: 'Scegli oggi o una data futura.',
    pastDate: 'La data di consegna deve essere oggi o una data futura.',
    receiveFrom: 'Ricezione dalle',
    receiveTo: 'Alle',
    catalogSearch: 'Cerca nel catalogo…',
    catalogError: 'Impossibile caricare il catalogo.',
    noProducts: 'Nessun prodotto disponibile',
    product: 'Prodotto',
    vatLine: '{{unit}} · IVA {{vat}}%',
    net: 'Netto',
    quantity: 'Quantità {{name}}',
    removeNamed: 'Rimuovi {{name}}',
    remove: 'Rimuovi',
    add: 'Aggiungi',
    productCount: '{{count}} prodotti',
    totalLine: 'Totale IVA inclusa: {{amount}}',
    createHint:
      'Scegli un destinatario, una data e almeno un prodotto con quantità positiva. L’orario finale deve essere successivo a quello iniziale.',
    createError: 'Impossibile creare l’ordine.',
    calendarTitle: 'Calendario ordini',
    otherDate: 'Altra data',
    loadPeriod: 'Carica un altro periodo',
    today: 'Oggi',
    previousDays: 'Scorri verso i giorni precedenti',
    nextDays: 'Scorri verso i giorni successivi',
    window: '(±20 giorni)',
    incoming: 'In arrivo',
    toReview: 'Da verificare',
    calendarTotals: 'Totali · tutti i fornitori',
    calendarError: 'Conteggi calendario non disponibili.',
    calendarDays: 'Giorni del calendario ordini',
    incomingSummary: '{{count}} in arrivo',
    reviewSummary: '{{count}} da verificare',
    processedSummary: '{{count}} processati',
    ordersSummary: '{{count}} ordini, dettaglio stati non disponibile',
    countsUnavailable: 'conteggi non disponibili',
    calendarHint:
      'Il numero sul giorno include ordini programmati, in arrivo e processati. Ricerca e fornitore filtrano l’elenco sottostante.',
    exportJson: 'Esporta JSON',
    importJson: 'Importa JSON',
    exported: 'File esportato.',
    importedCount: '{{count}} elementi importati.',
    exportError: 'Esportazione non riuscita. Riprova.',
    invalidJson: 'JSON non valido.',
    importProducts:
      'Inserisci un array di prodotti con nome, prezzo netto, IVA e unità di misura validi.',
    importSuppliers:
      'Inserisci un array di fornitori con un nome per ogni riga.',
    importError: 'Importazione non riuscita. Verifica i dati e riprova.',
    importTitle: 'Importa {{label}}',
    importAction: 'Importa',
    jsonFile: 'File JSON',
    fileTooLarge: 'Il file supera 5 MB.',
    fileReadError: 'Impossibile leggere il file.',
    importOrdersHint:
      'L’importazione crea nuovi ordini per la sede selezionata. Ripetere l’importazione crea altri ordini.',
    jsonArray: 'Array JSON',
    ordersLabel: 'ordini',
    productsLabel: 'prodotti',
    suppliersLabel: 'fornitori',
    statuses: {
      BOZZA: 'Bozza',
      PROGRAMMATO: 'Programmato',
      IN_PREPARAZIONE: 'In preparazione',
      IN_CONSEGNA: 'In consegna',
      DA_VERIFICARE: 'Da verificare',
      RICEVUTO: 'Ricevuto',
      NON_CONSEGNATO: 'Non consegnato',
      ANNULLATO: 'Annullato',
      INVIATO: 'Inviato',
      DA_CONFERMARE: 'Da confermare',
      CONSEGNATO: 'Consegnato',
    },
  },
  supplierOrderDetail: {
    matchProduct: 'Associa al prodotto',
    skipLine: 'Non applicare questa riga',
    duplicateMatch: 'Associa ogni riga a un prodotto diverso.',
    continue: 'Continua',
    category: 'Categoria',
    close: 'Chiudi',
    allCategories: 'Tutte le categorie',
    personalWhatsApp: 'WhatsApp personale',
    localWhatsApp: 'WhatsApp del locale',
    confirmSend: 'Inviare l’ordine al fornitore con questo canale?',
    confirmEdit: 'Salvare le nuove quantità dell’ordine?',
    checkQuantities: 'Controlla le quantità',
    alreadyDelivered: 'Merce già arrivata: passa alla verifica',
    createReview: 'Riepilogo ordine',
    discardDraft: 'Scartare i prodotti selezionati?',
    savedShare: 'Ordine salvato. Vuoi scegliere come inviarlo al fornitore?',
    title: 'Dettaglio ordine fornitore',
    orderTitle: 'Ordine #{{id}}',
    back: 'Ordini',
    unavailable: 'Ordine non disponibile.',
    requestedDelivery: 'Consegna richiesta',
    changeDate: 'Cambia data',
    orderTotal: 'Totale ordine IVA inclusa',
    netLine: 'Imponibile {{net}} · IVA {{vat}}',
    products: 'Prodotti ordinati',
    addReceived: 'Aggiungi prodotto ricevuto',
    verifyAll: 'Verifica tutti',
    ordered: 'Ordinata',
    sent: 'Inviata',
    unitNet: 'Netto unitario',
    received: 'Ricevuta',
    receivedPrice: 'Prezzo ricevuto netto',
    verified: 'Verificato',
    verifyNamed: 'Verifica {{name}}',
    receivedNamed: 'Ricevuta {{name}}',
    receivedPriceNamed: 'Prezzo ricevuto {{name}}',
    editOrder: 'Modifica ordine',
    prepare: 'Prepara ordine',
    dispatch: 'Conferma consegna',
    arrived: 'Consegna arrivata',
    cancelOrder: 'Annulla ordine',
    notDelivered: 'Non consegnato',
    receiptTitle: 'Verifica ricezione',
    invoiceTotal: 'Totale fattura IVA inclusa',
    receivedProductsTotal: 'Totale calcolato dai prodotti (IVA inclusa)',
    invoiceTotalHint: 'Importo registrato alla conferma della ricezione.',
    invoiceDraftHint:
      'Importo inserito per la ricezione; sarà salvato alla conferma.',
    insertedAmount: 'Totale inserito',
    expectedAmount: 'Previsto nell’ordine',
    recordedAmount: 'Totale registrato',
    receivedProductsTitle: 'Riepilogo prodotti ricevuti',
    lineTotal: 'Totale riga IVA inclusa',
    receivedLineCalculation: '{{quantity}} × {{price}} + IVA {{vat}}%',
    invoiceGoodsDifference: 'Differenza fattura − prodotti: {{amount}}',
    pendingVerification: 'Da verificare',
    verifiedProgress: '{{checked}} di {{total}} righe verificate',
    receiptBlockedTitle: 'Per abilitare “Conferma ricezione”:',
    receiptNeedsProducts: 'Aggiungi almeno un prodotto ricevuto.',
    receiptNeedsChecks:
      'Spunta la casella “Da verificare” nelle righe ancora da controllare: {{count}}.',
    receiptInvalidLines:
      'Correggi quantità o prezzi ricevuti non validi (devono essere pari o superiori a zero). Righe: {{count}}.',
    receiptInvalidInvoice:
      'Inserisci un totale fattura valido, pari o superiore a zero.',
    receiptSaving: 'Attendi il salvataggio dell’ordine.',
    receiptUploading: 'Attendi il caricamento della foto fattura.',
    receiptAnalyzing: 'Attendi il completamento dell’analisi della fattura.',
    goToVerification: 'Vai alla prossima riga da verificare',
    receiptReady:
      'Tutte le righe sono verificate. Puoi confermare la ricezione.',
    receiptTotalDifference:
      'Il totale fattura {{invoice}} è diverso dal totale prodotti ricevuti {{products}}. Controlla gli importi: questa differenza non blocca la conferma.',
    movedToHistory: 'Messaggio spostato in storico ordini',
    receiptHint:
      'Controlla quantità ricevuta e prezzo netto, poi spunta “Verificato” su ogni riga prodotto. Se modifichi quantità o prezzo, la verifica di quella riga viene rimossa.',
    confirmReceipt: 'Conferma ricezione',
    invoice: 'Fattura',
    invoiceAlt: 'Foto della fattura',
    invoicePhoto: 'Foto fattura',
    choosePhoto: 'Scegli file',
    ocrLabel: 'Note opzionali',
    ocrPlaceholder:
      'Note opzionali per l’analisi AI. Lascia vuoto per analizzare solo la foto.',
    ocrRunning: 'Analisi fattura con AI…',
    ocrFailed:
      'Analisi fattura non riuscita. Prova un’altra foto o aggiungi note.',
    clearPhotoAndText: 'Cancella foto e testo',
    readingPhotosTitle: 'Caricamento foto…',
    ocrProcessingTitle: 'Analisi fattura con AI…',
    ocrProcessingHint:
      'Lettura della foto sul server. Può richiedere alcuni secondi.',
    aiAnalyzingTitle: 'Analisi fattura con AI…',
    aiAnalyzingHint:
      'Lettura della foto sul server. Può richiedere alcuni secondi.',
    photoUploadingTitle: 'Salvataggio foto fattura…',
    photoUploadingHint: 'Caricamento dell’immagine prima dell’analisi.',
    analyze: 'Analizza fattura',
    reviewExtracted: 'Controlla i valori estratti prima di applicarli.',
    applyMatches: 'Applica ai prodotti corrispondenti',
    photoType: 'Carica un’immagine JPG o PNG fino a 5 MB.',
    imageInvalid: 'Immagine non valida.',
    imageReadError: 'Lettura immagine non riuscita.',
    photoSaved: 'Foto fattura salvata.',
    photoSaveError: 'Impossibile salvare la foto fattura.',
    extractError: 'Analisi fattura non riuscita.',
    communications: 'Comunicazioni al fornitore',
    configureMessages: 'Configura messaggi',
    communicationsError: 'Impossibile caricare le comunicazioni.',
    openSupplier: 'Apri pagina fornitore',
    recipientEmail: 'Email destinatario',
    sendEmail: 'Invia email',
    updated: 'Ordine aggiornato.',
    operationError: 'Operazione non riuscita.',
    sendFailed: 'Invio non riuscito.',
    messageSent: 'Messaggio inviato.',
    requestLogged:
      'Richiesta registrata. Verifica l’esito nelle comunicazioni.',
    communicationError: 'Comunicazione non riuscita.',
    saveDate: 'Salva data',
    invalidDate: 'Data non valida.',
    confirmTitle: 'Conferma operazione',
    confirmAction: 'Conferma',
    confirmBody: 'Aggiornare lo stato dell’ordine?',
    confirmReceive:
      'La ricezione verrà registrata con le quantità e i prezzi verificati.',
    unexpectedProduct: 'Prodotto ricevuto non previsto',
    catalogUnavailable: 'Catalogo non disponibile.',
  },
  supplierOrderHistory: {
    title: 'Storico ordini',
    subtitle: 'Ricerca gli acquisti per fornitore, prodotto e data di consegna',
    back: 'Ordini ai fornitori',
    dateRange: 'Date di consegna',
    load: 'Carica storico',
    loading: 'Caricamento…',
    reset: 'Azzera filtri',
    orders: 'Ordini',
    ordered: 'Ordinato',
    received: 'Ricevuto',
    vatProducts: 'IVA inclusa · prodotti filtrati',
    allResults: 'Tutti i risultati filtrati',
    search: 'Cerca per nome prodotto…',
    hint: 'Tutti gli stati, inclusi gli archiviati · Importi IVA inclusa',
    summaryError: 'Impossibile caricare il riepilogo.',
    dirty:
      'Filtri modificati: premi “Carica storico” per aggiornare i risultati e gli export.',
    loadError: 'Impossibile caricare lo storico.',
    results: 'Risultati della ricerca',
    resultsMeta: '{{count}} ordini · Export di tutti i risultati filtrati.',
    excel: 'Excel',
    pdf: 'PDF',
    exporting: 'Esportazione…',
    empty: 'Nessun ordine trovato con questi filtri',
    deliveryDate: 'Data consegna',
    products: 'Prodotti',
    filteredTotal: 'Prodotti filtrati · IVA inclusa',
    exported: 'Esportati {{orders}} ordini e {{rows}} righe prodotto.',
    exportError: 'Esportazione non riuscita.',
    allSuppliers: 'Tutti i fornitori',
    exportSheetProducts: 'Prodotti',
    exportSheetSearch: 'Ricerca',
    exportTitle: 'Storico ordini ai fornitori',
    exportOrder: 'Ordine',
    exportDelivery: 'Data consegna',
    exportSupplier: 'Fornitore',
    exportSupplierCode: 'Codice fornitore',
    exportStatus: 'Stato',
    exportProduct: 'Prodotto',
    exportUnit: 'Unità',
    exportOrderedQty: 'Quantità ordinata',
    exportSentQty: 'Quantità inviata',
    exportReceivedQty: 'Quantità ricevuta',
    exportOrderedNet: 'Prezzo netto ordinato',
    exportReceivedNet: 'Prezzo netto ricevuto',
    exportVat: 'IVA %',
    exportOrderedTotal: 'Totale ordinato IVA inclusa',
    exportReceivedTotal: 'Totale ricevuto IVA inclusa',
    exportQtyChange: 'Variazione quantità',
    exportPriceChange: 'Variazione prezzo',
    exportYes: 'Sì',
    exportNo: 'No',
    exportBranch: 'Sede',
    exportDeliveryFrom: 'Data consegna dal',
    exportDeliveryTo: 'Data consegna al',
    exportProductName: 'Nome prodotto',
    exportAllProducts: 'Tutti',
    exportStatuses: 'Stati',
    exportAllStatuses: 'Tutti, inclusi archiviati',
    exportOrderCount: 'Totale ordini',
    exportOrderedAmount: 'Importo ordinato IVA inclusa',
    exportReceivedAmount: 'Importo ricevuto IVA inclusa',
    exportProductRows: 'Righe prodotto',
    exportPage:
      'Pagina {{page}} di {{pages}} · {{orders}} ordini · {{rows}} righe prodotto',
  },
  suppliersPage: {
    title: 'Fornitori',
    subtitle: 'Contatti e cataloghi per gli ordini della sede',
    create: 'Nuovo fornitore',
    active: 'Attivi',
    archived: 'Archiviati',
    search: 'Cerca fornitore, email o partita IVA…',
    noBranch: 'Seleziona una sede.',
    loadError: 'Impossibile caricare i fornitori.',
    supplier: 'Fornitore',
    contacts: 'Contatti',
    catalog: 'Catalogo',
    openCatalog: 'Apri catalogo',
    productCount: '{{count}} prodotti',
    vatLine: 'P. IVA {{vat}}',
    editNamed: 'Modifica {{name}}',
    archiveNamed: 'Archivia {{name}}',
    restoreNamed: 'Ripristina {{name}}',
    empty: 'Nessun fornitore trovato.',
    archiveTitle: 'Archivia fornitore',
    restoreTitle: 'Ripristina fornitore',
    archiveAction: 'Archivia',
    restoreAction: 'Ripristina',
    archiveDesc:
      'Archiviare {{name}}? Il catalogo e lo storico degli ordini verranno conservati. Il fornitore non sarà selezionabile per nuovi ordini fino al ripristino.',
    restoreDesc: 'Ripristinare {{name}} tra i fornitori attivi?',
    saveError: 'Impossibile salvare il fornitore. Riprova.',
    archiveError:
      'Impossibile aggiornare l’archiviazione del fornitore. Riprova.',
    editTitle: 'Modifica fornitore',
    groupContacts: 'Dati e contatti',
    groupFiscal: 'Dati fiscali',
    groupAddress: 'Indirizzo',
    legalName: 'Ragione sociale',
    email: 'Email',
    phone: 'Telefono',
    pec: 'PEC',
    vat: 'Partita IVA',
    taxCode: 'Codice fiscale',
    street: 'Indirizzo',
    city: 'Città',
    postcode: 'CAP',
    country: 'Paese',
    fromInvoice: 'Da fattura',
    invoiceTitle: 'Fornitore da fattura',
    createSupplier: 'Crea fornitore',
    invoicePhoto: 'Foto della fattura',
    photosSelected: '{{count}} immagini selezionate',
    removePhotos: 'Rimuovi foto',
    readingPhotos: 'Lettura immagini…',
    tooManyPhotos:
      'Seleziona fino a 5 immagini JPG o PNG, massimo 5 MB ciascuna.',
    invalidImage: 'Formato immagine non valido.',
    imageReadError: 'Impossibile leggere la foto.',
    photosReadError: 'Impossibile leggere le immagini. Riprova.',
    analyze: 'Analizza',
    existing: 'Fornitori già presenti',
    analyzeError: 'Impossibile analizzare la fattura.',
    createError: 'Impossibile creare il fornitore.',
  },
  menu: {
    ingredientsPage: {
      title: 'Ingredienti',
      subtitle: 'Ingredienti a scelta, categorie e supplementi',
      search: 'Cerca ingrediente…',
      createIngredient: 'Crea ingrediente',
      createCategory: 'Crea categoria',
      ingredients: 'Ingredienti',
      categories: 'Categorie',
      loadError: 'Impossibile caricare il catalogo.',
      operationError: 'Operazione non riuscita. Riprova.',
      filterCategory: 'Filtra categoria',
      allCategories: 'Tutte le categorie',
      pageHint:
        'Le categorie raggruppano gli ingredienti di questa pagina. Una categoria può continuare nella pagina successiva.',
      available: 'Disponibile',
      unavailable: 'Disabilitato',
      editNamed: 'Modifica {{name}}',
      deleteNamed: 'Elimina {{name}}',
      emptyCategory: 'Nessun ingrediente in questa categoria.',
      emptyPage:
        'Nessun ingrediente in questa pagina. Cambia ricerca o torna alla pagina precedente.',
      perPage: 'Ingredienti per pagina',
      createCategoryFirst: 'Crea una categoria per iniziare.',
      categoryOrder:
        'Ordine del catalogo. Ogni ricetta può personalizzare l’ordine delle proprie categorie.',
      moveUp: 'Sposta su {{name}}',
      moveDown: 'Sposta giù {{name}}',
      newIngredient: 'Nuovo ingrediente',
      editIngredient: 'Modifica ingrediente',
      newCategory: 'Nuova categoria',
      editCategory: 'Modifica categoria',
      ingredientDesc: 'Nome, categoria e supplemento.',
      categoryDesc: 'Nome e traduzioni della categoria.',
      name: 'Nome *',
      category: 'Categoria *',
      select: 'Seleziona',
      supplement: 'Supplemento € *',
      description: 'Descrizione',
      translations: 'Traduzioni',
      english: 'English',
      spanish: 'Español',
      french: 'Français',
      deleteTitle: 'Elimina dal catalogo',
      deleteDesc:
        'Eliminare “{{name}}”? Gli ingredienti associati ai piatti e le categorie non vuote sono protetti.',
      delete: 'Elimina',
    },
    recipeEditor: {
      createTitle: 'Crea ricetta',
      tabsLabel: 'Ricetta e traduzioni',
      configurationTab: 'Ricetta',
      basePrice: 'Prezzo base',
      dishName: 'Nome piatto',
      typeTitle: 'Tipo di piatto',
      simpleTitle: 'Ricetta semplice',
      simpleDesc: 'Nome, descrizione e allergeni',
      customizableTitle: 'Ricetta componibile',
      customizableDesc: 'Base + ingredienti a scelta',
      compositeTitle: 'Menu composto',
      compositeDesc: 'Piatti semplici a prezzo fisso',
      wineTitle: 'Vino / Champagne',
      wineDesc: 'Bottiglia, calice e caratteristiche',
      includedDishes: 'Piatti inclusi',
      searchDishes: 'Cerca piatto…',
      includedSections: 'Sezione dei piatti inclusi',
      allSections: 'Tutte le sezioni',
      selectionHint:
        '{{count}} piatti selezionati. Le selezioni rimangono quando cambi pagina o ricerca.',
      cannotInclude: '{{reason}}: non può essere incluso',
      weightedDish: 'Prezzo a peso',
      noDishes: 'Nessun piatto trovato.',
      dishesPerPage: 'Piatti per pagina',
      range: '{{from}}–{{to}} di {{total}}',
      previous: 'Precedente',
      next: 'Successiva',
      minTwo: 'Seleziona almeno due piatti semplici.',
      selectableIngredients: 'Ingredienti selezionabili',
      minimumTotal: 'Minimo totale',
      maximumTotal: 'Massimo totale',
      searchIngredients: 'Cerca ingrediente…',
      ingredientLoadError: 'Caricamento ingredienti fallito. Riprova',
      disabled: 'Disabilitato',
      ingredientPage: '{{total}} ingredienti · pagina {{page}}',
      categoryFilter: 'Categoria',
      allCategories: 'Tutte le categorie',
      noIngredients: 'Nessun ingrediente trovato.',
      categoryOrderHelp:
        'Usa le frecce per decidere in quale ordine il cliente vedrà le categorie mentre compone la ricetta. Compaiono solo le categorie degli ingredienti selezionati; spostarle non modifica ingredienti o prezzi.',
      categoryOrder: 'Ordine delle categorie nella ricetta',
      moveEarlier: 'Sposta prima {{name}}',
      moveLater: 'Sposta dopo {{name}}',
      availability: 'Disponibilità e prezzi',
      restaurantMenu: 'Menu ristorante',
      takeaway: 'Asporto',
      delivery: 'Delivery',
      enabled: 'Abilitato',
      channelPrice: 'Prezzo {{channel}} (€)',
      invalidChoices:
        'Seleziona gli ingredienti e controlla il minimo/massimo delle scelte.',
      invalidPrices: 'Prezzi non validi: massimo due decimali.',
      invalidComposition:
        'Piatti componibili e menu composti richiedono un prezzo fisso e non possono essere vini.',
      invalidForm: 'Controlla nome, prezzo e campi obbligatori.',
      numericError: 'Inserisci un numero valido.',
      nonNegativeError: 'Il valore deve essere maggiore o uguale a zero.',
      wineRed: 'Rosso',
      wineWhite: 'Bianco',
      wineRose: 'Rosato',
      wineChampagne: 'Champagne',
      wineSparkling: 'Spumante',
      wineOther: 'Altro',
    },
    title: 'Menu',
    disabled: {
      badge: 'Sezione non abilitata',
      title: 'Questa sezione non è abilitata',
      description:
        'Questa funzione è attualmente disabilitata per il tuo account. Contatta il supporto per saperne di più.',
      infoTitle: 'Hai bisogno di accedere a questa sezione?',
      infoDescription:
        'Contatta il nostro team di supporto e ti aiuteremo a iniziare.',
      footerHelp: 'Hai ancora bisogno di aiuto?',
      footerHelpDesc:
        'Il nostro team di supporto è disponibile 24/7 per assisterti.',
    },
    subtitleDefault: 'Visualizza e gestisci i tuoi menu digitali',
    statsCount: '{{count}} menu',
    searchPlaceholder: 'Cerca menu per nome...',
    menuName: 'Nome menu',
    menuNamePlaceholder: 'Inserisci il nome del menu',
    description: 'Descrizione',
    descriptionPlaceholder: 'Inserisci la descrizione del menu',
    newMenu: 'Nuovo menu',
    createTitle: 'Crea nuovo menu',
    createAction: 'Crea',
    url: 'URL',
    qrCode: 'Codice QR',
    preview: 'Anteprima',
    copyUrl: 'Copia URL',
    editMenu: 'Modifica menu',
    deleteMenu: 'Elimina menu',
    duplicate: 'Duplica',
    close: 'Chiudi',
    saveChanges: 'Salva',
    noBranch: 'Seleziona una sede per visualizzare i menu',
    empty: 'Nessun menu trovato',
    emptyDesc: 'Non ci sono ancora menu per questa sede.',
    validation: {
      nameRequired: 'Il nome del menu è obbligatorio',
      nameMax: 'Il nome del menu non può superare i 100 caratteri',
      descriptionMax: 'La descrizione non può superare i 500 caratteri',
    },
    deleteModal: {
      title: 'Eliminare il menu?',
      description:
        'Sei sicuro di voler eliminare questo menu? Questa azione non può essere annullata.',
    },
    toast: {
      createSuccess: 'Menu creato con successo.',
      createError: 'Creazione del menu non riuscita. Riprova.',
      updateSuccess: 'Menu aggiornato con successo.',
      updateError: 'Aggiornamento del menu non riuscito. Riprova.',
      deleteSuccess: 'Menu eliminato con successo.',
      deleteError: 'Eliminazione del menu non riuscita. Riprova.',
      duplicateSuccess: 'Menu duplicato con successo.',
      duplicateError: 'Duplicazione del menu non riuscita. Riprova.',
      logoUploadSuccess: 'Logo caricato con successo.',
      logoUploadError: 'Caricamento del logo non riuscito.',
      logoRemoveSuccess: 'Logo rimosso con successo.',
      logoRemoveError: 'Rimozione del logo non riuscita.',
      backgroundUploadSuccess: 'Sfondo caricato con successo.',
      backgroundUploadError: 'Caricamento dello sfondo non riuscito.',
      backgroundRemoveSuccess: 'Sfondo rimosso con successo.',
      backgroundRemoveError: 'Rimozione dello sfondo non riuscita.',
      templateUpdateSuccess: 'Modello aggiornato con successo.',
      templateUpdateError: 'Aggiornamento del modello non riuscito.',
    },
    edit: {
      pageTitle: 'Modifica dettagli menu',
      subtitle: 'Configura contenuto, stile e sezioni del menu.',
      tabListLabel: 'Schede modifica menu',
      withPhoto: 'con foto',
      withoutPhoto: 'senza foto',
      uploading: 'Caricamento...',
      removing: 'Rimozione...',
      tabs: {
        titleDescription: 'Titolo e descrizione',
        menuStyle: 'Stile menu',
        sectionsRecipes: 'Sezioni e ricette',
      },
      informationTitle: 'Informazioni menu',
      informationDesc: 'Configura nome del menu e traduzioni.',
      languages: {
        italian: 'Italiano',
        italianMain: 'Lingua principale',
        english: 'Inglese',
        englishTranslation: 'Traduzione inglese',
        spanish: 'Spagnolo',
        spanishTranslation: 'Traduzione spagnola',
        french: 'Francese',
        frenchTranslation: 'Traduzione francese',
      },
      descriptions: {
        italian: 'Descrizione menu',
        english: 'Descrizione inglese',
        spanish: 'Descrizione spagnola',
        french: 'Descrizione francese',
      },
      style: {
        appearanceTitle: 'Aspetto del menu',
        appearanceDesc: 'Carica logo e immagine di sfondo per il tuo menu.',
        logo: 'Seleziona logo del menu',
        logoHint: 'Consigliamo un logo quadrato (PNG), max 5MB.',
        background: 'Seleziona immagine di sfondo del menu',
        backgroundHint: "Consigliamo un'immagine orizzontale, max 5MB.",
        removeImage: 'Rimuovi immagine',
        templatesTitle: 'Seleziona lo stile da applicare al menu',
        templatesDesc: 'Scegli un modello per la presentazione del menu.',
        previewStyleMenu: 'Anteprima stile menu',
        select: 'Seleziona',
        selected: 'Selezionato',
        active: 'Attivo',
      },
    },
    sections: {
      title: 'Sezioni e ricette',
      description: 'Organizza il menu in sezioni e aggiungi ricette.',
      addSection: 'Crea nuova sezione',
      searchPlaceholder:
        'Cerca sezioni o ricette per nome, descrizione o codice...',
      noSearchResults: 'Nessuna sezione o ricetta corrispondente',
      name: 'Nome sezione',
      namePlaceholder: 'Inserisci il nome della sezione',
      descriptionPlaceholder: 'Inserisci la descrizione della sezione',
      createTitle: 'Crea sezione nel menu {{menu}}',
      createAction: 'Crea',
      editTitle: 'Modifica elemento menu',
      editDesc: 'Gestisci contenuti, traduzioni e ingredienti',
      dishDrink: 'Piatto/Bevanda',
      missingTranslations: 'Traduzioni mancanti',
      missingTranslationsTooltip:
        'Nome o descrizione in questa sezione non sono stati tradotti in tutte le lingue previste dal menu; in caso di cambio lingua verrà mostrata la versione italiana',
      addRecipe: 'Aggiungi ricetta/prodotto a {{section}}',
      sortAlphabetically: 'Ordina ricette A-Z',
      moveLeft: 'Sposta sezione a sinistra',
      moveRight: 'Sposta sezione a destra',
      settings: 'Impostazioni sezione',
      delete: 'Elimina sezione',
      menuPrice: 'Prezzo menu',
      sectionVisibility: 'Sezione visibile',
      sectionVisibilityHint:
        'Mostra questa sezione nel menu ristorante, delivery e asporto.',
      sectionHidden: 'Sezione nascosta',
      hideSectionPrices: 'Nascondi prezzi sezione',
      showSectionPrices: 'Mostra prezzi sezione',
      visibleSection: 'Sezione visibile',
      visibility: 'Mostra / nascondi sezione',
      fixedMenuPrice: 'MENU FISSO ({{price}}€)',
      empty: 'Nessuna sezione ancora',
      emptyDesc: 'Usa il pulsante più per creare la prima sezione.',
      emptyRecipes: 'Nessuna ricetta aggiunta a questa sezione.',
      recipeCount: '{{count}} ricette in questa sezione',
      deleteModal: {
        title: 'Eliminare la sezione?',
        description:
          'Sei sicuro di voler eliminare questa sezione? Questa azione non può essere annullata.',
      },
      validation: {
        nameRequired: 'Il nome della sezione è obbligatorio',
        nameMax: 'Il nome della sezione non può superare i 100 caratteri',
        descriptionMax: 'La descrizione non può superare i 500 caratteri',
      },
      toast: {
        createSuccess: 'Sezione creata con successo.',
        createError: 'Creazione della sezione non riuscita. Riprova.',
        updateSuccess: 'Sezione aggiornata con successo.',
        updateError: 'Aggiornamento della sezione non riuscito. Riprova.',
        deleteSuccess: 'Sezione eliminata con successo.',
        deleteError: 'Eliminazione della sezione non riuscita. Riprova.',
        sortSuccess: 'Ricette ordinate in ordine alfabetico.',
        sortError: 'Ordinamento delle ricette non riuscito. Riprova.',
      },
    },
    recipes: {
      editTitle: 'Modifica elemento menu',
      editDesc: 'Gestisci contenuti, traduzioni e ingredienti',
      imageTitle: 'Immagine',
      productPhoto: 'Foto prodotto',
      winePhoto: 'Foto vino',
      wineChampagne: 'Vino/Champagne',
      imageHintCreate:
        'Seleziona un’immagine da caricare. Puoi aggiungere il nome della ricetta prima di salvare.',
      generalTitle: 'Informazioni generali',
      generalDesc: 'Nome, descrizione e configurazione',
      priceSection: 'Prezzo',
      priceHint: 'Inserisci il prezzo finale del prodotto',
      price: 'Prezzo €',
      bottlePrice: 'Prezzo bottiglia',
      priceBottleLabel: 'Prezzo bottiglia (€)',
      chalice: 'Calice',
      pricePerGlass: 'Prezzo al calice',
      alcoholPercentage: 'Alcol (%) - Es: 14',
      wineType: 'Tipologia di vino',
      wineTypePlaceholder:
        'Tipologia di vino (es. Rosso, Bianco, Rosato, Champagne...)',
      blend: 'Uvaggio',
      blendPlaceholder:
        'Uvaggio (es. 50% Negroamaro, 20% Nero di Troia, 30% Malvasia Nera)',
      yearOfProduction: 'Anno di produzione',
      cellar: 'Cantina',
      countryRegion: 'Paese/Regione',
      provenance: 'Provenienza',
      pricePerWeight:
        'Il prezzo è indicato per unità di peso (es. kg, litri) anziché per porzione?',
      unitOfMeasurement: 'Unità di misura',
      unitPlaceholder: 'Unità di misura (es: Kg, Lt, Pz)',
      assignAllergens: 'Assegna allergeni',
      productConfiguration: 'Configurazione prodotto',
      allergensTitle: 'Allergeni',
      bestSeller: 'Best seller',
      bestSellerDesc:
        'Metti in evidenza questo prodotto tra i più ordinati e rendilo visivamente più importante nel menu.',
      available: 'Disponibile',
      availableDesc:
        'Mostra questa ricetta ai clienti nel menu digitale e rendila ordinabile.',
      notAvailable: 'Non disponibile',
      active: 'Attivo',
      visible: 'Visibile',
      delete: 'Elimina ricetta/prodotto',
      dragHandle: 'Trascina per riordinare la ricetta',
      deleteModal: {
        title: 'Eliminare la ricetta?',
        description:
          'Sei sicuro di voler eliminare questa ricetta? Questa azione non può essere annullata.',
      },
      validation: {
        nameRequired: 'Il nome della ricetta è obbligatorio',
      },
      toast: {
        createSuccess: 'Ricetta creata con successo.',
        createError: 'Creazione della ricetta non riuscita. Riprova.',
        updateSuccess: 'Ricetta aggiornata con successo.',
        updateError: 'Aggiornamento della ricetta non riuscito. Riprova.',
        deleteSuccess: 'Ricetta eliminata con successo.',
        deleteError: 'Eliminazione della ricetta non riuscita. Riprova.',
        moveSuccess: 'Ordine delle ricette aggiornato.',
        moveError:
          'Aggiornamento dell’ordine delle ricette non riuscito. Riprova.',
        saveBeforeImage: 'Salva la ricetta prima di caricare un’immagine.',
        imageUploadSuccess: 'Immagine della ricetta caricata con successo.',
        imageUploadError:
          'Caricamento dell’immagine della ricetta non riuscito.',
        imageRemoveSuccess: 'Immagine della ricetta rimossa con successo.',
        imageRemoveError: 'Rimozione dell’immagine della ricetta non riuscita.',
      },
    },
    allergens: {
      cereals: 'Cereali',
      gluten: 'Glutine',
      crustaceans: 'Crostacei',
      molluscs: 'Molluschi',
      fish: 'Pesce',
      eggs: 'Uova',
      peanuts: 'Arachidi',
      soybean: 'Soia',
      mushrooms: 'Funghi',
      nuts: 'Frutta a guscio',
      celery: 'Sedano',
      mustard: 'Senape',
      sesame: 'Sesamo',
      sulfur: 'Anidride solforosa',
      lupins: 'Lupini',
      lactose: 'Lattosio',
      corn: 'Mais',
      pork: 'Maiale',
      beef: 'Manzo',
      poultry: 'Carne di pollame',
      nickel: 'Nichel',
      legumes: 'Legumi',
      citrus: 'Agrumi',
      cacao: 'Cacao',
      honey: 'Miele',
    },
    translate: {
      action: 'Traduci',
      progressTitle: 'Traduzione in corso...',
      progressDesc: 'Attendi il completamento delle traduzioni.',
      translationsTitle: 'Traduzioni',
      success: 'Traduzioni applicate con successo.',
      error: 'Traduzione non riuscita. Riprova.',
    },
    previewPage: {
      pageTitle: 'Anteprima del menu',
      loading: 'Caricamento menu',
      menuLabel: 'Menu',
      sectionLabel: 'Sezione',
      priceLabel: 'Prezzo',
      noPhoto: 'Nessuna foto disponibile',
      bestSeller: 'Best',
      allergensTitle: 'Allergeni',
      poweredBy: 'Offerto da PRO20',
      scrollCue: 'Scorri al menu',
      storyContinue: 'Menu',
      close: 'Chiudi',
      wine: {
        grape: 'Vitigno',
        cellar: 'Cantina',
        year: 'Anno',
        region: 'Regione',
        alcohol: 'Alcol',
        glass: 'Calice',
        bottle: 'Bottiglia',
      },
    },
  },
  landingPages: {
    title: 'Landing Page',
    subtitle: 'Elenco Landing Page per {{branch}}',
    subtitleDefault: 'Visualizza e gestisci l’elenco delle Landing Page',
    searchPlaceholder: 'Cerca landing page per nome',
    searchPlaceholder2: 'Inserisci il nome della landing page',
    landingPageName: 'Nome Landing Page',
    itemsPerPage: 'Elementi per pagina',
    range: '{{start}} - {{end}} di {{total}}',
    noBranch: 'Seleziona una filiale per visualizzare le landing page',
    empty: 'Nessuna landing page trovata',
    emptyDesc: 'Non ci sono ancora landing page per questa filiale.',
    addLandingPage: 'Aggiungi Landing Page',
    newLanding: 'Nuova Landing Page',
    newLandingDesc: 'Crea una nuova landing page per la tua filiale.',
    createLanding: 'Crea landing page',
    statsCount: '{{count}} Landing Page',
    present: 'presenti',
    url: 'URL',
    qrCode: 'Codice QR',
    preview: 'Anteprima',
    showPreview: 'Mostra anteprima',
    copyUrl: 'Copia URL',
    editLanding: 'Modifica Landing Page',
    deleteLanding: 'Elimina Landing Page',
    duplicate: 'Duplica',
    validation: {
      landingPageNameMin:
        'Il nome della landing page deve contenere almeno 2 caratteri',
      landingPageNameMax:
        'Il nome della landing page non può superare i 50 caratteri',
    },
    deleteModal: {
      title: 'Eliminare la landing page?',
      description:
        'Sei sicuro di voler eliminare questa landing page? Questa azione non può essere annullata.',
    },
    toast: {
      createSuccess: 'Landing page creata con successo.',
      createError: 'Impossibile creare la landing page. Riprova.',
      deleteSuccess: 'Landing page eliminata con successo.',
      deleteError: 'Impossibile eliminare la landing page. Riprova.',
      duplicateSuccess: 'Landing page duplicata con successo.',
      duplicateError: 'Duplicazione della landing page non riuscita. Riprova.',
      updateSuccess: 'Landing page aggiornata con successo.',
      updateError: 'Impossibile aggiornare la landing page. Riprova.',
      logoUploadSuccess: 'Logo caricato con successo.',
      logoUploadError: 'Impossibile caricare il logo.',
      logoRemoveSuccess: 'Logo rimosso con successo.',
      logoRemoveError: 'Impossibile rimuovere il logo.',
      backgroundUploadSuccess: 'Sfondo caricato con successo.',
      backgroundUploadError: 'Impossibile caricare lo sfondo.',
      backgroundRemoveSuccess: 'Sfondo rimosso con successo.',
      backgroundRemoveError: 'Impossibile rimuovere lo sfondo.',
      buttonSaveSuccess: 'Pulsante salvato con successo.',
      buttonSaveError: 'Impossibile salvare il pulsante.',
      buttonDeleteSuccess: 'Pulsante eliminato con successo.',
      buttonDeleteError: 'Impossibile eliminare il pulsante.',
    },
    edit: {
      pageTitle: 'Modifica Landing Page',
      subtitle: 'Configura media e pulsanti di azione.',
      formConfiguration: 'Configurazione landing page',
      formConfigurationDesc: 'Configura media e pulsanti di azione.',
      linkForm: 'Link landing page',
      close: 'Chiudi',
      mediaTitle: 'Media',
      mediaDescription:
        'Carica un logo e un’immagine di sfondo per la tua landing page.',
      detailsTitle: 'Dettagli',
      logo: 'Logo',
      background: 'Immagine di sfondo',
      imageUpload: 'Clicca o trascina per caricare un’immagine',
      removeImage: 'Rimuovi immagine',
      uploading: 'Caricamento...',
      removing: 'Rimozione...',
      buttons: {
        title: 'Pulsanti Landing',
        description:
          'Aggiungi pulsanti call-to-action sulla landing page. Usa l’icona ✓ per salvare ogni riga.',
        text: 'Testo pulsante',
        link: 'Link',
        color: 'Sfondo',
        textColor: 'Colore testo',
        add: 'Aggiungi pulsante',
        save: 'Salva pulsante',
        delete: 'Elimina pulsante',
        empty: 'Nessun pulsante. Clicca + per aggiungerne uno.',
        textPlaceholder: 'es. Prenota ora',
        linkPlaceholder: 'https://',
        validationRequired: 'Testo e link del pulsante sono obbligatori.',
      },
    },
    public: {
      documentTitle: 'Landing page',
      loading: 'Caricamento landing page...',
      unavailableTitle: 'Landing page non disponibile',
      unavailableDesc:
        'Questa landing page non è stata trovata o non è più disponibile.',
      buttonFallback: 'Apri',
    },
  },
  tags: {
    title: 'Tag',
    titleWithBranch: 'Tag {{branch}}',
    pageDescription: 'Nessun tag presente',
    subtitle: 'Elenco tag per {{branch}}',
    subtitleDefault: 'Visualizza e gestisci l’elenco dei tag',
    searchPlaceholder: 'Cerca tag per nome',
    searchPlaceholder2: 'Inserisci il nome del tag',
    tagName: 'Nome del tag',
    itemsPerPage: 'Elementi per pagina',
    range: '{{start}} - {{end}} di {{total}}',
    noBranch: 'Seleziona una filiale per visualizzare i tag',
    empty: 'Nessun tag presente',
    emptyDesc:
      'Crea il primo tag per organizzare meglio clienti, prenotazioni e segmentazioni marketing.',
    addTag: 'Aggiungi tag',
    createTitle: 'Aggiungi tag',
    createDesc: 'Crea un nuovo tag per la tua filiale.',
    editTitle: 'Modifica tag',
    editDesc: 'Aggiorna il nome del tag.',
    editTag: 'Modifica tag',
    duplicate: 'Duplica',
    typeLabel: 'TAG',
    statsCount: '{{count}} tag presenti',
    updatedAgo: 'Aggiornato 2 minuti fa',
    creating: 'Creazione...',
    saving: 'Salvataggio...',
    saveChanges: 'Salva modifiche',
    toast: {
      createSuccess: 'Tag creato con successo.',
      createError: 'Impossibile creare il tag. Riprova.',
      updateSuccess: 'Tag aggiornato con successo.',
      updateError: 'Impossibile aggiornare il tag. Riprova.',
      deleteSuccess: 'Tag eliminato con successo.',
      deleteError: 'Impossibile eliminare il tag. Riprova.',
      duplicateSuccess: 'Tag duplicato con successo.',
      duplicateError: 'Duplicazione del tag non riuscita. Riprova.',
    },
    validation: {
      tagNameMin: 'Il nome del tag deve contenere almeno 2 caratteri',
      tagNameMax: 'Il nome del tag non può superare i 50 caratteri',
    },
    deleteTag: 'Elimina tag',
    deleteModel: {
      deleteTitle: 'Eliminare il tag?',
      deleteDescription:
        'Sei sicuro di voler eliminare questo tag? Questa azione non può essere annullata.',
    },
  },
  publicRelations: {
    title: 'Relazioni Pubbliche',
    titleWithBranch: 'PR {{branch}}',
    pageDescription: 'Nessuna PR presente',
    subtitle: 'Elenco PR per {{branch}}',
    subtitleDefault: 'Visualizza e gestisci l’elenco PR',
    searchPlaceholder: 'Cerca PR per nome',
    searchPlaceholder2: 'Inserisci il nome della PR',
    prName: 'Nome PR',
    itemsPerPage: 'Elementi per pagina',
    range: '{{start}} - {{end}} di {{total}}',
    noBranch: 'Seleziona una filiale per visualizzare le PR',
    empty: 'Nessuna PR presente',
    emptyDesc:
      'Crea la prima PR per organizzare referral, collaborazioni e gestione clienti.',
    addPr: 'Aggiungi PR',
    createTitle: 'Aggiungi PR',
    createDesc:
      'Crea un nuovo contatto relazioni pubbliche per la tua filiale.',
    editTitle: 'Modifica PR',
    editDesc: 'Aggiorna il nome del contatto relazioni pubbliche.',
    editPr: 'Modifica PR',
    duplicate: 'Duplica',
    typeLabel: 'PR MANAGER',
    statsCount: '{{count}} PR presenti',
    updatedAgo: 'Aggiornato 2 minuti fa',
    creating: 'Creazione...',
    saving: 'Salvataggio...',
    saveChanges: 'Salva modifiche',
    toast: {
      createSuccess: 'PR creata con successo.',
      createError: 'Impossibile creare la PR. Riprova.',
      updateSuccess: 'PR aggiornata con successo.',
      updateError: 'Impossibile aggiornare la PR. Riprova.',
      deleteSuccess: 'PR eliminata con successo.',
      deleteError: 'Impossibile eliminare la PR. Riprova.',
      duplicateSuccess: 'PR duplicata con successo.',
      duplicateError: 'Duplicazione della PR non riuscita. Riprova.',
    },
    validation: {
      prNameMin: 'Il nome della PR deve contenere almeno 2 caratteri',
      prNameMax: 'Il nome della PR non può superare i 50 caratteri',
    },
    deletePR: 'Elimina PR',
    deleteModel: {
      deleteTitle: 'Eliminare la PR?',
      deleteDescription:
        'Sei sicuro di voler eliminare questa PR? Questa azione non può essere annullata.',
    },
  },
  staff: {
    collaborators: {
      title: 'Collaboratori',
      subtitle: 'Collaboratori per {{branch}}',
      subtitleDefault: 'Visualizza e gestisci l’elenco collaboratori',
      searchPlaceholder:
        'Cerca collaboratori per nome, cognome, email, numero di telefono o ruolo',
      firstName: 'Nome',
      lastName: 'Cognome',
      firstNamePlaceholder: 'Inserisci il nome',
      lastNamePlaceholder: 'Inserisci il cognome',
      email: 'Email',
      phone: 'Numero di Telefono',
      role: 'Ruolo',
      itemsPerPage: 'Elementi per pagina',
      viewDetails: 'Visualizza Dettagli',
      sharedCredentials: 'Credenziali Condivise',
      addCollaborator: 'Aggiungi Collaboratore',
      editCollaborator: 'Modifica Collaboratore',
      deleteCollaborator: 'Elimina Collaboratore',
      changePassword: {
        action: 'Cambia password',
        title: 'Cambia password',
        subtitle: 'Imposta una nuova password per {{name}}',
        newPassword: 'Nuova password',
        newPasswordPlaceholder: 'Inserisci password di 6 cifre',
        confirmPassword: 'Conferma password',
        confirmPasswordPlaceholder: 'Conferma password di 6 cifre',
        submit: 'Salva password',
        validation: {
          passwordDigits: 'La password deve essere di esattamente 6 cifre',
          passwordMismatch: 'Le password non coincidono',
        },
      },
      range: '{{start}} - {{end}} di {{total}}',
      noBranch: 'Seleziona una filiale per visualizzare i collaboratori',
      empty: 'Nessun collaboratore trovato',
      emptyDesc: 'Non ci sono ancora collaboratori per questa filiale.',
      emailPlaceholder: 'Inserisci l’email',
      phonePlaceholder: 'Inserisci il numero di telefono',
      username: 'Username',
      usernamePlaceholder: 'Inserisci lo username',
      password: 'Password',
      passwordPlaceholder: 'Inserisci la password',
      gender: 'Genere',
      selectGender: 'Seleziona genere',
      male: 'Uomo',
      female: 'Donna',
      selectRole: 'Seleziona ruolo',
      remunerationType: 'Tipo retribuzione',
      selectRemunerationType: 'Seleziona tipo retribuzione',
      hourly: 'Oraria',
      daily: 'Giornaliera',
      monthly: 'Mensile',
      sendCredentials: 'Invia credenziali',
      roles: {
        receptionist: 'Accoglienza',
        assistantCook: 'Aiuto cuoco',
        administrator: 'Amministratore',
        bartender: 'Barman',
        chef: 'Cuoco',
        dishwasher: 'Lavapiatti',
        pizzaChef: 'Pizzaiolo',
        director: 'Direttore',
        waiter: 'Cameriere',
        publicRelations: 'Public relations',
        deputyDirector: 'Vice direttore',
      },
      validation: {
        firstNameMin: 'Il nome deve contenere almeno 2 caratteri',
        lastNameMin: 'Il cognome deve contenere almeno 2 caratteri',
        firstNameMax: 'Il nome non può superare i 40 caratteri',
        lastNameMax: 'Il cognome non può superare i 40 caratteri',
        emailInvalid: 'Inserisci un indirizzo email valido',
        phoneInvalid: 'Il numero di telefono deve contenere da 6 a 15 cifre',
        countryCodeInvalid: 'Seleziona un prefisso internazionale valido',
        usernameMin: 'Lo username deve contenere almeno 4 caratteri',
        usernameMax: 'Lo username non può superare i 30 caratteri',
        passwordMin: 'La password deve contenere almeno 8 caratteri',
        passwordDigits: 'La password deve essere di esattamente 6 cifre',
        genderRequired: 'Seleziona un genere',
        roleRequired: 'Seleziona un ruolo',
        remunerationRequired: 'Seleziona un tipo di retribuzione',
      },
      deleteModel: {
        deleteTitle: 'Eliminare il collaboratore?',
        deleteDescription:
          'Sei sicuro di voler eliminare questo collaboratore? Questa azione non può essere annullata.',
      },
      collaboratorDetails: {
        title: 'Dettagli Collaboratore',
        subtitle:
          'Visualizza le informazioni del collaboratore e i dettagli lavorativi.',
        personalDetails: 'Dettagli Personali',
        employmentDetails: 'Dettagli Lavorativi',
        jobDescription: 'Mansione',
        dateOfBirth: 'Data di Nascita',
        branchCode: 'Codice Filiale',
        contractType: 'Tipo Contratto',
        startDateInduction: 'Data Inizio Induction',
        endDateInduction: 'Data Fine Induction',
        mobileDeviceAccessDetail: 'Dettaglio Accesso Dispositivo Mobile',
        uniqueId: 'ID Univoco',
        platform: 'Piattaforma',
        model: 'Modello',
        machine: 'Macchina',
        release: 'Release',
        fcmToken: 'Token FCM',
        registrationDate: 'Data Registrazione',
        noMobileDevices: 'Nessun dispositivo mobile registrato.',
        close: 'Chiudi',
        contractTypes: {
          determined: 'Determinato',
          indeterminate: 'Indeterminato',
        },
        visible: 'Visibile',
        fired: 'Licenziato',
        retribution: 'Retribuzione',
        yes: 'Sì',
        no: 'No',
      },
      toast: {
        createSuccess: 'Collaboratore creato con successo.',
        createError: 'Creazione del collaboratore non riuscita. Riprova.',
        updateSuccess: 'Collaboratore aggiornato con successo.',
        updateError: 'Aggiornamento del collaboratore non riuscito. Riprova.',
        deleteSuccess: 'Collaboratore eliminato con successo.',
        deleteError: 'Eliminazione del collaboratore non riuscita. Riprova.',
        sendCredentialsSoon:
          'L’invio delle credenziali sarà disponibile a breve.',
        sendCredentialsSuccess: 'Credenziali inviate con successo.',
        sendCredentialsError: 'Invio delle credenziali non riuscito. Riprova.',
        sendCredentialsMissingPhone:
          'Questo collaboratore non ha un numero di telefono valido.',
        sendCredentialsMissingCredentials:
          'Username o password mancanti per questo collaboratore.',
        changePasswordSuccess: 'Password aggiornata con successo.',
        changePasswordError:
          'Aggiornamento della password non riuscito. Riprova.',
      },
      credentialsMessage:
        'Le tue credenziali Proventi:\nUsername: {{username}}\nPassword: {{password}}',
    },
    collaboratorReports: {
      title: 'Report Collaboratori',
      titleWithMonth: 'Report {{branch}} di {{monthYear}}',
      subtitle: 'Report collaboratori per {{branch}}',
      subtitleDefault:
        'Visualizza e gestisci l’elenco dei report collaboratori',
      noReportsPresent: 'Nessun report presente',
      searchPlaceholder: 'Cerca per nome',
      name: 'Nome',
      lunch: 'Pranzo',
      cena: 'Cena',
      illness: 'Malattia',
      holiday: 'Ferie',
      free: 'Libero',
      hoursWorked: 'Ore Lavorate',
      itemsPerPage: 'Elementi per pagina',
      range: '{{start}} - {{end}} di {{total}}',
      noBranch: 'Seleziona una filiale per visualizzare i report collaboratori',
      empty: 'Nessun report collaboratore trovato',
      emptyDesc: 'Non ci sono ancora report collaboratori per questa filiale.',
      previousMonth: 'Mese precedente',
      nextMonth: 'Mese successivo',
      downloadPdf: 'Scarica report del {{monthYear}}',
      downloadPdfSuccess: 'PDF del report collaboratori scaricato.',
      downloadPdfError:
        'Impossibile scaricare il PDF del report collaboratori.',
      pdfTitle: 'Report Collaboratori',
      pdfMeta:
        '{{branch}} · {{monthYear}} · {{count}} collaboratori · Generato il {{date}}',
    },
  },
  extraServices: {
    title: 'Servizi e Prodotti Extra',
    subtitle:
      'Gestisci servizi, prodotti e amenità disponibili per {{branchName}}',
    subtitleDefault: 'Visualizza e gestisci l’elenco dei servizi extra',
    statsCount: '{{count}} servizi extra',
    searchPlaceholder: 'Cerca un servizio o prodotto...',
    noBranch: 'Seleziona una filiale per visualizzare i servizi extra',
    empty: 'Nessun servizio extra',
    emptyDesc:
      'Non hai ancora configurato servizi o prodotti extra. Aggiungi amenità per migliorare l’esperienza dei tuoi clienti.',
    itemsPerPage: 'Elementi per pagina',
    range: '{{start}} - {{end}} di {{total}}',
    columns: {
      service: 'Servizio',
      price: 'Prezzo',
      guests: 'Ospiti',
      state: 'Stato',
    },
    active: 'Attivo',
    inactive: 'Inattivo',
    createTitle: 'Crea nuovo servizio',
    createDesc: 'Aggiungi un nuovo servizio o prodotto per la tua filiale.',
    editTitle: 'Modifica servizio',
    editDesc: 'Aggiorna le informazioni del servizio',
    createService: 'Crea servizio',
    addService: 'Aggiungi servizio',
    creating: 'Creazione...',
    saveChanges: 'Salva modifiche',
    close: 'Chiudi',
    name: 'Nome',
    namePlaceholder: 'Inserisci il nome del servizio',
    description: 'Descrizione',
    descriptionPlaceholder: 'Inserisci la descrizione',
    price: 'Prezzo (€)',
    pricePlaceholder: '0',
    guests: 'Numero di ospiti',
    guestsPlaceholder: '1',
    activeService: 'Servizio attivo',
    serviceImage: 'Immagine servizio',
    uploadImage: 'Carica immagine',
    removeImage: 'Rimuovi immagine',
    uploading: 'Caricamento...',
    removing: 'Rimozione...',
    editService: 'Modifica servizio',
    duplicate: 'Duplica',
    deleteService: 'Elimina servizio',
    deleteModal: {
      title: 'Eliminare il servizio?',
      description:
        'Sei sicuro di voler eliminare questo servizio? Questa azione non può essere annullata.',
    },
    validation: {
      nameRequired: 'Il nome è obbligatorio',
      nameMax: 'Il nome non può superare 120 caratteri',
      descriptionMax: 'La descrizione non può superare 300 caratteri',
      numberInvalid: 'Inserisci solo un numero valido',
      priceMin: 'Il prezzo non può essere negativo',
      guestsMin: 'Il numero di ospiti non può essere negativo',
      guestsInteger: 'Il numero di ospiti deve essere un numero intero',
    },
    toast: {
      createSuccess: 'Servizio creato con successo.',
      createError: 'Impossibile creare il servizio. Riprova.',
      updateSuccess: 'Servizio aggiornato con successo.',
      updateError: 'Impossibile aggiornare il servizio. Riprova.',
      deleteSuccess: 'Servizio eliminato con successo.',
      deleteError: 'Impossibile eliminare il servizio. Riprova.',
      duplicateSuccess: 'Servizio duplicato con successo.',
      duplicateError: 'Impossibile duplicare il servizio. Riprova.',
      uploadSuccess: 'Immagine caricata con successo.',
      uploadError: "Impossibile caricare l'immagine.",
      removeImageSuccess: 'Immagine rimossa con successo.',
      removeImageError: "Impossibile rimuovere l'immagine.",
    },
  },
  formMenu: {
    title: 'Form Menu',
    subtitleDefault: 'Visualizza e gestisci i Form Menu della filiale',
    statsCount: '{{count}} Form Menu',
    searchPlaceholder: 'Cerca un Form Menu...',
    noBranch: 'Seleziona una filiale per vedere i Form Menu',
    empty: 'Nessun Form Menu',
    emptyDesc:
      'Non hai ancora configurato nessun Form Menu. Aggiungine uno per iniziare.',
    columns: {
      item: 'Nome menu',
      status: 'Stato',
      price: 'Prezzo',
      totalBooking: 'Prenotazioni totali',
      forms: 'Form',
      state: 'Stato',
    },
    formsCount: '{{count}} form',
    active: 'Attivo',
    inactive: 'Inattivo',
    createTitle: 'Crea Form Menu',
    createDesc: 'Aggiungi un nuovo Form Menu per la tua filiale.',
    editTitle: 'Modifica Form Menu',
    editDesc: 'Aggiorna le informazioni del Form Menu',
    createItem: 'Crea',
    addItem: 'Aggiungi Form Menu',
    saveChanges: 'Salva modifiche',
    close: 'Chiudi',
    name: 'Nome menu',
    namePlaceholder: 'Inserisci il nome del Form Menu',
    description: 'Descrizione',
    descriptionPlaceholder: 'Inserisci la descrizione',
    price: 'Prezzo (€)',
    pricePlaceholder: '0',
    forms: 'Form',
    formsPlaceholder: 'Seleziona i form',
    activeItem: 'Form Menu attivo',
    itemImage: 'Immagine menu',
    uploadImage: 'Carica immagine',
    removeImage: 'Rimuovi immagine',
    uploading: 'Caricamento...',
    weekdaysLabel: 'Giorni della settimana',
    selectAllWeekdays: 'Seleziona tutti',
    clearWeekdays: 'Deseleziona',
    availabilityDates: {
      title: 'Date di disponibilità menu',
      dateRange: 'Intervallo date',
      placeholder: 'Seleziona un intervallo',
      empty:
        'Nessuna data di disponibilità. Aggiungi un intervallo per iniziare.',
      addRange: 'Aggiungi intervallo',
      createTitle: 'Aggiungi intervallo date',
      editTitle: 'Modifica intervallo date',
      create: 'Aggiungi',
      update: 'Aggiorna',
      editRange: 'Modifica intervallo',
      deleteRange: 'Elimina intervallo',
      deleteTitle: 'Eliminare intervallo date?',
      deleteDescription:
        'Sei sicuro di voler rimuovere questo intervallo di disponibilità?',
      dateRequired: 'Seleziona un intervallo di date.',
      dateOrder: "La data di fine deve essere successiva o uguale all'inizio.",
      dateOverlap:
        'Questo intervallo si sovrappone a date di disponibilità esistenti. Scegli date libere.',
    },
    weekdays: {
      monday: 'Lunedì',
      tuesday: 'Martedì',
      wednesday: 'Mercoledì',
      thursday: 'Giovedì',
      friday: 'Venerdì',
      saturday: 'Sabato',
      sunday: 'Domenica',
    },
    editItem: 'Modifica Form Menu',
    duplicate: 'Duplica',
    editNotFound:
      'Questo Form Menu non è stato trovato o non è più disponibile.',
    deleteItem: 'Elimina Form Menu',
    deleteModal: {
      title: 'Eliminare Form Menu?',
      description:
        'Sei sicuro di voler eliminare questo Form Menu? Questa azione non può essere annullata.',
    },
    validation: {
      nameRequired: 'Il nome è obbligatorio',
      nameMax: 'Il nome non può superare 120 caratteri',
      descriptionMax: 'La descrizione non può superare 300 caratteri',
      numberInvalid: 'Inserisci solo un numero valido',
      priceMin: 'Il prezzo non può essere negativo',
    },
    toast: {
      createSuccess: 'Form Menu creato con successo.',
      createError: 'Creazione del Form Menu non riuscita. Riprova.',
      updateSuccess: 'Form Menu aggiornato con successo.',
      updateError: 'Aggiornamento del Form Menu non riuscito. Riprova.',
      deleteSuccess: 'Form Menu eliminato con successo.',
      deleteError: 'Eliminazione del Form Menu non riuscita. Riprova.',
      duplicateSuccess: 'Form Menu duplicato con successo.',
      duplicateError: 'Duplicazione del Form Menu non riuscita. Riprova.',
      showEnabled: 'Form Menu ora visibile.',
      showDisabled: 'Form Menu ora nascosto.',
      statusError:
        'Aggiornamento dello stato del Form Menu non riuscito. Riprova.',
      uploadSuccess: 'Immagine caricata con successo.',
      uploadError: 'Caricamento immagine non riuscito.',
      removeImageSuccess: 'Immagine rimossa con successo.',
      removeImageError: 'Rimozione immagine non riuscita.',
      dateRangeCreateSuccess: 'Intervallo di disponibilità aggiunto.',
      dateRangeCreateError:
        'Aggiunta intervallo di disponibilità non riuscita.',
      dateRangeUpdateSuccess: 'Intervallo di disponibilità aggiornato.',
      dateRangeUpdateError:
        'Aggiornamento intervallo di disponibilità non riuscito.',
      dateRangeDeleteSuccess: 'Intervallo di disponibilità eliminato.',
      dateRangeDeleteError:
        'Eliminazione intervallo di disponibilità non riuscita.',
      dateRangeLoadError:
        'Caricamento intervallo di disponibilità non riuscito.',
    },
  },
  events: {
    title: 'Eventi',
    subtitleDefault: 'Visualizza e gestisci gli eventi della filiale',
    statsCount: '{{count}} eventi',
    searchPlaceholder: 'Cerca eventi...',
    filterDatePlaceholder: 'Filtra per data',
    reset: 'Reimposta',
    noBranch: 'Seleziona una filiale per visualizzare gli eventi',
    empty: 'Nessun evento',
    emptyDesc: 'Non hai ancora creato eventi. Aggiungi un evento per iniziare.',
    columns: {
      event: 'Evento',
      start: 'Inizio',
      end: 'Fine',
      capacity: 'Capienza',
      totalBooking: 'Total Booking',
      forms: 'Moduli',
      status: 'Stato',
    },
    formsCount: '{{count}} moduli',
    createTitle: 'Crea evento',
    createDesc: 'Aggiungi un nuovo evento per la tua filiale.',
    editTitle: 'Modifica evento',
    editDesc: 'Aggiorna le informazioni dell’evento',
    createEvent: 'Crea evento',
    createItem: 'Crea',
    addEvent: 'Aggiungi evento',
    saveChanges: 'Salva modifiche',
    close: 'Chiudi',
    name: 'Nome',
    namePlaceholder: 'Inserisci il nome dell’evento',
    description: 'Descrizione',
    descriptionPlaceholder: 'Inserisci la descrizione',
    startDate: 'Data di inizio',
    startTime: 'Ora di inizio',
    endDate: 'Data di fine',
    endTime: 'Ora di fine',
    capacity: 'Capienza massima',
    capacityPlaceholder: '100',
    visible: 'Mostra capienza massima',
    forms: 'Moduli',
    formsPlaceholder: 'Seleziona moduli',
    mandatory: 'Evento obbligatorio',
    weekdaysLabel: 'Giorni della settimana',
    selectAllWeekdays: 'Seleziona tutti',
    clearWeekdays: 'Cancella',
    availabilityDates: {
      title: 'Date di disponibilità evento',
      dateRange: 'Intervallo date',
      placeholder: 'Seleziona intervallo date',
      empty:
        'Nessuna data di disponibilità. Aggiungi un intervallo per iniziare.',
      addRange: 'Aggiungi intervallo',
      createTitle: 'Aggiungi intervallo date',
      editTitle: 'Modifica intervallo date',
      create: 'Aggiungi',
      update: 'Aggiorna',
      editRange: 'Modifica intervallo',
      deleteRange: 'Elimina intervallo',
      deleteTitle: 'Eliminare l’intervallo date?',
      deleteDescription:
        'Sei sicuro di voler rimuovere questo intervallo di disponibilità?',
      dateRequired: 'Seleziona un intervallo date.',
      dateOrder:
        'La data di fine deve essere uguale o successiva alla data di inizio.',
      dateOverlap:
        'Questo intervallo si sovrappone a date già presenti. Scegli date libere.',
    },
    weekdays: {
      monday: 'Lunedì',
      tuesday: 'Martedì',
      wednesday: 'Mercoledì',
      thursday: 'Giovedì',
      friday: 'Venerdì',
      saturday: 'Sabato',
      sunday: 'Domenica',
    },
    eventImage: 'Immagine evento',
    uploadImage: 'Carica immagine',
    removeImage: 'Rimuovi immagine',
    uploading: 'Caricamento...',
    removing: 'Rimozione...',
    editEvent: 'Modifica evento',
    duplicate: 'Duplica',
    deleteEvent: 'Elimina evento',
    deleteModal: {
      title: 'Eliminare l’evento?',
      description:
        'Sei sicuro di voler eliminare questo evento? L’azione non può essere annullata.',
    },
    validation: {
      nameRequired: 'Il nome è obbligatorio',
      nameMax: 'Il nome non può superare 120 caratteri',
      descriptionMax: 'La descrizione non può superare 500 caratteri',
      capacityInvalid: 'Inserisci una capienza valida',
      capacityInteger: 'La capienza deve essere un numero intero',
      capacityMin: 'La capienza deve essere almeno 1',
      capacityMax: 'La capienza non può superare 999',
      startDateRequired: 'La data di inizio è obbligatoria',
      startTimeRequired: 'L’ora di inizio è obbligatoria',
      endDateRequired: 'La data di fine è obbligatoria',
      endTimeRequired: 'L’ora di fine è obbligatoria',
      endBeforeStart: 'La fine deve essere successiva all’inizio',
    },
    toast: {
      createSuccess: 'Evento creato correttamente.',
      createError: 'Impossibile creare l’evento. Riprova.',
      updateSuccess: 'Evento aggiornato correttamente.',
      updateError: 'Impossibile aggiornare l’evento. Riprova.',
      deleteSuccess: 'Evento eliminato correttamente.',
      deleteError: 'Impossibile eliminare l’evento. Riprova.',
      duplicateSuccess: 'Evento duplicato correttamente.',
      duplicateError: 'Impossibile duplicare l’evento. Riprova.',
      showEnabled: 'Evento ora visibile.',
      showDisabled: 'Evento ora nascosto.',
      statusError: 'Aggiornamento stato evento non riuscito. Riprova.',
      uploadSuccess: 'Immagine caricata correttamente.',
      uploadError: 'Impossibile caricare l’immagine.',
      saveBeforeImage:
        'Compila i campi obbligatori dell’evento prima di caricare un’immagine.',
      removeImageSuccess: 'Immagine rimossa correttamente.',
      removeImageError: 'Impossibile rimuovere l’immagine.',
      dateRangeCreateSuccess: 'Intervallo di disponibilità aggiunto.',
      dateRangeCreateError:
        'Impossibile aggiungere l’intervallo di disponibilità.',
      dateRangeUpdateSuccess: 'Intervallo di disponibilità aggiornato.',
      dateRangeUpdateError:
        'Impossibile aggiornare l’intervallo di disponibilità.',
      dateRangeDeleteSuccess: 'Intervallo di disponibilità eliminato.',
      dateRangeDeleteError:
        'Impossibile eliminare l’intervallo di disponibilità.',
      dateRangeLoadError: 'Impossibile caricare l’intervallo di disponibilità.',
    },
  },
  reports: {
    title: 'Report',
    subtitle: 'Dashboard analitica e reportistica',
    comingSoon: 'Report in arrivo',
    comingSoonDesc:
      'Analisi avanzate e report personalizzati saranno disponibili in una versione futura.',
  },
  errors: {
    somethingWrong: 'Qualcosa è andato storto',
    unexpected: 'Si è verificato un errore imprevisto. Riprova.',
    pageNotFound: 'Pagina non trovata',
    pageNotFoundDesc:
      'La pagina che stai cercando non esiste o è stata spostata.',
    accessDenied: 'Accesso negato',
    accessDeniedDesc:
      "Non hai i permessi per accedere a questa risorsa. Contatta l'amministratore.",
    backToDashboard: 'Torna alla Dashboard',
    backToHome: 'Torna alla home',
    publicHint:
      'Controlla il link che hai ricevuto, oppure contatta il locale se hai bisogno di aiuto.',
    applicationError: 'Errore applicazione',
    reload: 'Ricarica pagina',
  },
  publicPromo: {
    documentTitle: 'Promozione',
    loading: 'Caricamento promozione',
    unavailableTitle: 'Promozione non disponibile',
    unavailableDesc:
      'Questo link promozione manca, non è valido o non è più attivo.',
    expiredDesc: 'Questa promozione è scaduta e non è più disponibile.',
    comingSoonDesc:
      'Questa promozione non è ancora pronta per raccogliere risposte.',
    formUnavailableTitle: 'Modulo non disponibile',
    formUnavailableMessage:
      'Questo modulo promozione è attualmente sospeso o non disponibile.',
    badge: 'Promozione',
    requestTitle: 'Richiedi il tuo coupon',
    requestSubtitle: 'Compila i tuoi dati per richiedere la promozione.',
    optional: 'Facoltativo',
    birthDate: 'Data di nascita',
    cap: 'CAP',
    capPlaceholder: 'Es. 09124',
    address: 'Indirizzo',
    addressPlaceholder: 'Via e numero civico',
    nationality: 'Nazionalità',
    nationalityPlaceholder: 'La tua nazionalità',
    requiredNote: '* Campi obbligatori',
    submitting: 'Salvataggio in corso…',
    personalInfo: 'Informazioni personali',
    phoneLabel: 'Cellulare',
    phonePlaceholder: 'Cellulare',
    firstName: 'Nome',
    firstNamePlaceholder: 'Nome',
    lastName: 'Cognome',
    lastNamePlaceholder: 'Cognome',
    email: 'Email',
    emailPlaceholder: 'Email',
    consentsTitle: 'Acconsento al trattamento dei dati personali',
    privacyConsent: 'Privacy e trattamento dei dati',
    marketingConsent: 'Consenso marketing e comunicazione',
    profilingConsent: 'Consenso al profiling',
    detailsLink: 'Dettaglio',
    selectAll: 'Seleziona tutto',
    submit: 'Richiedi il coupon',
    toast: {
      success: 'Coupon generato con successo.',
      error: 'Generazione coupon non riuscita. Riprova.',
    },
    thankYou: {
      title: 'Grazie!',
      message: 'La tua richiesta è stata inviata con successo.',
      continue: 'Continua',
    },
    lookupFound: 'Abbiamo trovato i tuoi dati e li abbiamo compilati.',
    welcomeBack: 'Bentornato, {{firstName}}. Abbiamo già i tuoi dati.',
    welcomeBackNoName: 'Bentornato. Abbiamo già i tuoi dati.',
    alreadyHasCoupon: 'Hai già un coupon per questa promozione.',
    alreadyUsed: 'Questo coupon è già stato utilizzato.',
    maxCoupons:
      'Non è più possibile generare coupon per questa promozione: è stato raggiunto il limite massimo.',
    lookupError:
      'Non è stato possibile verificare questo numero. Puoi comunque compilare il modulo.',
    lookupNotFound: 'Questa promozione non è stata trovata.',
    lookupInvalid: 'Controlla il numero di cellulare e riprova.',
    resend: {
      title: 'Inviare il coupon su WhatsApp?',
      message:
        'Hai già richiesto un coupon. Possiamo inviartelo di nuovo su WhatsApp con il codice QR.',
      confirm: 'Invia su WhatsApp',
      decline: 'Non ora',
      close: 'Chiudi',
      success: 'Abbiamo inviato il coupon su WhatsApp.',
      error: 'Non è stato possibile inviare il coupon. Riprova.',
    },
    validation: {
      prefixRequired: 'Seleziona un prefisso internazionale.',
      phoneMin: 'Inserisci un numero di cellulare valido.',
      phoneRange: 'Inserisci da 6 a 15 cifre.',
      firstNameRequired: 'Il nome è obbligatorio.',
      lastNameRequired: 'Il cognome è obbligatorio.',
      emailRequired: 'L’email è obbligatoria.',
      emailInvalid: 'Inserisci un’email valida.',
      birthDateInvalid: 'Inserisci una data valida, non futura.',
      privacyRequired: 'Il consenso privacy è obbligatorio.',
    },
  },
  publicFidelity: {
    documentTitle: 'Carta fidelity',
    loading: 'Caricamento programma fidelity',
    unavailableTitle: 'Programma non disponibile',
    unavailableDesc:
      'Controlla il link ricevuto oppure contatta il ristorante.',
    badge: 'Il tuo programma fedeltà',
    cardLabel: 'Fidelity card',
    previewPoints: 'Ogni visita conta.',
    previewDiscount: 'Vantaggi dedicati a te.',
    requestTitle: 'Richiedi la tua carta',
    requestSubtitle: 'Crea la tua carta personale e portala sempre con te.',
    optional: 'Facoltativo',
    requiredNote: '* Campi obbligatori',
    submitting: 'Salvataggio in corso…',
    phoneLabel: 'Cellulare',
    phonePlaceholder: 'Il tuo numero',
    firstName: 'Nome',
    firstNamePlaceholder: 'Il tuo nome',
    lastName: 'Cognome',
    lastNamePlaceholder: 'Il tuo cognome',
    email: 'Email',
    emailPlaceholder: 'nome@esempio.it',
    consentsTitle: 'Le tue preferenze',
    privacyConsent: 'Privacy e trattamento dei dati',
    marketingConsent: 'Consenso marketing e comunicazioni',
    profilingConsent: 'Consenso alla profilazione',
    detailsLink: 'Informativa',
    selectAll: 'Seleziona tutto',
    submit: 'Crea la mia carta',
    successTitle: 'La tua carta è pronta.',
    successSubtitle: 'Salva il QR e mostralo al ristorante.',
    qrAlt: 'QR code della tua fidelity card',
    openQr: 'Apri e salva il QR',
    welcomeBack: 'Bentornato, {{firstName}}. Abbiamo già i tuoi dati.',
    welcomeBackNoName: 'Bentornato. Abbiamo già i tuoi dati.',
    lookupError:
      'Non siamo riusciti a verificare questo numero. Puoi comunque completare il modulo.',
    lookupUnavailable: 'Questo programma non è più disponibile.',
    lookupInvalid: 'Controlla il numero di cellulare e riprova.',
    requestLink: 'Richiedi nuovamente il link',
    requestLinkSending: 'Invio della richiesta…',
    requestLinkSent:
      'Se il recapito è associato a una carta attiva, riceverai nuovamente il link.',
    requestLinkError: 'Non è stato possibile richiedere il link. Riprova.',
    errors: {
      alreadyRegistered:
        'Hai già una carta per questo programma. Usa il QR ricevuto oppure contatta il ristorante per recuperarla.',
      unavailable: 'Questo programma non è più disponibile.',
      generic:
        'Non è stato possibile creare la carta. Riprova tra poco oppure contatta il ristorante.',
    },
    validation: {
      prefixRequired: 'Seleziona un prefisso.',
      phoneRange: 'Inserisci da 6 a 15 cifre.',
      firstNameRequired: 'Inserisci il nome.',
      lastNameRequired: 'Inserisci il cognome.',
      emailInvalid: 'Controlla l’indirizzo email.',
      privacyRequired: 'Il consenso privacy è obbligatorio.',
    },
  },
  publicBookingForm: {
    imagePreview: {
      open: 'Ingrandisci immagine: {{name}}',
      close: 'Chiudi anteprima immagine',
      error: 'Impossibile caricare l’immagine.',
    },
    title: 'Prenotazione',
    welcome: 'Benvenuto in',
    loading: 'Caricamento modulo prenotazione',
    missingFormCode: 'Codice modulo mancante nell’URL.',
    loadError: 'Impossibile caricare questo modulo di prenotazione.',
    inactiveTitle: 'Modulo non disponibile',
    inactiveMessage: 'Questo modulo di prenotazione non è attualmente attivo.',
    invalidFormType: 'Questo link non è un modulo di prenotazione.',
    steps: {
      date: 'Data',
      hours: 'Orario',
      menu: 'Menu',
      extra: 'Extra',
      details: 'Dettagli',
      calendar: 'Calendario',
    },
    eventBanner: {
      countOne: '{{count}} evento questo mese',
      count: '{{count}} eventi questo mese',
      tapHint: 'Tocca per vedere quali giorni',
    },
    menuBanner: {
      countOne: '{{count}} menu questo mese',
      count: '{{count}} menu questo mese',
      tapHint: 'Tocca per vedere quali giorni',
    },
    calendar: {
      legendEvent: 'Evento speciale',
      legendMenu: 'Menu disponibili',
    },
    restaurantNoMenu: {
      title: 'No menu',
      description: 'Numero di ospiti senza menu',
    },
    eventSheet: {
      fromTime: 'Dalle {{time}}',
      maxCovers: 'Max {{count}} coperti',
      gotIt: 'Continua',
      continueWithEvent: 'Continua per evento',
      showMore: 'Mostra di più',
      showLess: 'Mostra di meno',
      close: 'Chiudi',
    },
    hours: {
      selectedDate: 'Data selezionata',
      guestsLabel: 'Ospiti (bambini esclusi)',
      childrenLabel: 'Bambini',
      timeSlotLabel: 'Fascia oraria disponibile',
      guestOption: '{{count}} Ospiti',
      childrenOption: '{{count}} Bambini',
      overlimit: 'Limite superato di {{count}}',
      overbooking: 'Overbooking di {{count}}',
      availabilityFinished: 'Disponibilità terminata per questa fascia oraria',
      attentionTitle: 'Attenzione',
      attentionMessage:
        'Il numero di ospiti selezionato supera la disponibilità rimanente per questa fascia oraria.',
      attentionOk: 'OK',
      callOverMaxOption: 'Chiama per tavoli superiori a {{count}} Ospiti',
      callOverMaxTitle: 'Info',
      callOverMaxMessageBefore: 'Chiamare il numero',
      callOverMaxMessageAfter: 'per prenotare un tavolo?',
      callOverMaxHint:
        'Aprirremo il dialer del telefono per completare la chiamata.',
      callOverMaxCall: 'Chiama',
      callOverMaxCancel: 'Annulla',
    },
    extra: {
      badge: 'Extra',
      catalogTitle: 'Catalogo servizi',
      mandatoryNotice:
        'La scelta di almeno un prodotto/servizio è obbligatoria.',
      serviceForGuests: 'Servizio/tavolo per {{count}} ospiti',
      decrease: 'Diminuisci quantità',
      increase: 'Aumenta quantità',
    },
    menu: {
      badge: 'Menu',
      catalogTitle: 'Catalogo menu',
      mandatoryQuantityHint:
        'Scegli un menu (o “Nessun menu”) per ogni persona. Il totale delle selezioni deve essere uguale a {{count}} (ospiti + bambini).',
      decrease: 'Diminuisci quantità',
      increase: 'Aumenta quantità',
    },
    recap: {
      title: 'Riepilogo della prenotazione',
      time: 'Orario',
      guests: 'Ospiti',
      children: 'Bambini',
      selectedServices: 'Servizi selezionati',
      selectedMenus: 'Menu selezionati',
      total: 'Totale',
      paymentOnSite: 'Pagamento in struttura',
    },
    details: {
      phoneTitle: 'Inserisci il numero di telefono per continuare',
      welcomeBackTitle: 'Bentornato, {{name}}!',
      welcomeBackTitleGeneric: 'Bentornato!',
      welcomeBackMessage:
        'Abbiamo già i tuoi dati. Puoi continuare o aggiornare i dettagli se necessario.',
      personalInfo: 'Informazioni personali',
      consentsTitle: 'Acconsento al trattamento dei dati personali',
      privacyConsent: 'Trattamento della privacy e dei dati',
      marketingConsent: 'Consenso marketing e comunicazione',
      commercialConsent:
        'Consenso al trasferimento dei dati a terzi per finalità commerciali',
      profilingConsent: 'Consenso alla profilazione',
      detailsLink: 'Dettagli',
      selectAll: 'Seleziona tutto',
      consentModal: {
        title: 'INFORMATIVA SUL TRATTAMENTO DEI DATI PERSONALI',
        subtitle: '(ai sensi dell’articolo 13 del Reg. UE n. 679/2016 - GDPR)',
        addressLabel: 'Indirizzo',
        emailLabel: 'Email',
        lastUpdated: 'Ultimo aggiornamento',
        section1Title: '1. Premessa',
        section1P1:
          'La presente informativa è fornita da {{branchName}} (Codice Fiscale e Partita IVA), con sede legale in {{branchAddress}} (di seguito "Titolare del trattamento"). (di seguito "Titolare del trattamento"), in conformità al Regolamento UE 2016/679 (GDPR) e al D.lgs. 196/2003 (Codice Privacy), come modificato dal <b>D.lgs. 101/2018.</b>',
        section1P2:
          'La presente informativa si applica al trattamento dei dati personali effettuato dal Titolare tramite la piattaforma <b>"Pro20"</b> per la gestione dell’attività, delle prenotazioni, dei servizi offerti e delle comunicazioni con i clienti.',
        section1P3: 'L’informativa è fornita agli Utenti che:',
        section1Li1:
          '• Utilizzano il sito web del Titolare per richiedere informazioni o prenotare un tavolo;',
        section1Li2:
          '• Forniscono volontariamente i propri dati tramite dispositivi mobili personali o dispositivi locali (es. tablet) per accedere a servizi quali il programma fidelizzazione o la connessione Wi-Fi gratuita;',
        section1Li3:
          '• Si presentano fisicamente nel locale e forniscono i propri dati per ricevere newsletter, offerte commerciali e materiale promozionale su prodotti e servizi del Titolare.',
        section2Title: '2. Titolare del trattamento',
        section2Sensitive:
          'I dati sensibili saranno trattati solo se strettamente indispensabili e con il consenso dell’Utente, manifestato mediante azioni esplicite quali la selezione di un’opzione online (es. "Accetto" o "Acconsento") o la firma di un modulo cartaceo.',
        section3Title: '3. Dati trattati e modalità di acquisizione',
        section3Intro: 'I dati sono acquisiti nei seguenti modi:',
        section3Li1:
          '• Attraverso la piattaforma PRO20 per la gestione delle prenotazioni e dei servizi offerti.',
        section3Li2:
          '• Tramite il sito web del Titolare, quando l’Utente richiede informazioni o prenota un tavolo',
        section3Li3:
          '• Direttamente nel locale, quando l’Utente fornisce i dati per accedere a servizi (Wi-Fi, fidelizzazione, etc.).',
        section4Title: '4. Dati di navigazione',
        section4Body:
          'Se l’Utente interagisce con il sito web o altre piattaforme digitali, vengono raccolti dati di navigazione automaticamente (es. indirizzo IP, tipo di dispositivo, sistema operativo, pagine visitate). L’utilizzo di cookie e strumenti analoghi è descritto nella Cookie Policy, consultabile sul sito web del Titolare.',
        section5Title: '5. Finalità del trattamento',
        section51Title: '5.1 Finalità primarie',
        section51Intro: 'I dati vengono trattati per:',
        section51Li1:
          '✔ Gestire le prenotazioni e i servizi richiesti (fila-fast, Wi-Fi, fidelizzazione, questionari di soddisfazione, etc.)',
        section51Li2:
          '✔ Adempiere ad obblighi legali, fiscali e amministrativi',
        section51Li3:
          '✔ Tutela del personale e del servizio da comportamenti illeciti o fraudolenti',
        section51Li4:
          '✔ Analisi statistiche anonime per migliorare la qualità dei servizi',
        section51Li5:
          '✔ Comunicare con l’Utente in merito alla prenotazione o altri servizi richiesti',
        section51Note:
          '📌 Conferire i dati è necessario per accedere ai servizi: il mancato conferimento potrebbe impedire la prenotazione o l’erogazione del servizio.',
        section52Title:
          '5.2 Finalità secondarie (Marketing e Promozioni): solo con consenso esplicito.',
        section52Intro:
          'Previo consenso esplicito, i dati potranno essere utilizzati per:',
        section52Li1:
          '📢 Invio di comunicazioni promozionali e offerte personalizzate, via email, SMS o altri canali digitali',
        section52Li2:
          '📢 Indagini di mercato e sondaggi sulla customer experience',
        section52Li3:
          '📢 Analisi delle abitudini di consumo per offrire esperienze personalizzate.',
        section52Note:
          '⚠ Il consenso al marketing è facoltativo e può essere revocato in qualsiasi momento scrivendo a {{email}}.',
        section6Title: '6. Destinatari dei dati',
        section6Intro: 'I dati raccolti potranno essere comunicati a:',
        section6Li1:
          '• Personale interno autorizzato del Titolare, per la gestione del servizio.',
        section6Li2:
          '• Fornitori di servizi IT e piattaforme di gestione (es. servizi di prenotazione online, email marketing, hosting web), nel rispetto di accordi di riservatezza.',
        section6Li3:
          '• Autorità pubbliche, in caso di richieste legali o obblighi normativi.',
        section6Note: '📌 I dati NON saranno diffusi né venduti a terzi.',
        section7Title: '7. Conservazione dei dati',
        section7Intro: 'I dati saranno conservati per:',
        section7Li1:
          '📌 Finalità amministrative e contabili: per il tempo necessario ad adempiere agli obblighi di legge (fino a 10 anni).',
        section7Li2:
          '📌 Finalità di marketing: fino alla revoca del consenso o, in assenza di interazioni, per un massimo di 24 mesi.',
        section7Li3:
          '📌 Dati di navigazione: secondo quanto stabilito dalla Cookie Policy.',
        section8Title: '8. Diritti dell’Utente',
        section8Intro: 'Gli utenti hanno il diritto di:',
        section8Li1:
          '✅ Accedere ai propri dati personali e ottenere informazioni sul loro trattamento.',
        section8Li2:
          '✅ Richiedere la rettifica o la cancellazione dei dati personali.',
        section8Li3:
          '✅ Limitare o opporsi al trattamento per motivi legittimi.',
        section8Li4:
          '✅ Revocare il consenso in qualsiasi momento senza pregiudicare la liceità del trattamento precedente.',
        section8Li5:
          '✅ Presentare un reclamo all’Autorità Garante per la Protezione dei Dati Personali (www.garanteprivacy.it).',
        section9Title:
          '9. Consenso alla cessione dei dati a terzi per scopi commerciali',
        section9Li1:
          '✅ Acconsento al trattamento dei miei dati personali per finalità di marketing diretto da parte di soggetti terzi, anche mediante la loro cessione a partner commerciali operanti nei settori quali editoria, tecnologia, viaggi, finanza, ecc. Tali soggetti potranno contattarmi tramite e-mail, telefono o altri canali per proprie iniziative promozionali. Il consenso è facoltativo e può essere revocato in qualsiasi momento scrivendo a {{email}}.',
        section9Contact: '📧 Per esercitare i diritti, contattare: {{email}}',
        section10Title: '10. Modifiche all’informativa',
        section10Body:
          'Il Titolare si riserva il diritto di aggiornare questa informativa. Le modifiche saranno comunicate sul sito web o via email se necessario.',
      },
    },
    actions: {
      continue: 'Continua',
      back: 'Indietro',
      proceedToDetails: 'Procedi ai dati personali',
    },
    validation: {
      date: 'Seleziona una data.',
      hours: 'Completa ospiti, bambini e fascia oraria.',
      menu: 'Controlla la selezione del menu.',
      menuMandatory:
        'Scegli i menu in modo che il totale corrisponda a ospiti + bambini.',
      extra: 'Seleziona almeno un servizio.',
    },
    fieldErrors: {
      firstNameMin: 'Il nome è obbligatorio.',
      firstNameInvalid: 'Il nome può contenere solo lettere.',
      lastNameMin: 'Il cognome deve contenere almeno 2 caratteri.',
      lastNameInvalid: 'Il cognome può contenere solo lettere.',
      emailRequired: 'L’email è obbligatoria.',
      emailInvalid: 'Inserisci un’email valida.',
      postalCodeRequired: 'Il CAP è obbligatorio.',
      dateOfBirthRequired: 'La data di nascita è obbligatoria.',
      specialRequestsRequired: 'Le richieste speciali sono obbligatorie.',
      singleChoiceRequired: "Seleziona un'opzione.",
      multiChoiceRequired: "Seleziona almeno un'opzione.",
      dateOfBirthInvalid: 'Inserisci una data di nascita valida.',
      dateOfBirthFuture: 'La data di nascita non può essere nel futuro.',
      countryCodeInvalid: 'Seleziona un prefisso valido.',
      phoneRequired: 'Il numero di telefono è obbligatorio.',
      phoneInvalid: 'Il numero di telefono deve contenere solo cifre.',
      phoneMin: 'Il numero di telefono deve avere almeno 6 cifre.',
      phoneMax: 'Il numero di telefono può avere al massimo 15 cifre.',
      privacyRequired: 'Il consenso privacy è obbligatorio.',
      cardRequestConsentRequired:
        'Accetta i termini di verifica carta / deposito.',
      bookingTimeInvalid: 'Seleziona un orario valido.',
      coverOverlimit:
        'Ospiti e bambini superano il massimo di coperti per questo modulo.',
    },
    cardRequest: {
      title: 'Card required for this booking',
      notice:
        'Per confermare questa prenotazione è richiesta una carta di pagamento per la fascia selezionata.',
      alreadyOnFile:
        'Abbiamo già una carta per questo numero. Conferma la prenotazione per usarla.',
      acceptValidation:
        'Accetto la verifica della carta. Nessun importo sarà addebitato o bloccato durante questa operazione.',
      acceptDeposit:
        'Accetto la preautorizzazione di {{amount}} sulla carta e le condizioni che regolano l’eventuale incasso, la cancellazione e il no-show.',
      acceptPayNow:
        'Confermo il pagamento di {{amount}} e accetto le condizioni di cancellazione, rimborso e no-show.',
      policyToggle: 'Leggi la policy completa di pagamento',
      policyTitle: 'Policy di pagamento, carta e cancellazione',
      policy: {
        s1Title: '1. Identità della struttura',
        s1Body:
          'La prenotazione e il pagamento sono gestiti da {{branchName}}, {{branchAddress}}. Stripe opera esclusivamente come prestatore di servizi di pagamento. Contatti: {{contact}}.',
        s2Title: '2. Verifica della carta',
        s2Body:
          'Quando è richiesta solo la verifica, nessun importo viene addebitato o bloccato. La carta è verificata tramite Stripe per confermarne la validità.',
        s3Title: '3. Preautorizzazione / deposito',
        s3Body:
          'Può essere richiesta una preautorizzazione di {{amount}} ({{perGuest}} a persona). I tempi di sblocco dipendono dalla banca emittente.',
        s4Title: '4. Pagamento anticipato',
        s4Body:
          'Se configurato, può essere addebitato un acconto o deposito secondo le regole della struttura indicate in fase di prenotazione.',
        s5Title: '5. Cancellazione e modifica',
        s5Body:
          'La cancellazione gratuita può essere consentita fino al termine indicato dalla struttura. Successivamente la struttura può trattenere o addebitare importi secondo la propria policy.',
        s6Title: '6. No-show e ritardo',
        s6Body:
          'In caso di no-show o ritardo significativo, la struttura può trattenere o addebitare importi secondo le proprie regole.',
        s7Title: '7. Rimborsi',
        s7Body:
          'I rimborsi ammessi sono elaborati sullo stesso metodo di pagamento usato per la prenotazione, nei tempi previsti dalla struttura e dal gestore dei pagamenti.',
        s8Title: '8. Conservazione e uso futuro della carta',
        s8Body:
          'I dati della carta possono essere conservati in forma tokenizzata da Stripe e riusati solo con consenso specifico. PRO20 e la struttura non conservano il numero completo della carta.',
        s9Title: '9. Contestazioni e assistenza',
        s9Body:
          'Per cancellazioni, contestazioni o assistenza contatta la struttura ({{contact}}) citando il codice prenotazione.',
        version: 'Versione {{version}}',
      },
    },
    legacyCard: {
      eyebrow: 'Verifica diretta',
      title: 'Dati carta di credito',
      subtitle: 'La struttura non utilizza Stripe per questa prenotazione.',
      holder: 'Nome sulla carta',
      number: 'Numero carta',
      month: 'Mese',
      year: 'Anno',
      cvc: 'CVV',
      circuit: '{{circuit}}',
      verified: 'Carta verificata · termina con {{last4}}',
      cancel: 'Annulla',
      verify: 'Verifica carta',
      verifying: 'Verifica…',
      confirm: 'Conferma',
      verifySuccess: 'Carta verificata con successo',
      verifyError: 'I dati della carta non sono validi',
      requiredTitle: 'Carta richiesta',
      required:
        'La carta deve essere verificata per completare la prenotazione.',
      storeWarning:
        'La prenotazione è stata creata, ma non è stato possibile salvare la carta. Riprova dalla richiesta carta.',
      saved: 'Carta salvata · termina con {{last4}}',
      alreadyOnFile:
        'Una carta è già associata a questo cliente. Non servono altri dati.',
      pageLoadError: 'Impossibile aprire questa richiesta carta.',
      saveError: 'Non è stato possibile salvare la carta. Riprova.',
      fieldRequired: 'Campo obbligatorio',
      fieldInvalid: 'Valore non valido',
      circuits: {
        VISA: 'Visa',
        MASTERCARD: 'Mastercard',
        AMERICAN_EXPRESS: 'American Express',
        DISCOVER: 'Discover',
        JCB: 'JCB',
        MAESTRO: 'Maestro',
      },
    },
    stripe: {
      verifyTitle: 'Verifica la tua carta',
      verifyDescription:
        'Inserisci i dati della carta per confermare questa prenotazione. Nessun importo verrà addebitato ora.',
      verifyConfirm: 'Verifica carta',
      payTitle: 'Completa il pagamento',
      payDescription:
        'Autorizza {{amount}} per confermare questa prenotazione.',
      payConfirm: 'Paga {{amount}}',
      initializing: 'Preparazione del pagamento sicuro…',
      processing: 'Elaborazione…',
      initError: 'Impossibile avviare il pagamento Stripe. Riprova.',
      confirmError: 'Il pagamento non è stato completato. Riprova.',
      verifyPending: 'Stripe non ha ancora confermato la carta. Riprova.',
      cardRequired:
        'La carta deve essere verificata per completare la prenotazione.',
      resumePayment: 'Completa il pagamento',
      resumeNotice:
        'La tua prenotazione è in attesa di pagamento. Completa il pagamento per confermarla: non verrà creata una nuova prenotazione.',
    },
    phonePlaceholder: 'Inserisci numero di telefono',
    unknownCustomer: 'Cliente sconosciuto',
    blacklist: 'Blacklist',
    customerDetails: 'I tuoi dati',
    bookingDetails: 'Dettagli prenotazione',
    firstName: 'Nome',
    lastName: 'Cognome',
    email: 'Email',
    phone: 'Telefono',
    postalCode: 'CAP',
    dateOfBirth: 'Data di nascita',
    guests: 'Ospiti',
    children: 'Bambini',
    time: 'Orario',
    selectTime: 'Seleziona orario',
    slotNotAvailable: 'Disponibilita terminata per questa fascia oraria',
    noAvailableTimes: 'Nessun orario disponibile per la data selezionata.',
    specialRequests: 'Note personali e richieste',
    comingWithDogs: 'Vengo con cani',
    extraServices: 'Servizi extra',
    amenityGuests: '{{count}} ospiti',
    submit: 'Invia prenotazione',
    submitError: 'Impossibile inviare la prenotazione. Riprova.',
    existingBooking: {
      title: 'Attenzione, esiste già una prenotazione',
      date: 'Data',
      time: 'Ora',
      guests: 'Ospiti',
      children: 'Bambini',
      event: 'Evento',
      menus: 'Menu',
      noMenu: 'Senza menu',
      services: 'Servizi',
      continueQuestion: 'Vuoi continuare e creare comunque questa prenotazione?',
      continue: 'Continua',
    },
    thankYou: {
      title: 'Grazie!',
      message:
        'La tua richiesta di prenotazione è stata ricevuta. Ti contatteremo a breve.',
      summaryTitle: 'Riepilogo prenotazione',
      date: 'Data',
      time: 'Ora',
      guests: 'Ospiti',
      guestsValue: '{{guests}} Ospite ({{children}} Bambini)',
      location: 'Luogo',
      name: 'Nome',
      phone: 'Telefono',
      eventCode: 'Codice evento',
      eventName: 'Evento',
      menus: 'Menu',
      makeAnother: 'Effettua un’altra prenotazione',
      backHome: 'Torna alla home',
      continue: 'Continua',
    },
  },
  publicBookingUpdate: {
    title: 'Modifica prenotazione',
    loading: 'Caricamento prenotazione',
    missingBookingCode: 'Il codice prenotazione manca dall’URL.',
    loadError: 'Impossibile caricare questa prenotazione.',
    formLoadError:
      'Impossibile caricare la disponibilità per questa prenotazione.',
    noFormTitle: 'Modifica online non disponibile',
    noFormMessage:
      'Questa prenotazione non può essere modificata online. Contatta direttamente il locale.',
    notEditable: 'La prenotazione non è più modificabile',
    hello: 'Ciao {{name}}',
    subtitle: 'Stai modificando la tua prenotazione presso {{branch}}',
    currentBadge: 'Attuale: {{time}} · {{date}}',
    currentlyBooked: 'Prenotazione attuale',
    current: {
      title: 'La tua prenotazione',
      client: 'Cliente',
      date: 'Data attuale',
      time: 'Ora attuale',
      guests: 'Ospiti',
      children: 'Bambini',
      event: 'Evento',
      menu: 'Menu',
      menuPeople: 'Persone',
      showDetails: 'Dettagli',
      hideDetails: 'Nascondi',
    },
    pendingModification: {
      title: 'C’è già una richiesta di modifica in attesa di conferma',
      summary: '{{date}} · {{time}} · {{guests}} ospiti, {{children}} bambini',
      date: 'Data',
      time: 'Ora',
      guests: 'Guests',
      children: 'Children',
    },
    form: {
      title: 'Modifica prenotazione',
      subtitle: 'Scegli una nuova data, numero di ospiti e fascia oraria.',
      selectedDate: 'Data selezionata',
      guestsLabel: 'Ospiti (bambini esclusi)',
      childrenLabel: 'Bambini',
      timeSlotLabel: 'Fascia oraria disponibile',
      guestOption: '{{count}} Ospiti',
      childrenOption: '{{count}} Bambini',
      selectTime: 'Seleziona orario',
      noAvailableTimes: 'Nessun orario disponibile per la data selezionata.',
      notesLabel: 'Note e richieste personali',
      notesPlaceholder: 'Aggiungi una nota per il locale…',
      readyToUpdate:
        'Controlla le modifiche e tocca Aggiorna per inviare la richiesta di modifica.',
      calendarUnavailable:
        'Il calendario non è disponibile per questa prenotazione (manca form o filiale).',
    },
    actions: {
      update: 'Modifica prenotazione',
      updating: 'Aggiornamento…',
      delete: 'Elimina',
      cancelReservation: 'Cancella prenotazione',
      cancelling: 'Cancellazione…',
    },
    deleteConfirm:
      'Sei sicuro di voler cancellare questa prenotazione? L’azione non può essere annullata.',
    deleteModal: {
      title: 'Cancellare la prenotazione?',
      description:
        'Sei sicuro di voler cancellare questa prenotazione? L’azione non può essere annullata.',
    },
    toast: {
      updateSuccess: 'Richiesta di modifica inviata.',
      updateError: 'Impossibile aggiornare la prenotazione. Riprova.',
      validationError: 'Correggi i campi evidenziati prima di aggiornare.',
      deleteSuccess: 'Prenotazione eliminata con successo.',
      deleteError: 'Impossibile eliminare la prenotazione. Riprova.',
    },
    thankYou: {
      badge: 'Richiesta inviata',
      title: 'Thanks',
      headerMessage:
        'We have received your booking modification request and will respond as soon as possible.',
      statusLabel: 'Richiesta inviata',
      awaitingTitle: 'La modifica è in attesa di conferma',
      awaitingMessage:
        'We have received your booking modification request and will respond as soon as possible.',
      step1Title: 'Controllo dal locale',
      step1Message:
        'Lo staff verificherà disponibilità e dettagli della richiesta.',
      step2Title: 'Risposta automatica',
      step2Message:
        'Riceverai un aggiornamento appena la modifica sarà accettata o rifiutata.',
      closeHint:
        'Puoi chiudere questa pagina: la tua richiesta è stata registrata correttamente.',
    },
    result: {
      updatedTitle: 'Prenotazione aggiornata',
      updatedMessage:
        'Le modifiche sono state salvate. Il locale è stato avvisato del tuo aggiornamento.',
      deletedTitle: 'Prenotazione eliminata',
      deletedMessage:
        'La tua prenotazione è stata cancellata. Speriamo di rivederti un’altra volta.',
    },
  },
  backoffice2020: {
    title: 'Backoffice PRO20',
    logout: 'Esci',
    login: {
      documentTitle: 'Accesso Backoffice',
      title: 'Accedi al Backoffice',
      subtitle: 'Usa email e password admin per continuare.',
      emailPlaceholder: 'Email',
      passwordPlaceholder: 'Password',
      rememberCredentials: 'Ricorda le credenziali',
      signIn: 'Accedi',
      invalidCredentials: 'Email o password non validi.',
      adminBadge: 'Accesso admin',
      authorizedOnly: 'Solo personale autorizzato',
      heroTitle: 'Gestisci la piattaforma Pro20 con sicurezza',
      heroSubtitle:
        'Accesso backoffice protetto per ristoranti, messaging e controlli di piattaforma.',
      heroCardTitle: 'Centro di controllo sicuro',
      heroCardSubtitle: 'Operazioni di piattaforma',
      heroPointOne: 'Gestisci ristoranti, staff e onboarding',
      heroPointTwo: 'Monitora lo stato del messaging e gli alert operativi',
      heroPointThree: 'Applica impostazioni globali con piena responsabilità',
      trustedBy: 'Affidabile per operazioni sicure',
      trustAccessTitle: 'Accesso controllato',
      trustAccessDesc: 'Credenziali solo admin con sessioni protette',
      trustAuditTitle: 'Pronto per audit',
      trustAuditDesc: 'Le azioni critiche restano tracciabili',
      trustPlatformTitle: 'Scala piattaforma',
      trustPlatformDesc: 'Opera su più locali da un’unica console',
      validation: {
        emailRequired: "L'email è obbligatoria",
        emailInvalid: 'Inserisci un indirizzo email valido',
        emailMax: "L'email deve contenere al massimo 255 caratteri",
        passwordMin: 'La password deve contenere almeno 8 caratteri',
        passwordMax: 'La password deve contenere al massimo 128 caratteri',
      },
    },
    whatsapp: 'WhatsApp',
    footer: 'BackOffice Pro20 © {{year}}',
    headingTitle: 'Benvenuto nel pannello operativo Pro20',
    headingWelcomeBack: 'Bentornato, {{name}}',
    headingWelcomeBackGeneric: 'Bentornato',
    actions: {
      announcements: 'Annunci',
      notifications: 'Notifiche',
      globalAlertTooltip: "Avviso globale sull'app",
      expiredActivities: 'Attività scadute',
      expiredActivitiesTooltip: 'Attiva/disattiva avvisi scadenza',
      removeBanner: 'Rimuovi banner',
      removeBannerTooltip: "Rimuovi il banner globale dall'app",
    },
    releaseMobile: {
      title: 'Release Mobile',
      currentVersion: 'Versione attuale {{version}}',
      manageVersion: 'Gestisci versione',
      toast: {
        versionUpdated: 'Versione app aggiornata',
        versionUpdateFailed: 'Impossibile aggiornare la versione app',
        bannerRemoved: 'Banner globale rimosso',
        bannerRemoveFailed: 'Impossibile rimuovere il banner globale',
      },
    },
    manageVersionModal: {
      title: "Aggiorna versione dell'app",
      loading: 'Caricamento versione attuale…',
      loadError: 'Impossibile caricare la versione attuale.',
      major: 'Major',
      majorHint: 'Modifiche principali',
      minor: 'Minor',
      minorHint: 'Nuove funzionalità',
      bug: 'Bug',
      bugHint: "Correzioni dell'applicazione",
      patch: 'Patch',
      patchHint: 'Release Review',
      formatHint: 'Il formato pubblicato sarà Major.Minor.Bug.Patch.',
      update: 'Aggiorna versione',
    },
    removeBannerModal: {
      title: 'Rimuovere il banner globale?',
      description: "Il messaggio non verrà più visualizzato nell'app.",
      confirm: 'Rimuovi banner',
      cancel: 'Annulla',
    },
    globalAlertModal: {
      title: 'Inserisci il nuovo messaggio di avviso globale',
      confirmation: 'Conferma',
      cancel: 'Annulla',
      toast: {
        success: 'Avviso globale aggiornato',
        failed: "Impossibile aggiornare l'avviso globale",
      },
    },
    createActivityModal: {
      title: 'Crea Filiale',
      cancel: 'Annulla',
      create: 'Crea',
      selectUser: 'Seleziona Utente',
      selectUserPlaceholder: 'Seleziona un utente',
      selectUserSearchPlaceholder: 'Cerca utente per nome o email',
      restaurantName: 'Nome Ristorante',
      email: 'Email',
      phone: 'Telefono',
      vatNumber: 'Partita IVA',
      companyName: 'Ragione sociale',
      sections: {
        marketing: 'Marketing',
        address: 'Indirizzo',
        geolocation: 'Geolocalizzazione',
        integration: 'Integrazione',
      },
      gtmCode: 'GTM Code',
      gtmCodeHint: 'Codice di Google Tag Manager',
      facebookPixel: 'Facebook Pixel',
      facebookPixelHint: 'Il codice del Pixel è composto da soli numeri',
      address: 'Indirizzo',
      postalCode: 'CAP',
      city: 'Città',
      country: 'Nazione',
      latitude: 'Latitudine',
      latitudeHint: 'Es. 45.464211',
      longitude: 'Longitudine',
      longitudeHint: 'Es. 9.191383',
      ipraticoApiKey: 'iPratico API Key',
      ipraticoApiKeyHint: "Chiave API per l'integrazione con iPracto",
      noUsers: 'Nessun utente disponibile',
      toast: {
        created: 'Filiale creata',
        createFailed: 'Impossibile creare la filiale',
      },
      validation: {
        userRequired: "La selezione dell'utente è obbligatoria",
        restaurantRequired: 'Il nome del ristorante è obbligatorio',
        emailInvalid: 'Inserisci un indirizzo email valido',
        facebookPixelNumeric: 'Il Pixel Facebook deve contenere solo numeri',
      },
    },
    editActivity: {
      pageTitle: 'Modifica attività',
      breadcrumbRoot: 'backoffice',
      close: 'Chiudi',
      back: 'Torna alle attività',
      notFound: 'Attività non trovata.',
      update: 'Aggiorna',
      enable: 'Abilita',
      disabled: 'Disabilitato',
      smsRemaining: 'SMS residui',
      addSms: 'Aggiungi SMS',
      editSms: 'Modifica SMS residui',
      configureSmsModal: {
        title: 'Aggiorna il numero residuo di SMS',
        branchCodeLabel: 'BranchCode {{branchCode}}',
        confirm: 'Configura SMS',
      },
      aiCreditsRemaining: 'Crediti AI',
      editAiCredits: 'Modifica crediti AI',
      configureAiCreditsModal: {
        title: 'Aggiorna il saldo crediti AI',
        branchCodeLabel: 'BranchCode {{branchCode}}',
        confirm: 'Configura crediti AI',
      },
      emailSetup: 'Configurazione email',
      editEmailConfig: 'Modifica configurazione email',
      restoreDefaultEmailConfig: 'Ripristina configurazione predefinita',
      emailFields: {
        mailResidue: 'Residuo email',
        customerEmail: 'Email cliente',
        fromAddress: 'Indirizzo mittente',
        password: 'Password',
        smtpServer: 'Server SMTP',
        smtpPort: 'Porta SMTP',
      },
      configureEmailModal: {
        title: 'Aggiorna il numero residuo di email',
        mailResidue: 'Residuo email',
        customerEmail: 'Email cliente per messaggi personalizzati',
        fromAddress: 'Indirizzo mittente',
        password: 'Password generata',
        smtpServer: 'Server SMTP',
        smtpPort: 'Porta SMTP',
        required: 'Questo campo è obbligatorio',
        save: 'Salva configurazione',
      },
      sections: {
        general: 'Dati generali',
        subscription: 'Abbonamento',
        address: 'Indirizzo',
        coordinates: 'Coordinate',
        marketing: 'Marketing',
        integrations: 'Integrazioni',
        communications: 'Comunicazioni',
      },
      restaurantName: 'Nome filiale',
      branchNameOnGoogle: 'Nome brand (Google)',
      email: 'Email',
      capacity: 'Capienza locale',
      vatNumber: 'Partita IVA',
      companyName: 'Ragione sociale',
      phone: 'Cellulare',
      url: 'URL',
      deadline: 'Data di scadenza',
      address: 'Indirizzo',
      postalCode: 'CAP',
      city: 'Città',
      province: 'Provincia / Località',
      region: 'Regione',
      country: 'Nazione',
      longitude: 'Longitudine',
      latitude: 'Latitudine',
      gtmCode: 'GTM Code',
      facebookPixel: 'FB Pixel Code',
      ipraticoApiKey: 'iPratico XApiKey',
      ipraticoApiKeyPlaceholder: 'Inserisci la chiave',
      menuRestaurant: 'Menu Ristorante',
      menuRestaurantDesc:
        'Abilita il menu digitale del ristorante per questa filiale.',
      officialApi: 'Api Ufficiali',
      officialApiDesc: 'Abilita le API ufficiali WhatsApp per questa filiale.',
      googleReservation: 'Google Reservation',
      googleReservationDesc: 'Abilita Google Reservation per questa filiale.',
      supplierOrders: 'Ordini fornitori',
      supplierOrdersDesc: 'Abilita gli ordini fornitori per questa filiale.',
      deliveryTakeaway: 'Delivery / Asporto',
      deliveryTakeawayDesc:
        'Abilita delivery e asporto per questa filiale. Richiede il Menu Ristorante.',
      automation: 'Automazione',
      automationDesc: 'Abilita l’automazione per questa filiale.',
      statusEnabled: 'Attiva',
      statusDisabled: 'Spenta',
      sectionHints: {
        general:
          'Nome, contatti e quanti ospiti può accogliere questa filiale.',
        subscription: 'Quando scade l’accesso a questa filiale.',
        address: 'Dove ospiti e partner trovano questa filiale.',
        coordinates: 'Posizione sulla mappa usata dagli elenchi pubblici.',
        marketing: 'Codici di tracciamento del sito di questa filiale.',
        integrations: 'Prodotti e servizi partner collegati a questa filiale.',
        communications:
          'Credito SMS, crediti AI e casella usata per i messaggi agli ospiti.',
      },
      publicApi: {
        title: 'API REST pubblica',
        description:
          'Espone lo storico prenotazioni di questa filiale. I client si autenticano con l’header X-API-KEY.',
        configure: 'Configura',
        statusConfigured: 'Configurata',
        statusMissing: 'Non configurata',
        notConfigured:
          'Nessuna X-API-KEY. Configura questa filiale per crearla sul server.',
        loadFailed: 'Impossibile caricare la X-API-KEY.',
        retry: 'Riprova',
        configureFailed: 'Impossibile creare la X-API-KEY',
        configured: 'X-API-KEY creata',
        deleteConfiguration: 'Elimina configurazione',
        deleteConfirmTitle: 'Eliminare la configurazione dell’API REST?',
        deleteConfirmDescription:
          'Questo rimuove la X-API-KEY di questa sede. Chi la usa non potrà più autenticarsi.',
        deleted: 'Configurazione API REST eliminata',
        deleteFailed: 'Impossibile eliminare la configurazione dell’API REST',
        keyLabel: 'X-API-KEY',
        keyHint:
          'Invia questo valore nell’header X-API-KEY in ogni richiesta dello storico prenotazioni.',
        showKey: 'Mostra chiave',
        hideKey: 'Nascondi chiave',
        copyKey: 'Copia chiave',
        copiedKey: 'X-API-KEY copiata',
        copyFailed: 'Impossibile copiare negli appunti',
        bookingHistory: 'Storico prenotazioni',
        sampleRequest: 'Richiesta di esempio',
        sampleResponse: 'Risposta di esempio',
        copySample: 'Copia esempio',
        copiedSample: 'Esempio copiato',
        filtersHint:
          'Ogni parametro è elencato a parte. L’endpoint cambia in base ai filtri inclusi.',
        copyFilters: 'Copia tutti i filtri',
        showFilters: 'Mostra filtri',
        hideFilters: 'Nascondi filtri',
        copiedFilters: 'Filtri copiati',
        endpointLabel: 'Endpoint',
        params: {
          page: 'page',
          size: 'size',
          status: 'status',
          bookingDate: 'bookingDate',
          bookingDateFrom: 'bookingDateFrom',
          bookingDateTo: 'bookingDateTo',
          search: 'search',
          timeRangeFilter: 'timeRangeFilter',
          assigned: 'assigned',
          walkInOnly: 'walkInOnly',
          sortBy: 'sortBy',
          sortDirection: 'sortDirection',
        },
        languages: {
          curl: 'cURL',
          javascript: 'JavaScript',
          python: 'Python',
          php: 'PHP',
        },
      },
      toast: {
        updated: 'Filiale aggiornata',
        updateFailed: 'Impossibile aggiornare la filiale',
        featureUpdated: 'Funzionalità aggiornata',
        featureUpdateFailed: 'Impossibile aggiornare la funzionalità',
        deliveryRequiresMenu:
          'Abilita il Menu Ristorante prima di attivare Delivery / Asporto.',
        smsUpdated: 'Numero SMS aggiornato',
        smsUpdateFailed: 'Impossibile aggiornare il numero di SMS',
        aiCreditsUpdated: 'Crediti AI aggiornati',
        aiCreditsUpdateFailed: 'Impossibile aggiornare i crediti AI',
        emailUpdated: 'Configurazione email salvata',
        emailRestored: 'Configurazione email ripristinata',
      },
      validation: {
        restaurantRequired: 'Il nome della filiale è obbligatorio',
        emailInvalid: 'Inserisci un indirizzo email valido',
        urlInvalid: 'Inserisci un link valido',
        facebookPixelNumeric: 'Il Pixel Facebook deve contenere solo numeri',
        capacityInvalid: 'Inserisci una capacità valida',
        capacityInteger: 'La capacità deve essere un numero intero',
        capacityMin: 'La capacità deve essere almeno 0',
      },
    },
    activities: {
      title: 'Filiali',
      count: '{{count}} filiali',
      create: 'Crea Filiale',
      loadError: 'Impossibile caricare le filiali. Riprova.',
      expiringCaption: 'In scadenza',
      expiringAria: '{{count}} filiali in scadenza a {{month}}',
    },
    tabs: {
      branch: 'Branch Pro20',
      users: 'Pro20 Users',
      reports: 'iPratico - Report',
      security: 'Sicurezza',
    },
    security: {
      title: 'Cambia password',
      subtitle: 'Aggiorna la password del tuo account backoffice.',
      password: 'Password',
      confirmPassword: 'Conferma password',
      submit: 'Aggiorna password',
      validation: {
        required: 'La password è obbligatoria',
        minLength: 'La password deve contenere almeno 6 caratteri',
        confirmRequired: 'Conferma la password',
        mismatch: 'Le password non coincidono',
      },
      toast: {
        success: 'Password aggiornata con successo',
        failed: 'Impossibile aggiornare la password. Riprova.',
      },
    },
    users: {
      title: 'Utenti',
      count: '{{count}} utenti',
      create: 'Crea utente',
      searchPlaceholder: 'Cerca utente..',
      loadError: 'Impossibile caricare gli utenti. Riprova.',
      table: {
        userCode: 'UserCode',
        name: 'Nome',
        email: 'Email',
        phone: 'Telefono',
        activity: 'Attività',
        visibleOnApp: "Visibile sull'app",
      },
      tooltips: {
        linkActivity: 'Collega attività all’utente',
        editUser: 'Modifica utente',
        goToBackoffice: 'Vai al backoffice dell’utente',
        deleteUser: 'Elimina utente',
      },
      fields: {
        name: 'Nome',
        email: 'Email',
        phone: 'Telefono',
        password: 'Password',
        confirmPassword: 'Conferma password',
      },
      createModal: {
        title: 'Crea utente',
        cancel: 'Annulla',
        create: 'Crea',
      },
      editModal: {
        title: 'Modifica utente',
        cancel: 'Annulla',
        update: 'Aggiorna',
      },
      linkActivityModal: {
        title: 'Seleziona attività',
        searchLabel: 'Cerca attività',
        searchPlaceholder: 'Cerca per nome o codice',
        availableTitle: 'Attività disponibili',
        cancel: 'Annulla',
        confirm: 'Conferma',
      },
      relatedActivitiesModal: {
        title: 'Attività collegate',
        name: 'Nome',
        code: 'Codice',
        expiration: 'Scadenza',
        unlink: 'Scollega attività',
        close: 'Chiudi',
      },
      unlinkModal: {
        title: 'Scollegare l’attività?',
        description:
          'Sei sicuro di voler scollegare "{{name}}" da questo utente? Questa azione non può essere annullata.',
      },
      deleteModal: {
        title: 'Eliminare l’utente?',
        description:
          'Sei sicuro di voler eliminare "{{name}}"? Questa azione non può essere annullata.',
      },
      validation: {
        nameRequired: 'Il nome è obbligatorio',
        emailRequired: 'L’email è obbligatoria',
        emailInvalid: 'Inserisci un’email valida',
        passwordMin: 'La password deve avere almeno 6 caratteri',
        passwordMismatch: 'Le password non coincidono',
      },
      toast: {
        created: 'Utente creato',
        createFailed: "Impossibile creare l'utente",
        updated: 'Utente aggiornato',
        updateFailed: "Impossibile aggiornare l'utente",
        deleted: 'Utente eliminato',
        deleteFailed: "Impossibile eliminare l'utente",
        loadFailed: "Impossibile caricare i dettagli dell'utente",
        activitiesLinked: 'Attività collegate all’utente',
        activitiesLinkFailed: 'Impossibile collegare le attività',
        activityUnlinked: "Attività scollegata dall'utente",
        activityUnlinkFailed: "Impossibile scollegare l'attività",
        noAssociatedActivity: 'Nessuna attività associata',
        visibilityUpdated: 'Visibilità utente aggiornata',
        visibilityUpdateFailed: 'Impossibile aggiornare la visibilità utente',
      },
    },
    reports: {
      badge: 'Monitor integrazione',
      title: 'Errori iPratico',
      subtitle:
        'Controlla richieste, risposte e anomalie registrate durante le operazioni verso iPratico.',
      errorsFound: 'errori trovati',
      actions: {
        update: 'Aggiorna',
        reset: 'Reset',
        filter: 'Filtra',
      },
      filters: {
        branchCode: 'Codice filiale',
        operation: 'Operazione',
        httpStatus: 'Stato HTTP',
        fromDay: 'Dal giorno',
        toDay: 'Al giorno',
        allStatuses: 'Tutti gli stati',
        status400: '400 — Richiesta non valida',
        status401: '401 — Non autorizzato',
        status403: '403 — Accesso negato',
        status404: '404 — Risorsa non trovata',
        status409: '409 — Conflitto',
        status429: '429 — Troppe richieste',
        status500: '500 — Errore iPratico',
        status502: '502 — Gateway',
        status503: '503 — Servizio non disponibile',
      },
      table: {
        date: 'Data',
        branch: 'Filiale',
        operation: 'Operazione',
        status: 'Stato',
        endpoint: 'Endpoint',
        mistake: 'Errore',
      },
      detail: {
        title: 'Dettaglio errore',
        branch: 'Filiale',
        branchName: 'Nome sede',
        branchCode: 'Codice sede',
        date: 'Data',
        method: 'Metodo',
        status: 'Stato',
        apiKey: 'X-API-KEY usata',
        endpoint: 'Endpoint',
        message: 'Messaggio',
        requestPayload: 'Payload richiesta',
        responsePayload: 'Risposta iPratico',
      },
      tooltips: {
        openDetail: 'Apri dettaglio',
        closeDetail: 'Chiudi dettaglio',
        copyPayload: 'Copia payload',
      },
      toast: {
        updated: 'Report aggiornati',
        copied: 'Copiato negli appunti',
        loadFailed: 'Impossibile caricare gli errori iPratico',
      },
      loadError: 'Impossibile caricare gli errori iPratico. Riprova.',
    },
    searchPlaceholder: 'Cerca per nome, codice, ragione sociale o P.IVA',
    filters: {
      features: {
        label: 'Funzionalità',
        google: 'Prenotazione Google',
        menu: 'Menu ristorante',
        ipraticoKey: 'Chiave iPratico',
        whatsappBusiness: 'WhatsApp Business',
        stripe: 'Stripe',
      },
    },
    table: {
      nameActivities: 'Nome Filiale',
      branchCode: 'BranchCode',
      extra: 'Extra',
      deadline: 'Scadenza',
      expiredOn: 'Scaduto il {{date}}',
      alertOnApp: 'Avviso su App',
      companyName: 'Ragione sociale',
      noCompanyName: 'Nessuna ragione sociale',
      delete: 'Elimina Filiale',
      settings: 'Aggiorna Filiale',
      chat: 'Monitora messaggi',
      send: 'Vai al backoffice',
      tooltips: {
        whatsappBusiness: 'WhatsApp Business abilitato',
        menu: 'Menu abilitato',
        google: 'Prenotazione Google abilitata',
        ipraticoKey: 'Chiave iPratico',
        sms: 'SMS rimanenti: {{count}}',
        aiCredits: 'Crediti AI: {{count}}',
        loadError: 'Impossibile caricare gli utenti di questa filiale',
      },
    },
    stripeConnect: {
      tooltip: {
        ready: 'Stripe Connect attivo — visualizza o rimuovi la configurazione',
        pending: 'Completa, riprendi o rimuovi l’onboarding Stripe',
        unlinked: 'Collega il branch a Stripe Connect',
      },
      link: {
        title: 'Link onboarding Stripe',
        body: 'Il link per {{branch}} è pronto.',
        warning:
          'È un link Stripe temporaneo e monouso: condividilo o utilizzalo appena possibile.',
        openLink: 'Apri link',
        copyLink: 'Copia link',
        linkCopied: 'Link copiato',
        copyFailed:
          'Copia non riuscita — seleziona e copia manualmente il link.',
      },
      connected: {
        title: 'Stripe Connect attivo',
        body: 'Il branch {{branch}} è già collegato e può ricevere pagamenti e accrediti.',
      },
      pending: {
        title: 'Verifiche Stripe in corso',
        body: 'Stripe richiede ancora alcune verifiche per {{branch}}.',
        resume: 'Riprendi onboarding',
      },
      error: {
        title: 'Configurazione Stripe non riuscita',
        fallback: 'Controlla la configurazione Stripe e riprova.',
      },
      remove: {
        action: 'Rimuovi configurazione Stripe',
        confirmTitle: 'Rimuovere la configurazione Stripe?',
        confirmBody:
          'Disconnettere Stripe per {{branch}}? Il branch non potrà più accettare pagamenti Stripe finché non verrà collegato di nuovo.',
        success: 'Configurazione Stripe rimossa',
        failed: 'Impossibile rimuovere la configurazione Stripe',
      },
      close: 'Chiudi',
    },
    accessUserModal: {
      title: 'Seleziona un utente con cui accedere al backoffice',
      name: 'Nome',
      email: 'Email',
      close: 'Chiudi',
      loadError: 'Impossibile caricare gli utenti di questa filiale',
    },
    signInAsUser: {
      success: 'Accesso effettuato come utente selezionato',
      failed: 'Impossibile accedere come questo utente',
      missingIdentity: 'Codice utente e email sono obbligatori',
    },
    expiringBranchesModal: {
      title: 'Filiali in scadenza a {{month}}',
      name: 'Nome',
      userCode: 'Usercode',
      email: 'Mail',
      expires: 'Scadenza',
      close: 'Chiudi',
      empty: 'Nessuna filiale scade in questo mese.',
      openDetails: 'Apri dettagli filiale',
    },
    deleteActivityModal: {
      title: "Confermi l'eliminazione della filiale?",
      description:
        'Scrivi qui il codice filiale ({{branchCode}}) per continuare con la rimozione',
      branchCodePlaceholder: 'CODICE FILIALE',
      eliminate: 'Elimina',
      cancel: 'Annulla',
      deleted: 'Filiale eliminata',
      deleteFailed: 'Impossibile eliminare la filiale',
    },
    access: {
      title: 'Accesso backoffice',
      description:
        'Inserisci il codice numerico per accedere al pannello operativo Pro20.',
      codePlaceholder: '',
      enter: 'Entra',
    },
    validation: {
      codeRequired: 'Il codice di accesso è obbligatorio',
      codeNumeric: 'Il codice di accesso deve contenere solo numeri',
      invalidCode: 'Codice di accesso non valido',
    },
    messageMonitor: {
      title: 'Message Monitor',
      subtitle: 'Controlla gli invii normali e programmati per i test API.',
      close: 'Chiudi',
      reset: 'Reimposta',
      update: 'Aggiorna',
      stats: {
        posted: 'Inviati in questa pagina',
        errors: 'Errori in questa pagina',
        inProgress: 'In corso in questa pagina',
      },
      filters: {
        origin: 'Origine',
        state: 'Stato',
        channel: 'Canale',
        reference: 'Riferimento',
        referencePlaceholder: 'Riferimento',
        originEveryone: 'Tutti',
        originNormal: 'Normale',
        originScheduled: 'Programmato',
        originBulk: 'Bulk',
        stateEveryone: 'Tutti',
        stateSent: 'Inviato',
        stateErrors: 'Errori',
        statePending: 'In attesa',
        stateInQueue: 'In coda',
        stateInProgress: 'In corso',
        channelEveryone: 'Tutti',
        channelWhatsapp: 'WhatsApp',
        channelThem: 'THEM',
        channelEmail: 'Email',
        channelSms: 'SMS',
      },
      eventsFound: '{{count}} eventi trovati',
      empty: 'Nessun messaggio trovato con i filtri selezionati.',
      loadError: 'Impossibile caricare gli eventi messaggio. Riprova.',
      actions: {
        resubmit: 'Reinvia messaggio',
      },
      toast: {
        resubmitted: 'Messaggio reinviato',
        resubmitFailed: 'Impossibile reinviare il messaggio',
      },
      table: {
        date: 'Data',
        type: 'Tipo',
        recipient: 'Destinatario',
        reference: 'Riferimento',
        message: 'Messaggio',
        channel: 'Canale',
        state: 'Stato',
        attempts: 'Tentativi',
        endpoint: 'Endpoint',
        payload: 'Payload',
        details: 'Dettagli',
      },
    },
  },
  homeLanding: {
    documentTitle: 'PRO20 — Software per prenotazioni ristorante e tavoli',
    login: 'Accedi',
    bookDemo: 'Prenota una demo',
    startFreeTrial: 'Richiedi una prova gratuita',
    learnMore: 'Scopri di più',
    nav: {
      home: 'Home',
      product: 'Prodotto',
      solutions: 'Soluzioni',
      industries: 'Settori',
      integrations: 'Integrazioni',
      pricing: 'Prezzi',
      resources: 'Risorse',
      why: 'Perché PRO20',
      apps: 'App',
      howItWorks: 'Come funziona',
      reviews: 'Recensioni clienti',
      faq: 'FAQ',
      about: 'Chi siamo',
    },
    hero: {
      partnerBadge: 'Partner Google Reservation C2C',
      metaPartnerBadge: 'Fornitore tecnologico ufficiale Meta',
      titleBefore: 'Piattaforma all-in-one per',
      titleHighlight: 'ristoranti',
      titleAfter: 'e prenotazione tavoli.',
      subtitle:
        'Gestisci prenotazioni, mappe sala, staff, marketing, pagamenti e delizia i tuoi ospiti — tutto in un’unica piattaforma.',
      checkGoogle: 'Google Reservations',
      checkWhatsapp: 'WhatsApp Official API',
      checkFloor: 'Mappa sala e tavoli',
      checkMarketing: 'Marketing e loyalty',
      checkPayments: 'Pagamenti e depositi',
      checkApps: 'App iOS e Android',
      trustedBy: 'Scelto da oltre 100 ristoranti in più di 10 città in Italia.',
      previewAlt: 'Anteprima dashboard prenotazioni PRO20',
      chatAria: 'Apri chat',
    },
    trust: {
      heading: 'Scelto da oltre 100 ristoranti e brand venue',
      subtitle:
        'Ristoranti indipendenti e gruppi multi-sede usano PRO20 per riempire i tavoli ogni giorno.',
    },
    stats: {
      title: 'Scelto dai team di ristorazione in tutto il mondo',
      subtitle: 'Scala reale tra ristoranti, café, beach club e bar.',
      restaurantsValue: '100+',
      restaurantsLabel: 'Ristoranti',
      countriesValue: '10+',
      countriesLabel: 'Città in Italia',
      bookingsValue: '2M+',
      bookingsLabel: 'Prenotazioni gestite',
      uptimeValue: '99.9%',
      uptimeLabel: 'Uptime',
    },
    features: {
      title: 'Tutto il necessario per gestire le prenotazioni',
      subtitle:
        'Da prenotazioni e mappe sala a marketing, loyalty e integrazioni — una sola piattaforma.',
      reservationsTitle: 'Prenotazioni smart',
      reservationsDesc:
        'Accetta, gestisci e ottimizza le prenotazioni su ogni canale in tempo reale.',
      floorTitle: 'Mappa sala e tavoli',
      floorDesc:
        'Mappatura visuale con stato tavoli live per un servizio più fluido.',
      bookingLinkTitle: 'Link pubblico di prenotazione',
      bookingLinkDesc:
        'Crea un form di prenotazione e condividi l’URL pubblico su sito, QR, WhatsApp o social così gli ospiti prenotano online.',
      marketingTitle: 'Marketing e campagne',
      marketingDesc:
        'Raggiungi gli ospiti con offerte mirate, promemoria e campagne.',
      loyaltyTitle: 'Loyalty e promo',
      loyaltyDesc:
        'Premia i clienti abituali e aumenta i ritorni con promozioni flessibili.',
      reportsTitle: 'Report e insight',
      reportsDesc:
        'Monitora occupazione, ricavi e tendenze ospiti con dashboard chiare.',
    },
    why: {
      title: 'Perché PRO20?',
      subtitle:
        'Pensato per ristoranti e bar — non un widget di prenotazione generico.',
      allInOneTitle: 'Piattaforma all-in-one',
      allInOneDesc:
        'Prenotazioni, mappa sala, marketing, loyalty e pagamenti in un unico posto.',
      channelsTitle: 'Nativo Google e WhatsApp',
      channelsDesc: 'Incontra gli ospiti dove già prenotano e messaggiano.',
      hospitalityTitle: 'Fatto per le operazioni di sala',
      hospitalityDesc:
        'Mappatura sala, workflow staff e strumenti live pensati per il servizio.',
      appsTitle: 'App per ogni dispositivo',
      appsDesc:
        'App native per telefono e iPad su Android e iOS, sincronizzate con il backoffice web.',
      fidelityTitle: 'Fidelity e loyalty',
      fidelityDesc:
        'Premia i clienti abituali con punti, card e promo — aumenta i ritorni con un unico programma.',
      supportTitle: 'Supporto 24×7',
      supportDesc:
        'Assistenza continua quando il tuo team ne ha bisogno — giorno e notte, tutti i giorni.',
    },
    industries: {
      title: 'Pensato per ogni tipo di locale',
      subtitle:
        'Dal fine dining ai beach club — PRO20 si adatta a come il tuo locale serve gli ospiti.',
      restaurants: 'Ristoranti',
      cafes: 'Café',
      beachClubs: 'Beach Club',
      bars: 'Bar e pub',
      rooftops: 'Rooftop',
      resorts: 'Resort',
    },
    integrations: {
      title: 'Integrazioni potenti',
      subtitle:
        'Collega gli strumenti che usi già — prenotazioni, messaging, POS e pagamenti.',
      google: 'Google Reservations',
      whatsapp: 'WhatsApp Official API',
      ipratico: 'iPratico POS',
      centralino: 'Centralino VOIP',
      stripe: 'Pagamenti Stripe',
      meta: 'Partner tecnologico Meta',
    },
    engage: {
      title: 'Comunica. Coinvolgi. Delizia.',
      subtitle:
        'Contatta i tuoi ospiti all’istante con WhatsApp Official API e messaggi automatici.',
      confirmations: 'Conferme prenotazione',
      reminders: 'Promemoria e alert',
      offers: 'Offerte e promozioni',
      feedback: 'Feedback e recensioni',
      chatGuest: 'Ciao! Il tuo tavolo per 2 è confermato alle 20:00.',
      chatModify: 'Modifica prenotazione',
      chatCancel: 'Annulla prenotazione',
      chatDirections: 'Indicazioni stradali',
    },
    apps: {
      title: 'Gestisci il locale da telefono o iPad',
      subtitle:
        'App native PRO20 per telefoni e iPad — su Android e iOS, così il team resta sincronizzato in sala e in mobilità.',
      phoneTablet: 'Telefoni e iPad',
      androidIos: 'Android e iOS',
      floorPlan: 'Mappa sala completa e controllo tavoli su iPad',
      onTheGo: 'Gestisci le prenotazioni dal telefono ovunque',
      sync: 'Stessi dati live del backoffice web',
      appStore: 'Scarica su App Store',
      googlePlay: 'Disponibile su Google Play',
    },
    about: {
      title: 'Chi siamo',
      subtitle:
        'Aiutiamo i brand della ristorazione a trasformare ogni tavolo in un’esperienza migliore.',
      body: 'PRO20 è la piattaforma all-in-one per la prenotazione tavoli, scelta da locali in tutto il mondo. Dai ristoranti indipendenti ai gruppi multi-sede, uniamo prenotazioni, mappe sala, messaging ospiti e marketing così gli operatori possano concentrarsi sull’ospitalità — non su strumenti sparsi.',
      missionTitle: 'La nostra missione',
      missionBody:
        'Rendere semplici, connesse e piacevoli le operazioni di prenotazione di livello mondiale per ogni locale.',
      pillarGuests: 'Ospiti al centro',
      pillarOperators: 'Operatori potenziati',
      pillarConnected: 'Sempre connessi',
      imageAlt: 'Team di ristorante al lavoro',
    },
    howItWorks: {
      title: 'Inizia in 6 semplici passi',
      subtitle:
        'Tutto ciò che ti serve per gestire le operazioni del ristorante, dal setup agli ospiti soddisfatti.',
      steps: {
        s1: {
          title: 'Crea il tuo ristorante',
          desc: 'Configura il profilo con cucina, sede e branding in pochi minuti.',
        },
        s2: {
          title: 'Progetta sale e tavoli',
          desc: 'Crea la mappa sala con il planner drag-and-drop e organizza i tavoli come preferisci.',
        },
        s3: {
          title: 'Configura le regole di prenotazione',
          desc: 'Definisci fasce orarie, coperti, depositi e policy adatte al tuo servizio.',
        },
        s4: {
          title: 'Condividi e ricevi prenotazioni',
          desc: 'Pubblica il link di prenotazione su sito, WhatsApp, social o QR.',
        },
        s5: {
          title: 'Gestisci e cresci',
          desc: 'Gestisci sala, staff e marketing da un unico posto mentre arrivano le prenotazioni.',
        },
        s6: {
          title: 'Analizza e ottimizza',
          desc: 'Usa report chiari su occupazione, ricavi e trend ospiti per migliorare ogni servizio.',
        },
      },
      mocks: {
        createTitle: 'Crea il tuo ristorante',
        name: 'Nome ristorante',
        cuisine: 'Tipo di cucina',
        location: 'Sede',
        continue: 'Continua',
        floorTitle: 'Planner sala',
        rulesTitle: 'Impostazioni prenotazione',
        timeSlots: 'Fasce orarie',
        bookingRules: 'Regole di prenotazione',
        advancePayment: 'Pagamento anticipato',
        shareTitle: 'Condividi link prenotazione',
        manageTitle: 'Panoramica di oggi',
        reservations: 'Prenotazioni',
        occupied: 'Tavoli occupati',
        walkIns: 'Walk-in',
        revenue: 'Ricavi',
        reportsTitle: 'Panoramica report',
        totalReservations: 'Prenotazioni totali',
        occupancy: 'Tasso di occupazione',
      },
      ctaText:
        'Pronto a iniziare? Unisciti a migliaia di ristoranti che crescono già con PRO20.',
      bookDemo: 'Prenota una demo',
      contactSales: 'Contatta le vendite',
    },
    faq: {
      title: 'Domande frequenti',
      subtitle:
        'Risposte rapide sul software di prenotazione PRO20 per locali multi-sede.',
      viewAll: 'Vedi tutte le FAQ',
      q1: 'Posso condividere un link pubblico di prenotazione?',
      a1: 'Sì. Crea un form di prenotazione e condividi l’URL pubblico su sito, QR, WhatsApp o social così gli ospiti prenotano online.',
      q2: 'Supporta più ristoranti?',
      a2: 'Sì. I gruppi multi-sede possono gestire i locali in modo centralizzato con il Backoffice PRO20.',
      q3: 'Posso collegare WhatsApp?',
      a3: 'Sì. PRO20 supporta WhatsApp Business e l’API ufficiale Meta per conferme, promemoria e campagne.',
      q4: 'Quali integrazioni sono disponibili?',
      a4: 'Google Reservations, WhatsApp Official API, sistemi POS, Stripe, Razorpay e molte altre.',
      q5: 'C’è un’app mobile?',
      a5: 'Sì. App native per telefoni e iPad su Android e iOS, sincronizzate con la piattaforma web.',
      q6: 'Come inizio?',
      a6: 'Prenota una demo o avvia la prova gratuita — ti aiutiamo a configurare sale, tavoli e il link pubblico di prenotazione.',
    },
    reviews: {
      title: 'Non fidarti solo della nostra parola',
      subtitle:
        'Ristoranti in oltre 10 città in Italia usano già PRO20 per gestire prenotazioni, riempire i tavoli e deliziare gli ospiti. Pronto a scoprire di cosa si parla?',
      reassurance:
        '(Davvero. Nessuna clausola nascosta. Nessuna pressione commerciale. Solo software solido e un team che conosce i ristoranti.)',
      trustLabel: 'Da centinaia di recensioni',
      r1: {
        quote:
          'PRO20 ha sostituito tre strumenti che usavamo prima. Prenotazioni e WhatsApp finalmente in un unico posto.',
        author: 'Marco Rossi • Owner, Osteria Bella',
      },
      r2: {
        quote:
          'Il setup è stato rapido e il planner tavoli è esattamente ciò di cui lo staff aveva bisogno nelle serate di punta.',
        author: 'Sophie Laurent • Manager, Café Lumière',
      },
      r3: {
        quote:
          'Capienza stagionale e messaging ospiti erano un caos. Ora è prevedibile e professionale.',
        author: 'Elena Bianchi • Director, Azure Beach Club',
      },
      r4: {
        quote:
          'Il link pubblico ha riempito slot serali che lasciavamo vuoti. Gli ospiti prenotano da soli — noi confermiamo.',
        author: 'Alex Kim • Owner, Harbor Bistro',
      },
    },
    nextLevel: {
      title: 'Pronto a portare il tuo ristorante al livello successivo?',
      body: 'PRO20 semplifica le operazioni quotidiane, aumenta le prenotazioni e riduce lo stress di gestire un locale affollato.',
      closing: 'Inizia oggi con PRO20 e scopri la differenza.',
    },
    cta: {
      heading: 'Pronto a trasformare il tuo ristorante?',
      subtitle: 'Unisciti a migliaia di ristoranti che crescono con PRO20.',
    },
    footer: {
      blurb:
        'Il software all-in-one per prenotazioni ristorante, engagement ospiti e operazioni di locale.',
      product: 'Prodotto',
      productReservations: 'Prenotazioni',
      productFloor: 'Mappa sala',
      productMarketing: 'Marketing',
      productLoyalty: 'Loyalty',
      productReports: 'Report',
      productIntegrations: 'Integrazioni',
      solutions: 'Soluzioni',
      solutionsRestaurants: 'Per ristoranti',
      solutionsCafes: 'Per café',
      solutionsBeach: 'Per beach club',
      solutionsBars: 'Per bar',
      solutionsChains: 'Per catene',
      resources: 'Risorse',
      resourcesBlog: 'Blog',
      resourcesGuides: 'Guide',
      resourcesHelp: 'Centro assistenza',
      resourcesCases: 'Casi studio',
      resourcesApi: 'Documentazione API',
      resourcesApps: 'App mobile',
      company: 'Azienda',
      companyAbout: 'Chi siamo',
      companyCareers: 'Carriere',
      companyContact: 'Contattaci',
      companyPartners: 'Partner',
      legal: 'Legale',
      legalPrivacy: 'Privacy Policy',
      legalTerms: 'Termini di servizio',
      legalCookies: 'Cookie Policy',
      copyright: '© {{year}} PRO20. Tutti i diritti riservati.',
      socialFacebook: 'Facebook',
      socialInstagram: 'Instagram',
      socialLinkedin: 'LinkedIn',
      socialYoutube: 'YouTube',
    },
  },
  faqPage: {
    documentTitle: 'FAQ — Software di prenotazione ristoranti PRO20',
    title: 'Domande frequenti',
    subtitle:
      'Risposte rapide alle domande più comuni sul software di prenotazione PRO20.',
    searchPlaceholder: 'Cerca la tua domanda...',
    emptyResults:
      'Nessuna domanda corrisponde alla ricerca. Prova un’altra parola chiave o categoria.',
    categories: {
      all: 'Tutte le domande',
      reservations: 'Prenotazioni',
      whatsapp: 'WhatsApp',
      payments: 'Pagamenti',
      reports: 'Report',
      staff: 'Staff e utenti',
      integrations: 'Integrazioni',
      general: 'Generale',
    },
    promo: {
      badge: 'Piattaforma all-in-one',
      titleBefore: 'Tutto ciò che ti serve per',
      titleAccent: 'gestire e far crescere',
      titleAfter: 'il tuo ristorante',
      body: 'Dalle prenotazioni al marketing, dall’automazione WhatsApp ai report — PRO20 ti aiuta a gestire il business in modo più intelligente.',
      restaurantsValue: '10.000+',
      restaurantsLabel: 'Ristoranti',
      bookingsValue: '2M+',
      bookingsLabel: 'Prenotazioni',
      countriesValue: '10+',
      countriesLabel: 'Città in Italia',
      uptimeValue: '99,9%',
      uptimeLabel: 'Uptime',
    },
    items: {
      q1: 'Posso condividere link di prenotazione?',
      a1: 'Sì. Crea un form di prenotazione e condividi l’URL pubblico su sito, QR, WhatsApp o social così gli ospiti prenotano online.',
      q1Tag1: 'URL pubblico',
      q1Tag2: 'Codice QR',
      q1Tag3: 'WhatsApp',
      q1Tag4: 'Social media',
      q1Tag5: 'Sito web',
      q2: 'PRO20 supporta più ristoranti o sedi?',
      a2: 'Sì. I gruppi multi-sede gestiscono i locali in modo centralizzato con il Backoffice PRO20, controlli e report condivisi.',
      q3: 'Posso collegare WhatsApp Official e inviare messaggi?',
      a3: 'Sì. PRO20 supporta WhatsApp Business e l’API ufficiale Meta per conferme, promemoria, campagne e messaging.',
      q4: 'Quali gateway di pagamento sono supportati?',
      a4: 'PRO20 si integra con Stripe e altri provider per depositi e riduzione dei no-show.',
      q5: 'C’è un’app mobile?',
      a5: 'Sì. App native per telefoni e iPad su Android e iOS, sincronizzate con la piattaforma web.',
      q6: 'Quali lingue sono supportate?',
      a6: 'PRO20 supporta più lingue per operatori e ospiti, tra cui inglese, italiano, spagnolo, francese, olandese e cinese.',
      q7: 'Posso gestire lo staff e i ruoli?',
      a7: 'Sì. Assegna ruoli e permessi così il team vede solo ciò che serve — dalla sala ai manager multi-sede.',
      q8: 'Posso gestire campagne marketing e codici promo?',
      a8: 'Sì. Gestisci campagne, offerte e promo da PRO20 per riempire i momenti più tranquilli e premiare i clienti abituali.',
      q9: 'Che report posso ottenere?',
      a9: 'Monitora occupazione, prenotazioni, ricavi e attività ospiti con dashboard pensate per la ristorazione.',
      q10: 'Quanto sono sicuri i miei dati?',
      a10: 'Usiamo pratiche di sicurezza standard di settore per proteggere i dati di ristorante e ospiti, con uptime affidabile.',
    },
    cta: {
      eyebrow: 'Hai ancora domande?',
      title: 'Siamo qui per aiutarti!',
      body: 'Parla con i nostri esperti della ristorazione e scopri come PRO20 può trasformare il tuo business.',
      bookDemo: 'Prenota una demo',
      contactSales: 'Contatta le vendite',
      startTrial: 'Oppure richiedi una prova gratuita',
    },
  },
  legalPages: {
    privacy: {
      documentTitle: 'Privacy Policy — PRO20',
      loading: 'Caricamento della privacy policy…',
      loadError:
        'Impossibile caricare i dati del titolare. L’informativa è mostrata con informazioni limitate.',
    },
    privacyPolicy: {
      documentTitle: 'Informativa privacy | Proventi',
      title: 'Informativa sul trattamento dei dati personali',
      subtitle:
        'Ai sensi degli artt. 13 e 14 del Regolamento (UE) 2016/679 – GDPR',
      contents: 'In questa pagina',
      body: privacyPolicyBodyIt,
    },
    terms: {
      documentTitle: 'Termini di servizio — PRO20',
      title: 'Termini di servizio',
      subtitle: 'Questi termini verranno aggiornati a breve.',
      body: 'I Termini di servizio di PRO20 sono in preparazione. Riprova più tardi oppure contattaci se ti serve una copia nel frattempo.',
    },
  },
};
